import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import { showSuccess, showError } from "toast/toast";
// import "react-toastify/dist/ReactToastify.css";
import { useNavigate, Link, useLocation } from "react-router-dom";
import Select, { components } from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import { License, Office, Agent, Addresses, Product, Address, Addresstax } from "types";
import { em } from "@fullcalendar/core/internal-common";
import { priority } from 'validationSchemas/common'
import { StatusBadge } from "components/Productlocationstatus";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import AddressFormModal from "components/AddAddressFormModal";
import InstallationStatusTracker, { InstallStatus, RemovalStatus } from "components/InstallationStatusTracker";
import SectionHeader from "components/SectionPillPropsIcons";
import PostTypeSignConfiguration from "pages/signage/PostTypeSignConfiguration";
import ProductSelectionModal, {
  type LinkedProduct,
  type InstallationOrderLineCustomizations,
} from "components/ProductSelectionModal";
import type { StudioBatchLineItem, StudioCustomizationFlowProps } from "components/ordercheckoutpop";
import { getCustomizationBatch } from "api/realesyStudioApi";
import {
  isMongoObjectId,
  normalizeCustomizationBatchContexts,
  findBuyerSearchMatchForStudioRestore,
  effectiveLicenseTypeForItem,
} from "utils/signageStudioBatchRestore";
import {
  getInstallationOrderFromBatch,
  parseIsoToDateAndTime,
  splitFullName,
  contactFromInstallationOrderSnapshot,
  studioRestoreInstallationAddressBindings,
  normalizeInstallationPriority,
  installationAddressDisplayFromSnapshot,
  buildAddressesFromInstallationSnapshot,
} from "utils/signageInstallationStudioRestore";
import {
  getCustomizationsBlockFromLineItem,
  getVisibleCustomizationFieldsForDisplay,
  resolveSelectedLineItemForDisplay,
} from "utils/installationProductCustomizationDisplay";
import InstructionsCreateCard from "components/Notes";
import ProductSelection from "assets/images/brands/ProductSelection.png"
import Doller from "assets/images/brands/doller.png"
import OrderNotes from "assets/images/brands/OrderNotes.png"
import AddressAutocomplete from "components/AddressAutocomplete";
import { GOOGLE_MAPS_SCRIPT_LIBRARIES } from "constants/googleMapsScript";
import SpecialFeeui from "components/Tax/InstallationandremovalSpecialfeeui";
import PONumber from "components/PONumber";
import ProjectGetCard from "components/projectgetcard";
import CompactApprovedProofCard from "components/CompactApprovedProofCard";
import { formatLineDiscountAmount } from "components/DiscountLabelBadge";
import { OrderDiscountCodeField } from "components/orderResolve";
import {
  getInstallationLineDiscountDisplay,
  getInstallationLineUnitCostDisplay,
  computeInstallationUnitsCost,
  computeInstallationGrandTotal,
  getOriginalCatalogUnitPrice,
  getResolveRequestUnitPrice,
  orderLineKey,
} from "utils/orderResolveLinePricing";
import { sendInstallationOrderTriggerEmail } from "utils/salesOrderTriggerEmailEngine";
import { handleApiError } from "utils/errorHandler";
import { safeConsole, createSafeNavigate } from "utils/envUtils";
import { buildSpecialFeeCostBreakdownDisplay } from "utils/specialFeeCostBreakdownDisplay";

import {
  buildInstallationResolvePayload,
  buildInstallationAddResolveLineItems,
  postInstallationOrderResolve,
  applyInstallationResolveResponse,
  overlayInstallationSubmitFromResolved,
  mergeResolvedTaxIntoLineItems,
  getInstallationResolveRootDiscountCode,
  getOrderDiscountFieldDisplay,
  getResolvedDiscountDisplay,
  inferInstallationDiscountScope,
  inferInstallationDiscountScopeFromResolved,
  splitInstallationDiscountForPayload,
  mapInstallationSpecialFeeForResolve,
  mapInstallationSpecialFeeForSubmit,
  mapSavedInstallationSpecialFeeToUi,
  normalizeInstallationSelectedFee,
  type ResolvedInstallationOrder,
  type ResolvedInstallationLineItem,
  type InstallationResolveAddress,
} from "orderResolve";
import { GENERAL_SETTINGS_KEYS } from "pages/signage/SignageGeneralSettings";
import SearchBrokerageAgentTeam, {
  type SearchBrokerageAgentTeamHandle,
} from "components/SearchBrokerageAgentTeam";
import {
  fetchProjectById,
  fetchProjectApprovedMessage,
  fetchPublicProjectMessagesByProjectId,
  linkProjectQuote,
  resolveApprovedProofVersionSummary,
} from "components/proejctsandquoteapicalls";
import { isSignagePortalRoute } from "utils/isSignagePortalRoute";
import {
  findSalesOrderAddressById,
  resolveSalesOrderAddressId,
  sanitizeSalesOrderAddressForPayload,
} from "components/SignageCreateOrder/buildSalesOrderPayloadForApi";
import { getAuthCookie, getCookie, removeCookie, setCookie } from "../../utils/authCookies";
import { downloadWorkDriveFile } from "api/realesyStudioApi";

/** Placeholder contact phone when none is on file (matches SearchBrokerageAgentTeam). */
const DEFAULT_SIGNAGE_CONTACT_PHONE = "0000000000";

function withDefaultSignageContactPhone(v: unknown): string {
  const s = String(v ?? "").trim();
  if (!s || s === DEFAULT_SIGNAGE_CONTACT_PHONE) return DEFAULT_SIGNAGE_CONTACT_PHONE;
  return s;
}

const INSTALLATION_DRAFT_ID_COOKIE = "re_installation_unique_id";

function getInstallationDraftId(): string {
  const fromCookie = getCookie(INSTALLATION_DRAFT_ID_COOKIE);
  if (fromCookie) return fromCookie;
  if (typeof localStorage !== "undefined") {
    const legacy = localStorage.getItem(INSTALLATION_DRAFT_ID_COOKIE);
    if (legacy) {
      setCookie(INSTALLATION_DRAFT_ID_COOKIE, legacy);
      localStorage.removeItem(INSTALLATION_DRAFT_ID_COOKIE);
      return legacy;
    }
  }
  return "";
}

function setInstallationDraftId(id: string): void {
  setCookie(INSTALLATION_DRAFT_ID_COOKIE, id);
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(INSTALLATION_DRAFT_ID_COOKIE);
  }
}

function clearInstallationDraftId(): void {
  removeCookie(INSTALLATION_DRAFT_ID_COOKIE);
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(INSTALLATION_DRAFT_ID_COOKIE);
  }
}

/** Prefer API `phone1`, then `phone`, on user-like objects. */
function primaryPhoneFromUserLike(u: Record<string, unknown> | null | undefined): string {
  if (!u) return "";
  const p1 = String(u.phone1 ?? "").trim();
  if (p1) return p1;
  return String(u.phone ?? "").trim();
}

type ProductObj = {
  product_id: string;
  agent_id: string;
  office_id: string;
  inventory_type: string;
  product: Product;
  linked_installer_details: any
  created_by: {
    id: string;
    name: string;
  };
};

type Selected = {
  value: string;
  label: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  license_type?: string;
  brokerageFirmId?: string | null;
  brokerageFirmName?: string | null;
}

// Select2 types for brokerage firms
type BrokerageOption = {
  value: string;
  label: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

// Select2 types for team members
type TeamMemberOption = {
  value: string;
  label: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  offices: any[]; // Array of office objects from API
  /** From API or defaulted — used for post type customer_side datatable */
  license_type?: string;
};

// Select2 types for signage team members (installers)
type SignageTeamMemberOption = {
  value: string;
  label: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
};

// Select2 types for agents
type OfficeAgentOption = {
  value: string;
  label: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  license_type?: string;
};

export type FlattenedProduct = {
  rowKey?: string;
  product_id: string;
  name: string;
  sku: string;
  inventory_type: "agent" | "office" | string;
  product_location_status?: string;
  user_id?: string;
  product_current_status?: string;
  current_status?: string;
  availableqty: number;
  quantity: number;
  updateStatus?: string;
  productionStatus?: string;
  image_url?: string;
  /** True when the product was added as an accessory (quantity editable) */
  is_accessory?: boolean;
  location?: Array<{
    quantity: number;
    warehouse_id: string;
    warehouse_name?: string;
    zone_id?: string;
    zone_name?: string;
    rack_id?: string;
    rack_name?: string;
    bin_id?: string;
    bin_name?: string;
  }>;
  /** New/catalog product: from Browse Catalog; no inventory API call; used for line_items */
  is_new_catalog?: boolean;
  variant_id?: string;
  unit_price?: number;
  /** Product is taxable for manual tax when TaxCloud not integrated */
  is_taxable?: boolean;
  /** Slot key when product was added from a post-type slot; used to clear slot on delete */
  slotKey?: string;
  /** Backend slot id when product was added from a post-type slot; used for line_items + view mode */
  slot_id?: string;
  /** Realesy Studio design metadata when product was added with an active batch line */
  design_id?: string;
  design_name?: string;
  /** Work-drive preview file id */
  preview_url?: string;
  /** Catalog variant customization answers for installation API `line_items[].customizations`. */
  customizations?: InstallationOrderLineCustomizations;
};

const apiKey: string = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const mapContainerStyle = {
  width: "100%",
  height: "350px",
  borderRadius: "12px",
  overflow: "hidden",
  // boxShadow: "0 0 10px rgba(0,0,0,0.1)", // Soft shadow
  border: "1px solid #dee2e6",          // Light border
};

const center = {
  lat: 40.712776, // Default latitude (e.g., New York City)
  lng: -74.005974, // Default longitude
};

// Custom dropdown indicator component
const DropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <i className="ri-arrow-down-s-line fs-5" style={{ color: '#6c757d' }}></i>
    </components.DropdownIndicator>
  );
};

// Custom styles to ensure dropdown indicator is always visible
const customSelectStyles = {
  dropdownIndicator: (provided: any, state: any) => ({
    ...provided,
    display: 'flex !important',
    opacity: 1,
    visibility: 'visible',
  }),
  indicatorSeparator: (provided: any, state: any) => ({
    ...provided,
    display: 'none',
  }),
};

// Fetch a single new unique ID (no cache) - for custom product/variant IDs
async function fetchNewUniqueId(token: string): Promise<string> {
  const url = `${process.env.REACT_APP_API_URL}/utils/new-unique-id`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(errorData.message || `Failed to fetch unique ID: ${res.statusText}`);
  }
  const data = await res.json().catch(() => ({}));
  const id = data?.id || data?.data?.id || data?.data?.unique_id || data?.unique_id || null;
  if (!id) throw new Error("Unique ID not found in API response");
  return String(id);
}

const ORDER_TYPE_BADGE_MAP: Record<string, { label: string; cls: string }> = {
  BROKERAGE_FIRM: { label: "Brokerage", cls: "search-bat-badge search-bat-badge-b" },
  OFFICE: { label: "Office", cls: "search-bat-badge search-bat-badge-o" },
  BROKERAGE_AGENT: { label: "Brokerage Agent", cls: "search-bat-badge search-bat-badge-ba" },
  AGENT: { label: "Agent", cls: "search-bat-badge search-bat-badge-a" },
  BROKERAGE_TEAM_MEMBER: { label: "Team Member", cls: "search-bat-badge search-bat-badge-m" },
  TEAM_MEMBER: { label: "Team Member", cls: "search-bat-badge search-bat-badge-m" },
};

const INSTALLATION_PRIORITY_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: "High" },
  { value: 2, label: "Medium" },
  { value: 3, label: "Low" },
];

const SignageAddInstallation = () => {
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const userName = getAuthCookie("userName");
  const licenseType = getAuthCookie("licenseType");
  const location = useLocation();
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';
  const projectIdFromQuery = useMemo(
    () => new URLSearchParams(location.search).get("project_id") || "",
    [location.search]
  );
  const [linkedProjectId, setLinkedProjectId] = useState<string | null>(() => projectIdFromQuery || null);
  const [linkedProjectNumber, setLinkedProjectNumber] = useState<string | null>(null);
  const [approvedMessageData, setApprovedMessageData] = useState<any>(null);
  const [hasApprovedMessage, setHasApprovedMessage] = useState(false);
  const [parentSignageId, setParentSignageId] = useState<string | null>(null);
  const userClearedProjectRef = React.useRef(false);

  useEffect(() => {
    if (projectIdFromQuery && !userClearedProjectRef.current) {
      setLinkedProjectId(projectIdFromQuery);
    }
  }, [projectIdFromQuery]);

  useEffect(() => {
    let cancelled = false;
    const pid = String(linkedProjectId || "").trim();
    if (!token || !pid) {
      setHasApprovedMessage(false);
      setApprovedMessageData(null);
      return;
    }
    (async () => {
      try {
        const approved = await fetchProjectApprovedMessage(token, pid);
        if (cancelled) return;
        const hasMessage = Boolean(approved?.has_approved_message);
        setHasApprovedMessage(hasMessage);
        setApprovedMessageData(hasMessage ? approved?.data ?? null : null);
      } catch {
        if (cancelled) return;
        setHasApprovedMessage(false);
        setApprovedMessageData(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [linkedProjectId, token]);
  const buildInstallationOrderPayloadRef = useRef<((values: any) => Promise<Record<string, unknown> | null>) | null>(null);
  const lastResolvedInstallationRef = useRef<ResolvedInstallationOrder | null>(null);

  // Batch restore: when Studio redirects back with ?batch_id=xxx&context_id=yyy,
  // fetch the batch and patch studio customization data onto matching product rows.
  const studioReturnBatchId = useMemo(
    () => new URLSearchParams(location.search).get("batch_id") || "",
    [location.search]
  );
  const studioReturnContextId = useMemo(
    () => new URLSearchParams(location.search).get("context_id") || "",
    [location.search]
  );
  /** Populated from GET customization batch `items[]` — ProductSelectionModal uses for preview thumbs + Edit design. */
  const [studioBatchLineItems, setStudioBatchLineItems] = useState<StudioBatchLineItem[]>([]);
  useEffect(() => {
    if (!studioReturnBatchId?.trim()) {
      setStudioBatchLineItems([]);
    }
  }, [studioReturnBatchId]);
  const restoredStudioReturnKeyRef = React.useRef("");
  /** While true, skip installation dynamic-cost fetch so snapshot totals / address are not overwritten mid-restore. */
  const studioRestoreLockRef = React.useRef(false);
  /** Same imperative restore API as SignageCreateOrder — pick → office → team → address chain. */
  const searchBatRestoreRef = React.useRef<SearchBrokerageAgentTeamHandle | null>(null);
  /** Poll mirror for address list after SearchBAT restore (same pattern as Create Order). */
  const addressObjectsMirrorRef = React.useRef<any[]>([]);
  const [brokerageFirms, setBrokerageFirms] = useState<License[]>([]);
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [selectedTeamMember, setSelectedTeamMember] = useState<any | null>(null);
  const [selectedTeamMemberOption, setSelectedTeamMemberOption] = useState<TeamMemberOption | null>(null);
  /** Holds top-level team member selection from SearchBAT before React state/formik flushes. */
  const teamMemberFromContinueRef = useRef<{ value: string; label: string } | null>(null);
  /** When true, buyer restore from project_id must not clear project billing address. */
  const projectRestoreGuardRef = useRef(false);
  const projectBillingAddressRef = useRef<any | null>(null);
  const applyProjectBillingAddressRef = useRef<(() => void) | null>(null);
  const upsertAddressInList = (list: any[], addr: any): any[] => {
    const id = resolveSalesOrderAddressId(addr) || String(addr?._id || "").trim();
    if (!id) return Array.isArray(list) ? list : [];
    const current = Array.isArray(list) ? [...list] : [];
    const idx = current.findIndex(
      (a: any) =>
        resolveSalesOrderAddressId(a) === id ||
        String(a?._id || "") === id ||
        String(a?.address_details?._id || "") === id
    );
    if (idx >= 0) return Array.isArray(list) ? list : current;
    current.push(addr);
    return current;
  };
  const mergeProjectBillingAddress = (list: any[]): any[] => {
    const billing = projectBillingAddressRef.current;
    if (!billing) return Array.isArray(list) ? list : [];
    return upsertAddressInList(Array.isArray(list) ? list : [], billing);
  };
  const [projectContactPrefill, setProjectContactPrefill] = useState<{
    label: string;
    type: string;
    contact: { fName: string; lName: string; email: string; phone: string };
  } | null>(null);

  const [parentSignageName, setParentSignageName] = useState<string | null>(null);
  const [installer, setInstaller] = useState<any[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [officeAgents, setOfficeAgents] = useState<Agent[]>([]);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [quantity, setQuantity] = useState(1);
  const [addressObjects, setAddressObjects] = useState<any[]>([]);
  React.useEffect(() => {
    addressObjectsMirrorRef.current = addressObjects;
  }, [addressObjects]);
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [showViewInventoryModal, setShowViewInventoryModal] = useState(false);

  const [productObjs, setProductObjs] = useState<ProductObj[]>([]);
  const [selectedProductOption, setSelectedProductOption] = useState<any | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);

  const customizationWorkDriveFileIds = useMemo(() => {
    const HEX = /^[a-f0-9]{24}$/i;
    const ids: string[] = [];
    for (const p of selectedProducts || []) {
      const block = getCustomizationsBlockFromLineItem(p);
      const fields = Array.isArray((block as { fields?: unknown[] })?.fields)
        ? ((block as { fields: unknown[] }).fields as Array<{ field_type?: string; value?: unknown }>)
        : [];
      for (const f of fields) {
        const t = String(f?.field_type || "").toLowerCase();
        const v = String(f?.value ?? "").trim();
        if ((t === "file" || t === "image") && v && HEX.test(v)) ids.push(v);
      }
    }
    return Array.from(new Set(ids));
  }, [selectedProducts]);

  const customizationImageUrlMapData = useMemo(
    () =>
      customizationWorkDriveFileIds.length === 0
        ? []
        : [{ product: { variants: [{ images: customizationWorkDriveFileIds.map((url) => ({ url })) }] } }],
    [customizationWorkDriveFileIds]
  );
  const customizationImageUrlMap = useVariantImageUrlMap(customizationImageUrlMapData as any, token);
  const [selectedPostTypeId, setSelectedPostTypeId] = useState<string | null>(null);
  const [installationPost, setInstallationPost] = useState<import("pages/signage/PostTypeSignConfiguration").InstallationPostPayload | null>(null);
  const postTypeConfigRef = React.useRef<import("pages/signage/PostTypeSignConfiguration").PostTypeSignConfigurationRef | null>(null);

  // Fetch general settings to show/hide Custom Item button
  useEffect(() => {
    if (!token) return;
    const url = `${process.env.REACT_APP_API_URL}/settings/general?permissions=false`;
    fetch(url, { method: "GET", headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.ok ? res.json() : Promise.reject(new Error("Failed to load settings")))
      .then((data: { results?: { key: string; enabled: boolean }[] }) => {
        const list = Array.isArray(data?.results) ? data.results : [];
        const custom = list.find((s) => s.key === GENERAL_SETTINGS_KEYS.CUSTOM_PRODUCT);
        setCustomProductSettingEnabled(custom?.enabled ?? false);
      })
      .catch(() => setCustomProductSettingEnabled(false));
  }, [token]);

  useEffect(() => {
    if (!token || !projectIdFromQuery) return;
    let mounted = true;
    const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    const getSearchRestoreRef = async () => {
      const deadline = Date.now() + 3000;
      while (mounted && Date.now() < deadline) {
        const ref = searchBatRestoreRef.current;
        if (ref) return ref;
        await wait(60);
      }
      return searchBatRestoreRef.current;
    };
    const loadProjectBillingAddress = async () => {
      try {
        const msgRes = await fetchPublicProjectMessagesByProjectId(projectIdFromQuery, 1, 20);
        if (!mounted || !Array.isArray(msgRes?.data) || msgRes.data.length === 0) return null;
        const latest = msgRes.data.find((m: any) => m?.billing_address) as any;
        if (!latest?.billing_address) return null;
        const billingAddr = latest.billing_address;
        projectBillingAddressRef.current = billingAddr;
        return billingAddr;
      } catch (error) {
        safeConsole.warn("Failed to load billing address from public project messages:", error);
        return null;
      }
    };
    (async () => {
      projectRestoreGuardRef.current = true;
      try {
        const projectData = await fetchProjectById(token, projectIdFromQuery);
        if (!mounted || !projectData) return;
        formik.setFieldValue("po_number", String(projectData.po_number || "").trim(), false);
        await loadProjectBillingAddress();
        const isUnsetMongoId = (id: unknown) =>
          !id || String(id).trim() === "" || String(id).trim() === "000000000000000000000000";
        const rawLt = String(projectData.license_type || "").trim().toUpperCase();
        const teamMemberId = String(projectData.team_member_id || "").trim();
        const teamMemberName = String(projectData.team_member_name || "").trim();
        const agentId = String(projectData.agent_id || "").trim();
        const agentName = String(projectData.agent_name || "").trim();
        const customerIdFromProject = String(projectData.customer_id || "").trim();
        const hasTeamMember = !isUnsetMongoId(teamMemberId) || Boolean(teamMemberName);
        const hasAgent = !isUnsetMongoId(agentId) || Boolean(agentName);
        /** Project license_type is authoritative when end user is a team member (customer_id may still match agent). */
        const isEndUserTeamMember =
          rawLt === "BROKERAGE_TEAM_MEMBER" ||
          (!isUnsetMongoId(teamMemberId) && customerIdFromProject === teamMemberId);
        const isEndUserAgent =
          !isEndUserTeamMember &&
          !isUnsetMongoId(agentId) &&
          customerIdFromProject === agentId;
        let uiType = rawLt;
        if (!uiType) {
          if (hasTeamMember) uiType = "BROKERAGE_TEAM_MEMBER";
          else if (hasAgent) uiType = "AGENT";
          else uiType = "BROKERAGE_FIRM";
        }

        const badge =
          ORDER_TYPE_BADGE_MAP[uiType] ||
          (hasTeamMember ? ORDER_TYPE_BADGE_MAP.BROKERAGE_TEAM_MEMBER : ORDER_TYPE_BADGE_MAP.AGENT);
        setOrderTypeBadge(badge);

        const fn = String(projectData.first_name || "").trim();
        const ln = String(projectData.last_name || "").trim();
        const projectLabel = isEndUserTeamMember
          ? teamMemberName || [fn, ln].filter(Boolean).join(" ").trim()
          : isEndUserAgent
            ? agentName || [fn, ln].filter(Boolean).join(" ").trim()
            : teamMemberName || agentName || [fn, ln].filter(Boolean).join(" ").trim();
        if (!projectLabel) return;
        const firstName = isEndUserTeamMember
          ? teamMemberName.split(/\s+/)[0] || fn
          : fn ||
            (isEndUserAgent
              ? agentName.split(/\s+/)[0]
              : teamMemberName.split(/\s+/)[0] || agentName.split(/\s+/)[0]) ||
            "";
        const lastName = isEndUserTeamMember
          ? teamMemberName.split(/\s+/).slice(1).join(" ") || ln
          : ln ||
            (isEndUserAgent
              ? agentName.split(/\s+/).slice(1).join(" ")
              : teamMemberName.split(/\s+/).slice(1).join(" ") ||
                agentName.split(/\s+/).slice(1).join(" ")) ||
            "";
        const email = String(projectData.customer_email || "").trim();
        const phone = withDefaultSignageContactPhone(
          String(
            (projectData as { customer_phone?: string; phone1?: string }).customer_phone ??
              (projectData as { phone1?: string }).phone1 ??
              ""
          ).trim()
        );
        const contact = { fName: firstName, lName: lastName, email, phone };
        const firmId = String(projectData.brokerage_firm_id || "").trim();
        const firmName = String(projectData.brokerage_firm_name || "").trim();

        if (uiType === "BROKERAGE_FIRM" && firmId) {
          const projectOfficeId = String(projectData.office?._id || "").trim();
          const tmIdForRestore = String(projectData.team_member_id || "").trim();
          const agentIdForRestore = String(projectData.agent_id || "").trim();
          setProjectContactPrefill({
            label: firmName,
            type: "BROKERAGE_FIRM",
            contact,
          });
          const ref = await getSearchRestoreRef();
          if (mounted && ref) {
            ref.restoreApplyPick({
              value: firmId,
              label: firmName,
              license_type: "BROKERAGE_FIRM",
              company_name: firmName,
            });
            await wait(80);
            if (mounted) {
              if (projectOfficeId && !isUnsetMongoId(projectOfficeId)) {
                await ref.restoreSelectOfficeById(projectOfficeId);
              }
              if (tmIdForRestore && !isUnsetMongoId(tmIdForRestore)) {
                await ref.restoreSelectTeamMemberById(tmIdForRestore);
              }
              if (isEndUserAgent && agentIdForRestore && !isUnsetMongoId(agentIdForRestore)) {
                await ref.restoreSelectAgentById(agentIdForRestore, {
                  label: agentName,
                  firstName: fn,
                  lastName: ln,
                  email,
                  phone,
                });
              } else {
                ref.restoreSyncContact(contact);
              }
              applyProjectBillingAddressRef.current?.();
            }
          }
          return;
        }

        if (isEndUserTeamMember || (uiType === "BROKERAGE_TEAM_MEMBER" && hasTeamMember)) {
          const tmLabel = teamMemberName || projectLabel || "Team member";
          const tmContact = { ...contact, fName: firstName, lName: lastName };
          const projectOfficeId = String(projectData.office?._id || "").trim();
          const projectOfficeName = String(projectData.office?.officeName || "").trim();
          setProjectContactPrefill({
            label: tmLabel,
            type: "BROKERAGE_TEAM_MEMBER",
            contact: tmContact,
          });
          if (projectOfficeId && !isUnsetMongoId(projectOfficeId)) {
            setSelectedOffice({
              value: projectOfficeId,
              label: projectOfficeName || "Office",
              firstName: projectOfficeName || "Office",
              lastName: "",
              email: "",
              phone: "",
            } as any);
            formik.setFieldValue("officeSelection", projectOfficeId);
          }
          const synthetic = {
            value: teamMemberId,
            label: tmLabel,
            license_type: "BROKERAGE_TEAM_MEMBER",
            email,
            phone,
            brokerage_firm_id: projectData.brokerage_firm_id,
            brokerage_firm_name: projectData.brokerage_firm_name,
            brokerage_name: projectData.brokerage_firm_name,
            firstName: tmContact.fName,
            lastName: tmContact.lName,
            offices:
              projectOfficeId && !isUnsetMongoId(projectOfficeId)
                ? { _id: projectOfficeId, officeName: projectOfficeName || "Office" }
                : undefined,
          };
          const ref = await getSearchRestoreRef();
          if (mounted && ref) {
            ref.restoreApplyPick(synthetic);
            await wait(100);
            if (mounted) {
              ref.restoreSyncContact(tmContact);
              const oid = String(projectData.office?._id || "").trim();
              if (oid && !isUnsetMongoId(oid)) {
                await ref.restoreSelectOfficeById(oid);
              }
              if (hasAgent && agentId && !isUnsetMongoId(agentId)) {
                await ref.restoreSelectAgentById(agentId, {
                  label: agentName,
                  firstName: agentName.split(/\s+/)[0] || fn,
                  lastName: agentName.split(/\s+/).slice(1).join(" ") || ln,
                  email,
                  phone,
                });
                ref.restoreSyncContact(tmContact);
              }
              const tmForAddr = teamMemberId || synthetic.value;
              if (tmForAddr && !isUnsetMongoId(tmForAddr)) {
                await fetchAddresses({ value: tmForAddr, label: tmLabel });
              }
              applyProjectBillingAddressRef.current?.();
            }
          }
          return;
        }

        if (!hasAgent && !agentName) {
          safeConsole.warn(
            "Add installation: project has no agent/team member to prefill (check license_type and ids)."
          );
          return;
        }

        const label = agentName || projectLabel;
        const agentLt = rawLt === "BROKERAGE_AGENT" ? "BROKERAGE_AGENT" : "AGENT";
        setProjectContactPrefill({
          label,
          type: agentLt,
          contact,
        });
        const syntheticAgent = {
          value: agentId,
          label,
          license_type: agentLt,
          email,
          phone,
          brokerage_firm_id: projectData.brokerage_firm_id,
          brokerage_firm_name: projectData.brokerage_firm_name,
          brokerage_name: projectData.brokerage_firm_name,
          firstName: contact.fName,
          lastName: contact.lName,
        };
        const ref = await getSearchRestoreRef();
        if (mounted && ref) {
          ref.restoreApplyPick(syntheticAgent);
          await wait(100);
          if (mounted) {
            ref.restoreSyncContact(contact);
            if (agentId && !isUnsetMongoId(agentId)) {
              await fetchAddresses({ value: agentId, label });
            }
            applyProjectBillingAddressRef.current?.();
          }
        }
      } catch (error) {
        safeConsole.warn("Failed to prefill project contact for installation:", error);
      } finally {
        projectRestoreGuardRef.current = false;
      }
    })();
    return () => {
      mounted = false;
      projectRestoreGuardRef.current = false;
    };
  }, [token, projectIdFromQuery]);

  const [openStatusIndex, setOpenStatusIndex] = useState<number | null>(null);
  const [installationCost, setInstallationCost] = useState<number | null>(null);
  const [isEditingInstallationCost, setIsEditingInstallationCost] = useState(false);
  const [manualInstallationCost, setManualInstallationCost] = useState("");
  const [scheduledJobsCount, setScheduledJobsCount] = useState<number | null>(null);
  const [totalUnits, setTotalUnits] = useState<number>(0);
  const [customProductSettingEnabled, setCustomProductSettingEnabled] = useState(false);
  const [customproducts, setCustomproducts] = useState<Array<{
    product_id: string;
    variant_id: string;
    item_name: string;
    quantity: number;
    Price: number;
    is_taxable: boolean;
    custom_information: string;
    length: number;
    width: number;
    height: number;
    weight: number;
  }>>([]);
  console.log("customproducts", customproducts);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedFileMeta, setUploadedFileMeta] = useState<any[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [previewFiles, setPreviewFiles] = useState<{ file: File; url: string }[]>([]);
  const [rotationalPoolProducts, setRotationalPoolProducts] = useState<any[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [warehouseOptions, setWarehouseOptions] = useState<any[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<any | null>(null);
  const [availableSerializedUnits, setAvailableSerializedUnits] = useState<any[]>([]);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [instructions, setInstructions] = useState<string>("");
  const [allocatedUnits, setAllocatedUnits] = useState<{
    [linkedProductId: string]: {
      rotationalProductId: string;
      warehouseId: string;
      serial_id: string;
      serial_number: string;
    }[];
  }>({});

  /** Manual unit price override per catalog line (volume/discount pricing comes from /orders/resolve). */
  const [manualUnitPriceMap, setManualUnitPriceMap] = useState<Record<string, number>>({});
  /** In-progress unit price text while user is typing (avoids controlled input snap-back). */
  const [unitPriceDraftMap, setUnitPriceDraftMap] = useState<Record<string, string>>({});
  const manualUnitPriceMapRef = useRef(manualUnitPriceMap);
  const unitPriceDebounceRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const PURCHASED_FIELD_MIN = 1;
  const purchasedQtyMinMsg = "Quantity must be at least 1.";
  const purchasedUnitCostMinMsg = "Unit cost must be at least 1.";
  type PurchasedProductFieldErrors = { quantity?: string; unitCost?: string };
  const [purchasedProductFieldErrors, setPurchasedProductFieldErrors] = useState<
    Record<string, PurchasedProductFieldErrors>
  >({});
  const purchasedProductErrorKey = (kind: "catalog" | "custom", id: string) => `${kind}:${id}`;
  const validatePurchasedQuantityValue = (raw: string | number | undefined): string | undefined => {
    if (raw === "" || raw === undefined || raw === null) return purchasedQtyMinMsg;
    const n = Number(raw);
    if (!Number.isFinite(n) || n < PURCHASED_FIELD_MIN) return purchasedQtyMinMsg;
    return undefined;
  };
  const validatePurchasedUnitCostValue = (raw: string | number | undefined): string | undefined => {
    if (raw === "" || raw === undefined || raw === null) return purchasedUnitCostMinMsg;
    const trimmed = String(raw).trim();
    if (trimmed === "" || trimmed === ".") return purchasedUnitCostMinMsg;
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n < PURCHASED_FIELD_MIN) return purchasedUnitCostMinMsg;
    return undefined;
  };
  const patchPurchasedProductFieldError = (
    errorKey: string,
    field: keyof PurchasedProductFieldErrors,
    message?: string
  ) => {
    setPurchasedProductFieldErrors((prev) => {
      const next = { ...prev };
      const row = { ...(next[errorKey] || {}) };
      if (message) row[field] = message;
      else delete row[field];
      if (Object.keys(row).length === 0) delete next[errorKey];
      else next[errorKey] = row;
      return next;
    });
  };
  const validateAllPurchasedProductFieldsRef = useRef<() => boolean>(() => true);
  const pendingRecomputeRef = useRef(false);
  useEffect(() => {
    manualUnitPriceMapRef.current = manualUnitPriceMap;
  }, [manualUnitPriceMap]);
  const [couponInput, setCouponInput] = React.useState("");
  const [couponResolveError, setCouponResolveError] = React.useState("");

  // ✅ Simple state to hold selected special fees (bypasses Formik timing issues)
  const [selectedSpecialFees, setSelectedSpecialFees] = useState<any[]>([]);
  const [allAvailableSpecialFees, setAllAvailableSpecialFees] = useState<any[]>([]);
  /** After Studio restore, merge snapshot fee rows with vendor-loaded options by id. */
  const [pendingStudioSnapSpecialFees, setPendingStudioSnapSpecialFees] = useState<any[] | null>(null);
  React.useEffect(() => {
    if (!pendingStudioSnapSpecialFees?.length || !allAvailableSpecialFees?.length) return;
    const merged = pendingStudioSnapSpecialFees.map((p) => {
      const opt = allAvailableSpecialFees.find((o: any) => String(o.id) === String(p.id));
      return opt
        ? {
            ...opt,
            amount: Number(p.amount ?? opt.amount ?? 0),
            taxable: normalizeInstallationSelectedFee(p, allAvailableSpecialFees).taxable,
            tax_percentage: p.tax_percentage ?? opt.tax_percentage ?? null,
            tax_amount: p.tax_amount ?? opt.tax_amount ?? null,
          }
        : p;
    });
    setSelectedSpecialFees(merged);
    setPendingStudioSnapSpecialFees(null);
  }, [pendingStudioSnapSpecialFees, allAvailableSpecialFees]);
  const [isRecomputingTax, setIsRecomputingTax] = React.useState<boolean>(false);
  const isRecomputingRef = React.useRef<boolean>(false); // ✅ Ref to track recompute status (more reliable than state)
  const recomputeRequestIdRef = React.useRef<number>(0); // ✅ Track unique recompute requests to prevent duplicates
  const lastRecomputeParamsRef = React.useRef<string>(""); // ✅ Track last recompute parameters to prevent duplicate calls
  const recomputeRef = React.useRef<
    ((options?: { force?: boolean; discountCode?: string }) => Promise<boolean>) | null
  >(null);
  const triggerRecomputeIfReadyRef = React.useRef<() => void>(() => {});
  const lastRecomputeWatchSigRef = React.useRef<string>("");
  const isUpdatingFromRecomputeRef = React.useRef<boolean>(false);

  const [linkedProductIdContext, setLinkedProductIdContext] = useState<string | null>(null);
  const [isMarkerDraggable, setIsMarkerDraggable] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const closePicker = () => setPickerOpen(false);
  const rows = useMemo(
    () =>
      selectedProducts.filter(
        (p) => p.is_new_catalog || (p.inventory_type !== "rotational" && p.inventory_type !== "")
      ),
    [selectedProducts]
  );
  const approvedProofCards = useMemo(() => {
    const approvedRoot = (approvedMessageData as any) || {};
    const approvedFiles = Array.isArray(approvedRoot?.approved_files) ? approvedRoot.approved_files : [];
    if (approvedFiles.length === 0) return [];

    const grouped = new Map<number, any[]>();
    approvedFiles.forEach((f: any) => {
      const version = Number(f?.version);
      const key = Number.isFinite(version) && version > 0 ? Math.floor(version) : 1;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(f);
    });

    return Array.from(grouped.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([version, files]) => {
        const first = files[0] || {};
        const compactProofFiles = files.map((f: any, idx: number) => ({
          id: String(f?.id ?? f?._id ?? idx),
          name: String(f?.original_name ?? f?.file_name ?? f?.name ?? "File"),
          href:
            String(f?.preview_url ?? "").trim() ||
            String(f?.file_url ?? f?.url ?? "").trim(),
          previewUrl: String(f?.preview_url ?? f?.file_url ?? f?.url ?? "").trim(),
          fileId: String(f?.file_id ?? "").trim(),
          quantity: Number.isFinite(Number(f?.quantity)) ? Number(f?.quantity) : undefined,
          sizeLabel: Number.isFinite(Number(f?.file_size)) ? `${Math.round(Number(f?.file_size) / 1024)} KB` : "—",
        }));
        const approvedByName = String(
          first?.design_approved_by?.name ??
          first?.updated_by?.name ??
          approvedRoot?.design_approved_by?.name ??
          approvedRoot?.approved_by_name ??
          ""
        ).trim();
        const approvedAtRaw =
          first?.design_approved_at ??
          first?.updated_at ??
          approvedRoot?.design_approved_at ??
          approvedRoot?.approved_at;
        const approvedAtLabel = (() => {
          if (!approvedAtRaw) return "";
          const dt = new Date(approvedAtRaw);
          return Number.isNaN(dt.getTime()) ? "" : dt.toLocaleString();
        })();
        const shippingName = String(first?.shipping_method?.name ?? approvedRoot?.shipping_method?.name ?? "").trim();
        const shippingDesc = String(first?.shipping_method?.description ?? approvedRoot?.shipping_method?.description ?? "").trim();
        const shippingAmountRaw = first?.shipping_amount ?? approvedRoot?.shipping_amount;
        const shippingAmountLabel = Number.isFinite(Number(shippingAmountRaw))
          ? `$${Number(shippingAmountRaw).toFixed(2)}`
          : "";
        const assignedToName = String(approvedRoot?.assigned_to_name ?? "").trim();
        const orderSummary = resolveApprovedProofVersionSummary(approvedRoot, version, first);
        const billingAddress =
          first?.billing_address ??
          approvedRoot?.billing_address ??
          null;
        const installationAddress =
          first?.shipping_address ??
          approvedRoot?.shipping_address ??
          null;
        return {
          version,
          files: compactProofFiles,
          approvedByName,
          approvedAtLabel,
          assignedToName,
          shippingName,
          shippingDesc,
          shippingAmountLabel,
          orderSummary,
          billingAddress,
          installationAddress,
        };
      });
  }, [approvedMessageData]);


  const updateProducts = (next: FlattenedProduct[]) => {
    setSelectedProducts(next);
    console.log("new product add in Selected products :", next)
    // onProductsChange?.(next);
  };
  const handleAddFromPicker = async (flattened: FlattenedProduct) => {
    // Previous flow blocked adding the same product more than once:
    // const exists = selectedProducts.some((p) => p.product_id === flattened.product_id);
    // if (exists) {
    //   showError?.("Product already added");
    //   return;
    // }

    const nextRow: FlattenedProduct = {
      ...flattened,
      rowKey:
        flattened.rowKey ||
        `${flattened.product_id}-${flattened.variant_id ?? ""}-${flattened.slotKey ?? "manual"}-${Date.now()}-${Math.random()
          .toString(36)
          .slice(2, 8)}`,
    };
    const next = [...selectedProducts, nextRow];
    setPickerOpen(false);

    // New/catalog product: no inventory API; use data passed from child for Product Selection and line_items
    if (flattened.is_new_catalog) {
      updateProducts(next);
      return;
    }
    if (!pickerUserType || !pickerUserId) {
      updateProducts(next);
      return;
    }
    const productIds = next.map(p => p.product_id);

    // Use allSettled so one failed fetch (e.g. catalog product 404) doesn't block adding the product
    const results = await Promise.allSettled(
      productIds.map(id =>
        fetch(`${process.env.REACT_APP_API_URL}/inventory/${pickerUserType}/user/${pickerUserId}/product/${encodeURIComponent(id)}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        }).then(async r => ({ id, raw: await r.text(), ok: r.ok }))
      )
    );
    const responses = results.map((r) => (r.status === "fulfilled" ? r.value : { id: "", raw: "", ok: false }));

    // Build a map: product_id -> available
    const byId = new Map<string, number>();
    for (const { id, raw, ok } of responses) {
      if (!ok || !raw.trim()) continue;
      try {
        const parsed = JSON.parse(raw) as { available: number } | { product_id: string; available: number }[];
        if (Array.isArray(parsed)) {
          parsed.forEach(it => it?.product_id && byId.set(it.product_id, Number(it.available ?? 0)));
        } else {
          byId.set(id, Number(parsed.available ?? 0));
        }
      } catch {
        // ignore malformed responses for now
      }
    }

    const merged = next.map(p => ({
      ...p,
      availableqty: byId.get(p.product_id) ?? p.availableqty ?? 0,
      location: p.location || []
    }));
    updateProducts(merged);

  };

  // const handleAddFromPicker = (flattened: FlattenedProduct) => {
  //   const exists = selectedProducts.some((p) => p.product_id === flattened.product_id);
  //   if (exists) {
  //     showError?.("Product already added");
  //     return;
  //   }
  //   const next = [...selectedProducts, flattened];
  //   updateProducts(next);
  //   setPickerOpen(false);

  // };

  const removeRow = (rowKeyOrProdId: string) => {
    const item = selectedProducts.find((p) => (p.rowKey || p.product_id) === rowKeyOrProdId);
    if (item?.slotKey) postTypeConfigRef.current?.clearSlot(item.slotKey);
    if (item?.is_new_catalog && item.product_id) {
      const key = `${item.product_id}:${item.variant_id ?? ""}`;
      setManualUnitPriceMap((prev) => {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setUnitPriceDraftMap((prev) => {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      if (unitPriceDebounceRef.current[key]) {
        clearTimeout(unitPriceDebounceRef.current[key]);
        delete unitPriceDebounceRef.current[key];
      }
      setPurchasedProductFieldErrors((prev) => {
        const next = { ...prev };
        delete next[purchasedProductErrorKey("catalog", key)];
        return next;
      });
    }
    updateProducts(selectedProducts.filter((p) => (p.rowKey || p.product_id) !== rowKeyOrProdId));
  };

  const navigate = useNavigate();
  const safeNavigate = createSafeNavigate(navigate);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_SCRIPT_LIBRARIES,
  });

  const [selectedLocation, setSelectedLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const installationMapRef = useRef<google.maps.Map | null>(null);
  const selectedLocationRef = useRef<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    selectedLocationRef.current = selectedLocation;
  }, [selectedLocation]);
  const [orderTypeBadge, setOrderTypeBadge] = useState<{ label: string; cls: string } | null>(null);

  const [selectedBrokerageFirm, setSelectedBrokerageFirm] = useState<any | null>(null);

  const [selectedOffice, setSelectedOffice] = useState<Selected | null>(null);

  const [selectedOfficeAgent, setSelectedOfficeAgent] = useState<Selected | null>(null);

  const [selectedInstaller, setSelectedInstaller] = useState<Selected | null>(null);

  const [selectedAgent, setSelectedAgent] = useState<Selected | null>(null);

  const [customerGroupId, setCustomerGroupId] = useState<string | null>(null);

  const [selectedBillingAddress, setSelectedBillingAddress] =
    useState<Addresses | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);

  // const [selectedInstallationAddress, setSelectedInstallationAddress] =
  // useState<Address | null>(null);
  const [selectedOfficeBillingAddress, setSelectedOfficeBillingAddress] =
    useState<Addresses | null>(null);

  // const [selectedOfficeInstallationAddress, setSelectedOfficeInstallationAddress] =
  // useState<Address | null>(null);

  const [installationAddress, setInstallationAddress] = useState<Addresses>();
  // state for banner below widget
  const [dynamicCostId, setDynamicCostId] = React.useState<string>("");
  const [dynamicTaxable, setDynamicTaxable] = React.useState<boolean>(false);
  // wherever you render cost breakdown:
  // const [couponDiscounts, setCouponDiscounts] = React.useState<Discount[]>([]);
  const [showTaxDetails, setShowTaxDetails] = useState(false);
  const pendingInstructionFilesRef = useRef<{ id: string; file: File }[]>([]);
  const handlePendingInstructionFilesChange = useCallback((items: { id: string; file: File }[]) => {
    pendingInstructionFilesRef.current = items;
  }, []);

  const buyerOptions: { value: string; label: string }[] = [
    { value: "brokerageFirm", label: "Brokerage Firm" },
    { value: "agent", label: "Agent" },
  ];

  // Removed brokerageOptions - now using AsyncPaginate with loadBrokerageOptions

  // Removed teamMemberOptions - now using AsyncPaginate with loadTeamMemberOptions (based on office)

  // Removed officeOptions - now using AsyncPaginate with loadOfficeOptions (new flow)

  // Removed officeAgentOptions - now using AsyncPaginate with loadAgentOptions (based on office)

  // Removed agentOptions - now using AsyncPaginate

  const productOptions = productObjs?.map((item) => ({
    value: item.product_id,
    label: item.product.name,
  }));

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  // Fetch parent hierarchy for signageteammember login
  useEffect(() => {
    if (isSignageTeammember && token) {
      fetchParentHierarchy();
    }
  }, [isSignageTeammember, token]);

  const pickerUserType: "agent" | "office" | null =
    selectedOfficeAgent?.value || selectedAgent?.value
      ? "agent"
      : selectedOffice?.value
        ? "office"
        : null;

  const pickerUserId: string =
    selectedOfficeAgent?.value ||
    selectedAgent?.value ||
    selectedOffice?.value ||
    "";

  /** End user id for accessory-rules API only: agent or teammember, never office. When office is selected, use teammember id (or agent id if agent also selected). */
  const pickerEndUserId: string | undefined =
    selectedOfficeAgent?.value ||
    selectedAgent?.value ||
    selectedTeamMemberOption?.value ||
    selectedTeamMember?.value ||
    undefined;

  /** End customer for POST .../customer_side/datatable (not brokerage/office/signage firm ids). */
  const postTypeOrderContext = useMemo((): import("pages/signage/PostTypeSignConfiguration").PostTypeOrderEndUserContext | null => {
    if (selectedOfficeAgent?.value) {
      return {
        userId: String(selectedOfficeAgent.value),
        licenseType: String(
          (selectedOfficeAgent as OfficeAgentOption).license_type || "BROKERAGE_AGENT"
        ).trim(),
      };
    }
    if (selectedAgent?.value) {
      return {
        userId: String(selectedAgent.value),
        licenseType: String((selectedAgent as OfficeAgentOption).license_type || "AGENT").trim(),
      };
    }
    if (selectedTeamMemberOption?.value) {
      return {
        userId: String(selectedTeamMemberOption.value),
        licenseType: String(
          (selectedTeamMemberOption as TeamMemberOption).license_type || "BROKERAGE_TEAM_MEMBER"
        ).trim(),
      };
    }
    return null;
  }, [selectedOfficeAgent, selectedAgent, selectedTeamMemberOption]);

  const canShowAdd = Boolean(pickerUserId);
  const filtered = (selectedProducts || []).filter(
    (p) => p.is_new_catalog || (p.inventory_type !== "rotational" && p.inventory_type !== "")
  );

  const isPurchasedProductLine = (product: Partial<FlattenedProduct>) => {
    const inventoryType = String(product?.inventory_type || "").toLowerCase();
    return Boolean(product?.is_new_catalog) || inventoryType === "custom";
  };

  const customCatalogUnitsPurchased = useMemo(
    () =>
      (selectedProducts || []).reduce((sum, p) => {
        const isCustomInventory = String(p?.inventory_type || "").toLowerCase() === "custom";
        const shouldCount = Boolean(p?.is_new_catalog) || isCustomInventory;
        if (!shouldCount) return sum;
        return sum + (Number(p?.quantity) || 0);
      }, 0) +
      (customproducts || []).reduce((sum, cp) => sum + (Number(cp?.quantity) || 0), 0),
    [selectedProducts, customproducts]
  );
  const colCount = 7; // update if your table has a different number of columns
  const pickerSignageCompanyId: string = (isSignageTeammember && parentSignageId ? parentSignageId : userId) || "";

  // Fetch parent hierarchy for signageteammember login
  const fetchParentHierarchy = async () => {
    if (!isSignageTeammember || !token) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data = await response.json();

        // Find the parent with license_type "SIGNAGE_FIRM"
        const signageFirm = data.parent_chain?.find(
          (parent: any) => parent.license_type === "SIGNAGE_FIRM"
        );

        if (signageFirm) {
          setParentSignageId(signageFirm.id || signageFirm._id);
          setParentSignageName(signageFirm.name);
        }
      }
    } catch (error) {
      safeConsole.error("Error fetching parent hierarchy:", error);
      // Send error email notification
      await handleApiError(error, {
        module: "installation",
        action: "Signage_Add_Installation_fetch_parent_hierarchy",
        userId: userId || undefined,
        licenseType: licenseType || undefined,
        userName: userName || undefined,
        url: `${process.env.REACT_APP_API_URL}/parent-hierarchy`,
        method: "GET"
      }, token);
    }
  };

  const uploadOrderDocumentsToOrder = useCallback(
    async (orderDocumentId: string, files: File[]) => {
      const id = String(orderDocumentId || "").trim();
      if (!token || !id || !files.length) return;

      const fd = new FormData();
      fd.append("id", id);
      fd.append("collection_name", "installation");
      files.forEach((f) => fd.append("files[]", f));

      const res = await fetch(`${process.env.REACT_APP_API_URL}/upload-documents-to-order`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      if (!res.ok) {
        const raw = await res.json().catch(() => ({}));
        const msg =
          (raw as any)?.message ||
          (raw as any)?.error ||
          `Upload failed (${res.status})`;
        throw new Error(typeof msg === "string" ? msg : "Upload failed");
      }
    },
    [token]
  );

  // ===== Step 1: Normalize ID once when building Brokerage options =====
  const normalizeId = (row: any) => String(row?.id ?? row?._id ?? "");
  const PAGE_SIZE_AGENT = 10;

  // ===== Agent loader using Select2 endpoint =====
  const loadAgentOptions: LoadOptions<OfficeAgentOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;
      const q = (inputValue || "").trim();
      const signageId = (isSignageTeammember && parentSignageId) ? parentSignageId : userId;

      try {

        if (!signageId) {
          return { options: [], hasMore: false, additional: { page: 1 } };
        }

        const url =
          `${process.env.REACT_APP_API_URL}/users/signage/${signageId}/agents/select2` +
          `?page=${page}&limit=${PAGE_SIZE_AGENT}` +
          (q ? `&search=${encodeURIComponent(q)}` : "");

        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          safeConsole.error("Failed to fetch agents:", response.statusText);
          return { options: [], hasMore: false, additional: { page } };
        }

        const json = await response.json();

        const list = Array.isArray(json) ? json : json.data ?? json.results ?? [];

        const options: OfficeAgentOption[] = list.map((u: any) => ({
          value: normalizeId(u),
          label: u?.name || `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "(no name)",
          firstName: u?.firstName || "",
          lastName: u?.lastName || "",
          email: u?.email || "",
          phone: withDefaultSignageContactPhone(primaryPhoneFromUserLike(u)),
          license_type: String(u?.license_type || "AGENT").trim(),
        }));

        const hasMore = Array.isArray(json)
          ? options.length === PAGE_SIZE_AGENT
          : json?.pagination?.more ??
          (json?.total ? page * (json?.limit ?? PAGE_SIZE_AGENT) < json.total : options.length === PAGE_SIZE_AGENT);

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (err) {
        safeConsole.error("Failed to load agent options:", err);
        // Send error email notification
        await handleApiError(err, {
          module: "installation",
          action: "Signage_Add_Installation_load_agent_options",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/users/signage/${signageId}/agents/select2`,
          method: "GET",
          additionalInfo: {
            page,
            search: q || "",
            signageId: signageId || null,
            isSignageTeammember,
            parentSignageId: parentSignageId || null
          }
        }, token);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [userId, isSignageTeammember, parentSignageId, token, licenseType, userName]
  );

  const mapBrokerageToOption = (row: any) => ({
    value: normalizeId(row), // ALWAYS store the id in value
    label: row?.firmDetails?.companyName || row?.text || row?.name || "(no name)",
    firstName: row?.firstName || "",
    lastName: row?.lastName || "",
    email: row?.email || "",
    phone: withDefaultSignageContactPhone(primaryPhoneFromUserLike(row)),
  });

  // ===== Step 2: Single source of truth for brokerage (moved before loadOfficeOptions) =====
  const brokerageId = selectedBrokerageFirm?.value || null;

  // ===== Office loader using Select2 endpoint (new flow) =====
  const loadOfficeOptions: LoadOptions<Selected, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;

      // Guard: without brokerage, do nothing
      if (!brokerageId) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      const q = (inputValue || "").trim();
      const officePayload = {
        page,
        limit: PAGE_SIZE,
        search: q,
      };
      try {

        const url = `${process.env.REACT_APP_API_URL}/office/select/${brokerageId}`;

        const response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(officePayload),
        });

        if (!response.ok) {
          safeConsole.error("Failed to fetch offices:", response.statusText);
          return { options: [], hasMore: false, additional: { page } };
        }

        const json = await response.json();

        const list = json?.results || [];

        const options: Selected[] = list.map((office: any) => {
          const addr0 = office.officeAddress?.[0] as { phone?: string; phone1?: string } | undefined;
          return {
            value: office.id || office._id,
            label: office.officeName || "---",
            firstName: office.officeName || "",
            lastName: "",
            email: "",
            phone: withDefaultSignageContactPhone(
              String(addr0?.phone1 ?? addr0?.phone ?? "").trim()
            ),
          };
        });

        const hasMore = json?.pagination?.more ?? false;

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (err) {
        safeConsole.error("Failed to load office options:", err);
        // Send error email notification
        await handleApiError(err, {
          module: "installation",
          action: "Signage_Add_Installation_load_office_options",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/office/select/${brokerageId}`,
          method: "POST",
          requestBody: officePayload,
          additionalInfo: {
            page,
            search: q || "",
            brokerageId: brokerageId || null
          }
        }, token);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [brokerageId, token, userId, licenseType, userName]
  );


  // Note: Office loading is now handled by AsyncPaginate with loadOfficeOptions



  // Handle office selection state changes (agents now loaded via AsyncPaginate)
  const handleOfficeSelection = (office: any) => {
    // ✅ CASCADE RESET: Office → Reset team member, agent, addresses
    teamMemberFromContinueRef.current = null;
    setSelectedTeamMember(null);
    setSelectedTeamMemberOption(null);
    setSelectedOfficeAgent(null);
    setSelectedAgent(null);
    if (!projectRestoreGuardRef.current) {
      setAddressObjects([]);
      setSelectedBillingAddress(null);
      setSelectedOfficeBillingAddress(null);
      setInstallationAddress(undefined);
      setSelectedProducts([]);
      setProductObjs([]);
      formik.setFieldValue("billingAddressId", "");
      formik.setFieldValue("installationAddressId", "");
    }

    // Reset dependent form values (keep email and phone from brokerage since office doesn't have them)
    formik.setFieldValue("teamMemberId", "");
    formik.setFieldValue("teamMemberName", "");
    formik.setFieldValue("agentSelection", "");
    formik.setFieldValue("userFirstName", "");
    formik.setFieldValue("userLastName", "");

    // Set office
    setSelectedOffice(office);
  };

  // Removed fetchAgents - now using AsyncPaginate with Select2 endpoint

  const fetchAddresses = async (user: any) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/user-addresses/${user?.value}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      const list = Array.isArray(data) ? data : Array.isArray((data as { addresses?: unknown[] })?.addresses)
        ? (data as { addresses: unknown[] }).addresses
        : [];
      const merged = mergeProjectBillingAddress(list as any[]);
      setAddressObjects(merged);
      return merged;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "An unknown error occurred";
      setError(errorMessage);
      await handleApiError(err, {
        module: "installation",
        action: "Signage_Add_Installation_fetch_addresses",
        userId: userId || undefined,
        licenseType: licenseType || undefined,
        userName: userName || undefined,
        url: `${process.env.REACT_APP_API_URL}/user-addresses/${user?.value}`,
        method: "GET",
        additionalInfo: {
          userId: user?.value || null,
          userName: user?.label || null
        }
      }, token);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // const fetchOfficeAddresses = async (office: any) => {
  //   console.log("fetchOfficeAddresses IN")
  //   try {
  //     const response = await fetch(
  //       `${process.env.REACT_APP_API_URL}/offices/${office?.value}`,
  //       {
  //         method: "GET",
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //           "Content-Type": "application/json",
  //         },
  //       }
  //     );
  //     const data = await response.json();
  //     console.log("office addresses :", data.officeAddress);
  //     setAddressObjects(data.officeAddress);
  //   } catch (err) {
  //     setError(
  //       err instanceof Error ? err.message : "An unknown error occurred"
  //     );
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  useEffect(() => {
    if (selectedOfficeAgent) {
      fetchAddresses(selectedOfficeAgent);
    } else if (selectedAgent) {
      fetchAddresses(selectedAgent);
    } else if (selectedOffice) {
      fetchAddresses(selectedOffice);
    }
  }, [selectedAgent, selectedOfficeAgent, selectedOffice, token]);

  const addressOptions =
    addressObjects !== null
      ? [
        { value: "", label: "Select Address", _id: "", address_details: null },
        ...addressObjects?.map((address) => ({
          ...address,
          value: resolveSalesOrderAddressId(address) || address._id,
          label: `${address?.label}: ${address?.address_details?.addressLine1}, ${address.address_details?.city}, ${address?.address_details?.state}, ${address?.address_details?.zip ? address?.address_details?.zip : ''}`,
        })) || []
      ]
      : [{ value: "", label: "Select Address", _id: "", address_details: null }];

  // const fetchLinkedProducts = async (
  //   userType: "agent" | "office",
  //   user_Id: string,
  // ) => {
  //   try {
  //     const response = await fetch(
  //       `${process.env.REACT_APP_API_URL}/inventory/${userType}/${user_Id}/signage/${(isSignageTeammember && parentSignageId ? parentSignageId : userId) || ""}`,
  //       {
  //         method: "GET",
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //           "Content-Type": "application/json",
  //         },
  //       }
  //     );

  //     const data = await response.json();
  //     setProductObjs(data);

  //     if (!response.ok) {
  //       showError(data?.error || "Failed to fetch linked products");
  //     } else {
  //       // setLinkedProducts(data); if needed
  //     }
  //   } catch (err) {
  //     setError(err instanceof Error ? err.message : "Unknown error occurred");
  //   }
  // };

  // Fetch individual product by ID based on inventory_type
  const fetchProductById = async (
    inventoryType: "agent" | "office",
    userId: string,
    productId: string
  ): Promise<ProductObj | null> => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/${inventoryType}/${userId}/inventory/${productId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // console.error(`Failed to fetch product ${productId}:`, errorData?.error || response.statusText);
        return null;
      }

      const data = await response.json();
      return data;
    } catch (err) {
      // console.error(`Error fetching product ${productId}:`, err);
      // Send error email notification
      await handleApiError(err, {
        module: "installation",
        action: "Signage_Add_Installation_fetch_product_by_id",
        userId: userId || undefined,
        licenseType: licenseType || undefined,
        userName: userName || undefined,
        url: `${process.env.REACT_APP_API_URL}/${inventoryType}/${userId}/inventory/${productId}`,
        method: "GET",
        additionalInfo: {
          inventoryType: inventoryType || null,
          productId: productId || null,
          userId: userId || null
        }
      }, token);
      return null;
    }
  };

  // const fetchAvailablequantity = async (
  //   userType: "agent" | "office",
  //   user_Id: string
  // ) => {
  //   try {
  //     const productIds = selectedProducts.map(item => item.product_id);
  //     const idsParam = productIds.join(",");

  //     const responses = await fetch(
  //       `${process.env.REACT_APP_API_URL}/inventory/${pickerUserType}/user/${pickerUserId}/product/${idsParam}`,
  //       {
  //         method: "GET",
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //           "Content-Type": "application/json",
  //         },
  //       }
  //     );

  //     // const data1 = await responses.json();
  //     // console.log("Available Products:", data1);
  //     const data1: { available: number } = await responses.json();

  //     // ✅ Build a new array, then pass it
  //     const merged = selectedProducts.map((p) => ({
  //       ...p,
  //       availableqty: data1.available, // overwrite or add
  //     }));
  //     updateProducts(merged);

  //   } catch (err) {
  //     console.error("Error fetching available quantity:", err);
  //   }
  // };


  // useEffect(() => {
  //   if (selectedProducts.length > 0 && pickerUserType && pickerUserId) {
  //     fetchAvailablequantity(pickerUserType, pickerUserId);
  //   }
  // }, [selectedProducts, pickerUserType, pickerUserId]);
  // Note: Signage team members are now loaded via AsyncPaginate in the Assigned To dropdown

  // based on instaletion Address we are calling the dyniamc cost
  useEffect(() => {
    const fetchInstallationCost = async () => {
      if (studioRestoreLockRef.current) return;
      if (
        !installationAddress?.address_details?.latitude ||
        !installationAddress?.address_details?.longitude ||
        installationAddress?.address_details?.latitude === 0 ||
        installationAddress?.address_details?.longitude === 0
      ) {
        showError(
          "Selected installation address is missing latitude/longitude. Please enter the address manually."
        );
        return;
      }

      const costPayload = {
        installation_address: {
          addressLine1: installationAddress?.address_details?.addressLine1,
          addressLine2: installationAddress?.address_details?.addressLine2,
          city: installationAddress?.address_details?.city,
          state: installationAddress?.address_details?.state,
          country: installationAddress?.address_details?.country,
          zip: installationAddress?.address_details?.zip,
          cityCode: installationAddress?.address_details?.cityCode,
          stateCode: installationAddress?.address_details?.stateCode,
          countryCode: installationAddress?.address_details?.countryCode,
          latitude: installationAddress?.address_details?.latitude,
          longitude: installationAddress?.address_details?.longitude,
        }
      };
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/installation_orders/dynamic_cost/${(isSignageTeammember && parentSignageId ? parentSignageId : userId) || ""}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(costPayload),
          }
        );

        const data = await response.json();
        if (!response.ok) throw new Error(data?.error || "Failed to fetch cost");
        setDynamicCostId(data.id);
        setDynamicTaxable(data.is_taxable || data.taxabl);
        setInstallationCost(data.cost || 0); // adjust based on response shape
        triggerRecomputeIfReadyRef.current();
      } catch (error: any) {
        safeConsole.error("Cost fetch error:", error.message);
        setInstallationCost(null);
        // Send error email notification
        await handleApiError(error, {
          module: "installation",
          action: "Signage_Add_Installation_fetch_installation_cost",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/installation_orders/dynamic_cost/${(isSignageTeammember && parentSignageId ? parentSignageId : userId) || ""}`,
          method: "POST",
          requestBody: costPayload,
          additionalInfo: {
            isSignageTeammember,
            parentSignageId: parentSignageId || null,
            installationAddress: installationAddress ? "present" : "missing"
          }
        }, token);
      }
    };
    if (installationAddress) {
      fetchInstallationCost();
    }
  }, [installationAddress, token]);

  // ===== Types =====
  type Status = "on_site" | "with_supplier";

  type LinkedInstallerLocation = {
    quantity: number;
    warehouse_id: string;
    warehouse_name?: string;
    zone_id?: string;
    zone_name?: string;
    rack_id?: string;
    rack_name?: string;
    bin_id?: string;
    bin_name?: string;
  };

  type LinkedInstallerRow = {
    current_status: Status;                 // "on_site" | "with_supplier"
    product_location_status: string;        // mirror to result rows
    quantity?: number;                      // for bucket rows (no per-location)
    location?: LinkedInstallerLocation[];   // for per-location rows
  };

  type LinkedQuantityLocation = {
    quantity: number;
    current_status: Status;                 // EXACTLY "on_site" or "with_supplier"
    product_location_status: string;
    location?: Array<LinkedInstallerLocation & { quantity: number }>;
  };

  // ===== Helpers =====
  const fail = (msg: string): never => { showError(msg); throw new Error(msg); };

  // Helper: treat job-site items as not needing warehouse/bin/rack/zone
  const isOnSiteStatus = (p?: any, m?: any) => {
    const s = [
      p?.product_current_status,
      p?.current_status,
      m?.product_current_status,
      m?.current_status,
    ].map(v => (v ?? "").toString().toLowerCase());

    const ls = [
      p?.product_location_status,
      m?.product_location_status,
    ].map(v => (v ?? "").toString().toLowerCase());

    return s.includes("on_site") || ls.includes("at_installation_site");
  };

  function normalizeStatus(s: any): Status {
    const k = String(s || "").trim().toLowerCase();
    if (k === "on_site" || k === "on site" || k === "onsite") return "on_site";
    if (k === "with_supplier" || k === "with supplier" || k === "withsupplier") return "with_supplier";
    return "with_supplier";
  }

  const sumAll = (rows: LinkedInstallerRow[]) =>
    rows.reduce((t, r) => {
      if (r.location?.length) return t + r.location.reduce((s, l) => s + (Number(l.quantity) || 0), 0);
      return t + (Number(r.quantity) || 0);
    }, 0);

  const sumStorage = (items: LinkedQuantityLocation[] = []) =>
    items.reduce((t, r) => t + (Number(r.quantity) || 0), 0);

  // function getLiRowsFromMatched(matched: any): LinkedInstallerRow[] {
  //   const rows: any[] = matched?.linked_installer_details?.storage_locations ?? [];
  //   console.log("Matched LI storage locations:", matched);
  //   console.log("Raw LI rows:", rows);
  //   if (!Array.isArray(rows) || rows.length === 0) return [];
  //   return rows.map((r) => {
  //     const status = normalizeStatus(r.current_status);
  //     const hasLocs = Array.isArray(r.location) && r.location.length > 0;
  //     return {
  //       current_status: status,
  //       product_location_status: String(r.product_location_status || ""),
  //       quantity: hasLocs ? undefined : Number(r.quantity) || 0,
  //       location: hasLocs
  //         ? r.location.map((loc: any) => ({
  //           quantity: Number(loc.quantity) || 0,
  //           warehouse_id: String(loc.warehouse_id || ""),
  //           warehouse_name: String(loc.warehouse_name || ""),
  //           zone_id: String(loc.zone_id || ""),
  //           zone_name: String(loc.zone_name || ""),
  //           rack_id: String(loc.rack_id || ""),
  //           rack_name: String(loc.rack_name || ""),
  //           bin_id: String(loc.bin_id || ""),
  //           bin_name: String(loc.bin_name || ""),
  //         }))
  //         : [],
  //     };
  //   });
  // }

  function getLiRowsFromMatched(matched: any): LinkedInstallerRow[] {
    const rows: any[] =
      matched?.linked_installer_details?.storage_locations ?? [];

    // If backend didn't send storage_locations but item is on-site,
    // synthesize a single unlimited on-site bucket (no warehouse hierarchy).
    if (!Array.isArray(rows) || rows.length === 0) {
      // Handle on-site (no storage_locations case)
      const details = matched?.linked_installer_details;
      if (isOnSiteStatus(details, matched)) {
        return [
          {
            current_status: "on_site",
            product_location_status:
              details?.product_location_status ||
              matched?.product?.product_location_status ||
              "at_installation_site",
            quantity: Number.POSITIVE_INFINITY,
            location: [],
          },
        ];
      }

      return []; // everything else unchanged
    }


    // Normal mapping when rows exist
    return rows.map((r) => {
      const status = normalizeStatus(r.current_status);
      const hasLocs = Array.isArray(r.location) && r.location.length > 0;
      return {
        current_status: status as Status,
        product_location_status: String(r.product_location_status || ""),
        quantity: hasLocs ? undefined : Number(r.quantity) || 0,
        location: hasLocs
          ? r.location.map((loc: any) => ({
            quantity: Number(loc.quantity) || 0,
            warehouse_id: String(loc.warehouse_id || ""),
            warehouse_name: String(loc.warehouse_name || ""),
            zone_id: String(loc.zone_id || ""),
            zone_name: String(loc.zone_name || ""),
            rack_id: String(loc.rack_id || ""),
            rack_name: String(loc.rack_name || ""),
            bin_id: String(loc.bin_id || ""),
            bin_name: String(loc.bin_name || ""),
          }))
          : [],
      };
    });
  }

  /**
   * Strict allocator (Add / Edit delta)
   * - on_site first (bucket rows only)
   * - with_supplier must be EXACTLY ONE source (one bucket OR one warehouse location)
   * - If total available < requested OR single-source cannot cover remainder → showError + throw
   */
  function allocateFromLinkedInstallerStrict(
    requestedQty: number,
    liRows: LinkedInstallerRow[],
    preferredWarehouseId?: string // (optional) prefer this WH when adding delta on edit
  ): LinkedQuantityLocation[] {
    // console.log("Allocating strictly:", { requestedQty, liRows, preferredWarehouseId });
    if (!Number.isFinite(requestedQty) || requestedQty <= 0) {
      fail(`Invalid requested quantity: ${requestedQty}`);
    }
    if (!Array.isArray(liRows) || liRows.length === 0) {
      fail("No linked installer storage rows available for allocation.");
    }
    const totalAvail = sumAll(liRows);
    if (totalAvail < requestedQty) {
      fail(`Insufficient stock. Available: ${totalAvail}, Requested: ${requestedQty}.`);
    }

    let remaining = requestedQty;
    const out: LinkedQuantityLocation[] = [];

    // A) ON_SITE buckets first (no per-location)
    const onSiteBuckets = liRows.filter(
      (r) => r.current_status === "on_site" && (r.location?.length ?? 0) === 0
    );
    for (const row of onSiteBuckets) {
      if (remaining === 0) break;
      const avail = Number(row.quantity) || 0;
      const take = Math.min(remaining, avail);
      if (take > 0) {
        out.push({
          quantity: take,
          current_status: "on_site",
          product_location_status: row.product_location_status,
        });
        remaining -= take;
      }
    }
    if (remaining === 0) return out;

    // B) WITH_SUPPLIER — SINGLE SOURCE ONLY

    // B1) Try a single supplier BUCKET that fully covers remaining
    const supplierBuckets = liRows.filter(
      (r) => r.current_status === "with_supplier" && (r.location?.length ?? 0) === 0
    );
    const bucketThatFits = supplierBuckets.find((r) => (Number(r.quantity) || 0) >= remaining);
    if (bucketThatFits) {
      out.push({
        quantity: remaining,
        current_status: "with_supplier",
        product_location_status: bucketThatFits.product_location_status,
        location: [
          {
            quantity: remaining,
            warehouse_id: "",
            warehouse_name: "",
            zone_id: "",
            zone_name: "",
            rack_id: "",
            rack_name: "",
            bin_id: "",
            bin_name: "",
          },
        ],
      });
      remaining = 0;
      return out;
    }

    // B2) Try ONE per-location warehouse that covers all remaining
    type Candidate = { qty: number; pls: string; loc: LinkedInstallerLocation };
    const candidates: Candidate[] = [];
    liRows.forEach((row) => {
      if (row.current_status !== "with_supplier") return;
      (row.location || []).forEach((loc) => {
        candidates.push({
          qty: Number(loc.quantity) || 0,
          pls: row.product_location_status,
          loc,
        });
      });
    });

    let chosen: Candidate | undefined;
    if (preferredWarehouseId) {
      chosen = candidates.find(
        (c) => c.loc.warehouse_id === preferredWarehouseId && c.qty >= remaining
      );
    }
    if (!chosen) chosen = candidates.find((c) => c.qty >= remaining);

    if (!chosen) {
      const maxSingle = Math.max(
        supplierBuckets.reduce((m, r) => Math.max(m, Number(r.quantity) || 0), 0),
        candidates.reduce((m, c) => Math.max(m, c.qty), 0)
      );
      fail(
        `Cannot satisfy remaining ${remaining} from a single supplier source (max single source: ${maxSingle}).`
      );
    }

    out.push({
      quantity: remaining,
      current_status: "with_supplier",
      product_location_status: chosen!.pls,
      location: [
        {
          quantity: remaining,
          warehouse_id: chosen!.loc.warehouse_id,
          warehouse_name: chosen!.loc.warehouse_name,
          zone_id: chosen!.loc.zone_id,
          zone_name: chosen!.loc.zone_name,
          rack_id: chosen!.loc.rack_id,
          rack_name: chosen!.loc.rack_name,
          bin_id: chosen!.loc.bin_id,
          bin_name: chosen!.loc.bin_name,
        },
      ],
    });
    remaining = 0;

    return out;
  }

  /** Merge supplier entries ONLY if they all reference the same single warehouse (or same blank bucket). */
  function combineSupplierSingleWarehouse(items: LinkedQuantityLocation[]): LinkedQuantityLocation[] {
    const onSite = items.filter((i) => i.current_status === "on_site");
    const supplier = items.filter((i) => i.current_status === "with_supplier");
    if (supplier.length <= 1) return items;

    const ids = supplier.map((s) => (s.location && s.location.length > 0 ? s.location[0].warehouse_id : ""));
    const allSingle = supplier.every((s) => (s.location?.length ?? 0) <= 1);
    const firstId = ids[0];
    const sameId = ids.every((id) => id === firstId);
    if (!allSingle || !sameId) {
      fail("Multiple supplier sources detected; policy requires a single supplier warehouse.");
    }

    const pls = supplier[0].product_location_status || "";
    const totalQty = supplier.reduce((n, s) => n + (Number(s.quantity) || 0), 0);

    if (firstId === "") {
      return [
        ...onSite,
        {
          quantity: totalQty,
          current_status: "with_supplier",
          product_location_status: pls,
          location: [{ quantity: totalQty, warehouse_id: "", warehouse_name: "", zone_id: "", zone_name: "", rack_id: "", rack_name: "", bin_id: "", bin_name: "" }],
        },
      ];
    } else {
      const tmpl = supplier.find((s) => s.location && s.location.length > 0)!.location![0];
      return [
        ...onSite,
        {
          quantity: totalQty,
          current_status: "with_supplier",
          product_location_status: pls,
          location: [{
            quantity: totalQty,
            warehouse_id: tmpl.warehouse_id,
            warehouse_name: tmpl.warehouse_name,
            zone_id: tmpl.zone_id,
            zone_name: tmpl.zone_name,
            rack_id: tmpl.rack_id,
            rack_name: tmpl.rack_name,
            bin_id: tmpl.bin_id,
            bin_name: tmpl.bin_name,
          }],
        },
      ];
    }
  }

  /** Trim for decreases; error if you try to trim more than allocated. */
  function trimStorageDown(oldStorage: LinkedQuantityLocation[], toRemove: number): LinkedQuantityLocation[] {
    let remaining = toRemove;
    const copy = JSON.parse(JSON.stringify(oldStorage)) as LinkedQuantityLocation[];

    // Trim supplier first (per-location)
    const sIdx = copy.findIndex((s) => s.current_status === "with_supplier");
    if (sIdx >= 0 && remaining > 0) {
      const s = copy[sIdx];
      if (Array.isArray(s.location) && s.location.length > 0) {
        for (let i = s.location.length - 1; i >= 0 && remaining > 0; i--) {
          const loc = s.location[i];
          const take = Math.min(remaining, Number(loc.quantity) || 0);
          loc.quantity -= take;
          remaining -= take;
          if (loc.quantity <= 0) s.location.splice(i, 1);
        }
        s.quantity = (s.location ?? []).reduce((n, l) => n + (Number(l.quantity) || 0), 0);
        if ((s.location?.length ?? 0) === 0) copy.splice(sIdx, 1);
      } else if ((Number(s.quantity) || 0) > 0) {
        const take = Math.min(remaining, Number(s.quantity) || 0);
        (s as any).quantity = (Number(s.quantity) || 0) - take;
        remaining -= take;
        if ((Number(s.quantity) || 0) <= 0) copy.splice(sIdx, 1);
      }
    }

    // Trim on_site next
    if (remaining > 0) {
      for (let i = copy.length - 1; i >= 0 && remaining > 0; i--) {
        const row = copy[i];
        if (row.current_status !== "on_site") continue;
        const take = Math.min(remaining, Number(row.quantity) || 0);
        row.quantity -= take;
        remaining -= take;
        if (row.quantity <= 0) copy.splice(i, 1);
      }
    }

    if (remaining > 0) fail(`Cannot trim more than allocated; leftover ${remaining}.`);
    return copy;
  }

  /** Edit reconcile (uses strict allocator + errors) */
  function reconcileStorageForEdit(
    originalStorage: LinkedQuantityLocation[] | undefined,
    requestedQty: number,
    liRows: LinkedInstallerRow[]
  ): LinkedQuantityLocation[] {
    const old = originalStorage ?? [];
    const oldQty = sumStorage(old);

    if (requestedQty === oldQty) return old;

    if (requestedQty > oldQty) {
      const delta = requestedQty - oldQty;

      // Prefer same supplier WH if old has exactly one supplier location
      let preferredWh: string | undefined;
      const oldSuppliers = old.filter((s) => s.current_status === "with_supplier");
      if (oldSuppliers.length === 1 && (oldSuppliers[0].location?.length ?? 0) === 1) {
        preferredWh = oldSuppliers[0].location![0].warehouse_id || undefined;
      }

      const extra = allocateFromLinkedInstallerStrict(delta, liRows, preferredWh);
      const combined = combineSupplierSingleWarehouse([...old, ...extra]);
      return combined;
    }

    // requested < old
    return trimStorageDown(old, oldQty - requestedQty);
  }


  const DEFAULT_TEN_ZEROS = DEFAULT_SIGNAGE_CONTACT_PHONE;
  const withDefault = (v?: string, def = DEFAULT_TEN_ZEROS) =>
    !v || v.trim() === "" || v === def ? def : v;

  const formik = useFormik({
    initialValues: {
      userType: "",
      userFirstName: "",
      userLastName: "",
      userEmail: "",
      userPhone: "",
      draft_unique_id: "",
      brokerageFirm: "",
      teamMemberId: "",
      teamMemberName: "",
      officeSelection: "",
      agentSelection: "",
      company: "",
      update_status: "",
      product_status: "",
      office: "",
      agent: "",
      scheduled_date: "",
      scheduled_time: "",
      jobs_today: 0,
      installeId: "",
      // priority: 3,
      priority: null,
      billing_address: "",
      installation_address: "",
      isSameAsBilling: false,
      billingAddressId: "",
      installationAddressId: "",
      location: "",
      products: "",
      instructions: instructions,
      payment_method: "",
      image: null,
      discount: 0,
      subtotal: 0,
      total: 0,
      tax: 0,
      localLawsChecked: false,
      po_number: "",
      gatecode: "",
      label_of_address: "",
      firstName: "",
      lastName: "",
      phone: DEFAULT_TEN_ZEROS || "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      zipcode: "",
      country: "",
      installation_cost: 0,
      install_taxable: true,
      install_tax_percentage: null,
      calc_install_rate_str: 0,
      calc_overall_rate_str: 0,
      calc_fee_rate_str: 0,
      destination: { addressLine1: "", city: "", state: "", zip: "", country: "US", stateCode: "", countryCode: "US" },
      totals: { installation_subtotal: 0, installation_tax_amount: 0, specialfee_subtotal: 0, specialfee_tax_amount: 0, grand_total: 0, units_cost: 0, product_tax_amount: 0, cartId: "", specialfee_ids: [], specialfee_names: [] },
      resolved_line_items: [] as ResolvedInstallationLineItem[],
      tax_status: "",
      discount_amount: 0,
      product_discount_amount: 0,
      discount_code: "",
      additional_cost: 0,
      subtotal_amount: 0,
      already_paid: 0,
      specialfee: [] as Array<{
        id: string;             // ui id
        name: string;           // ui label
        amount: number;         // raw amount
        taxable: boolean;       // ui flag
        tax_percentage?: number | null;
        tax_amount?: number | null;
        tax_rule_id?: string;
        tax_group_id?: string;
      }>

    },
    validationSchema: Yup.object({
      // brokerageFirm: Yup.string().when("userType", {
      //   is: "brokerageFirm",
      //   then: (schema) => schema.required("Brokerage member is required"),
      //   otherwise: (schema) => schema.notRequired(),
      // }),
      officeSelection: Yup.string().when("userType", {
        is: "brokerageFirm",
        then: (schema) => schema.required("Office selection is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      // ✅ Team Member required when userType is brokerageFirm (NEW FLOW)
      // teamMemberId: Yup.string().when("userType", {
      //   is: "brokerageFirm",
      //   then: (schema) => schema.required("Team member is required"),
      //   otherwise: (schema) => schema.notRequired(),
      // }),
      agentSelection: Yup.string().when("userType", {
        is: "agent",
        then: (schema) => schema.required("Agent selection is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      userType: Yup.string().required("User type is required"),
      installeId: Yup.string().required("Installer is required"),
      priority: priority,
      userEmail: Yup.string().email("Invalid email").required("Email is required"),
      userPhone: Yup.string().required("Phone number is required"),
      billingAddressId: Yup.string().required("Billing address is required"),

      installationAddressId: Yup.string().when("isSameAsBilling", {
        is: false,   // ✅ explicitly check false
        then: (schema) => schema.required("Installation address is required"),
        otherwise: (schema) => schema.notRequired()
      }),
      isSameAsBilling: Yup.boolean(),

    }),
    onSubmit: async (values) => {
      // console.log("onSubmit values:", values);

      // ✅ Validate required special fees BEFORE submission
      const requiredFees = allAvailableSpecialFees.filter((f: any) => f.required === true);
      const selectedFeeIds = selectedSpecialFees.map((f: any) => String(f.id));
      const missingRequiredFees = requiredFees.filter((f: any) => !selectedFeeIds.includes(String(f.id || f._id)));

      if (missingRequiredFees.length > 0) {
        const feeNames = missingRequiredFees.map((f: any) => f.name).join(", ");
        showError(`The following required special fees must be selected: ${feeNames}`);
        return; // Don't submit
      }

      // Post-type: every visible slot except main_panel must have a product (or slot image).
      // Main panel never blocks; custom-only orders are allowed when no rider/topper/etc. are open.
      const slotErrors = postTypeConfigRef.current?.validateInstallationPost?.() ?? [];
      if (slotErrors.length > 0) {
        showError(slotErrors[0]);
        return;
      }

      const searchValidation = searchBatRestoreRef.current?.validate?.({
        officeId: formik.values.officeSelection,
        teamMemberId: formik.values.teamMemberId,
      });
      if (searchValidation && !searchValidation.valid) {
        showError(
          searchValidation.errors.teamMember ||
            searchValidation.errors.office ||
            searchValidation.errors.search ||
            "Complete office and team member selection."
        );
        return;
      }

      setFormSubmitting(true);
      let installationOrderPayload: any = undefined;
      try {
        // Example file upload handling
        const imageUrl = values.image ? await uploadFile(values.image) : null;

        if (!ready) {
          showError("Complete installer and installation address before saving.");
          setFormSubmitting(false);
          return;
        }

        const resolveOk = await recompute({ force: true });
        if (!resolveOk || !lastResolvedInstallationRef.current) {
          showError("Pricing could not be calculated. Please try again.");
          setFormSubmitting(false);
          return;
        }

        installationOrderPayload = await buildInstallationOrderPayloadRef.current?.(values);
        if (!installationOrderPayload) {
          showError("Installation order payload is not ready.");
          setFormSubmitting(false);
          return;
        }

        const ip = installationOrderPayload as Record<string, any>;
        const finalInstallationAddress = ip.installation_details?.installation_address;
        const finalBillingAddress = ip.billing_address;
        const lineItems = ip.line_items;
        const totalgrand = Number(ip.total_amount ?? 0);
        const agentName = ip.agent_name;

        const response = await fetch(`${process.env.REACT_APP_API_URL}/installation_orders`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`, // ensure token is set
          },
          body: JSON.stringify(installationOrderPayload),
        });

        const data = await response.json();

        if (!response.ok) {
          const userEmailError = typeof data?.userEmail === "string" ? data.userEmail : "";
          const userPhoneError = typeof data?.userPhone === "string" ? data.userPhone : "";

          if (userEmailError || userPhoneError) {
            if (userEmailError) {
              formik.setFieldError("userEmail", userEmailError);
              formik.setFieldTouched("userEmail", true, false);
            }
            if (userPhoneError) {
              formik.setFieldError("userPhone", userPhoneError);
              formik.setFieldTouched("userPhone", true, false);
            }
            return;
          }

          showError(data?.error || data?.message || "Failed to create installation order");
          return;
        }
        if (data?.order_id) {
          const pendingFiles = pendingInstructionFilesRef.current.map((x) => x.file);
          if (pendingFiles.length > 0) {
            try {
              await uploadOrderDocumentsToOrder(String(data.order_id), pendingFiles);
              pendingInstructionFilesRef.current = [];
            } catch (uploadErr) {
              showError(
                uploadErr instanceof Error
                  ? `Installation created, but attachments failed to upload: ${uploadErr.message}`
                  : "Installation created, but attachments failed to upload."
              );
            }
          }
          showSuccess("Installation order created successfully!");

          // If this installation order is created from a project context, link quote to project.
          // Same as SignageCreateOrder: URL (?project_id=) or Project card selection (linkedProjectId).
          const effectiveProjectIdForLink = userClearedProjectRef.current
            ? ""
            : String(linkedProjectId || projectIdFromQuery || "").trim();
          if (effectiveProjectIdForLink && token) {
            try {
              const linkedOrderId = String(data.order_id || "").trim();
              const linkedOrderNumber = String(data.order_number || linkedOrderId).trim();
              if (linkedOrderId) {
                await linkProjectQuote(token, effectiveProjectIdForLink, {
                  linked_order_id: linkedOrderId,
                  linked_order_number: linkedOrderNumber,
                  linked_order_type: "service_order",
                });
              }
            } catch (err) {
              safeConsole.warn("Failed to link created installation order to project:", err);
            }
          }
          
          // Send "Installation order created" emails (centralized)
          try {
            const from = process.env.REACT_APP_FROM_EMAIL2;
            safeConsole.log("Sending installation order created emails, from:", from);
            if (from) {
              // Format order ID
              const orderId = `#${data.order_number || data.order_id || "INST-XXXX"}`;
              safeConsole.log("Order ID for emails:", orderId);
              
              // Get signage company name
              const signageCompanyName = (isSignageTeammember && parentSignageName) ? parentSignageName : userName || "Signage Company";
              const { fetchUserData } = await import("utils/userDataFetcher");

              const customerName = agentName || (selectedOffice?.label) || (selectedBrokerageFirm?.label) || "Customer";
              const currentUserId = getAuthCookie("userId");
              const installationOrderId = String(
                (data as any)?.order_id ||
                (data as any)?.installation_order_id ||
                (data as any)?._id ||
                (data as any)?.id ||
                ""
              ).trim();
              // if (installationOrderId) {
              //   await sendInstallationOrderTriggerEmail({
              //     triggerName: "Signage Installation order created",
              //     installationOrderId,
              //     token: token || "",
              //     fromEmail: from,
              //     includeOrderPdfAttachment: true,
              //     extraParams: {
              //       orderId,
              //       "Order ID": orderId,
              //       "Order Number": orderId,
              //       firstName: String(values.userFirstName || "").trim() || "Customer",
              //       scheduledDate: values.scheduled_date
              //         ? new Date(values.scheduled_date).toLocaleDateString("en-US", {
              //             month: "2-digit",
              //             day: "2-digit",
              //             year: "numeric",
              //           })
              //         : "N/A",
              //       agentName: agentName || "N/A",
              //       signageCompanyName,
              //     },
              //   });
              // } else {
              //   safeConsole.warn("Missing installation order id for centralized email trigger");
              // }

              const selectedAgentForEmail = selectedOfficeAgent || selectedAgent;
              const hasAgentRecipient = !!selectedAgentForEmail?.email;
              const hasTeamMemberRecipient = !!values.teamMemberId && !!selectedBrokerageFirm;

              // ✅ Trigger: Installation Order Created - Send notifications
              try {
                const { createNotification } = await import("../../services/notificationApi");
                const { getInstallationOrderCreatedContent } = await import("../../services/notificationContent");
                
                // Send notification to Signage Admin/Team Member (always internal)
                if (currentUserId && token) {
                  try {
                    const currentUserDataForNotif = await fetchUserData(currentUserId, token);
                    const signageContent = getInstallationOrderCreatedContent({
                      orderId: orderId,
                      createdBy: customerName, // Brokerage / Agent Name
                      recipientType: currentUserDataForNotif?.licenseType === "SIGNAGE_TEAM_MEMBER" ? "team_member" : "signage_admin",
                    });
                    await createNotification(currentUserId, signageContent, token, currentUserDataForNotif?.licenseType === "SIGNAGE_TEAM_MEMBER" ? "team_member" : "signage_admin");
                  } catch (err) {
                    safeConsole.warn("Failed to send notification to signage admin:", err);
                  }
                }

                // Also notify Assigned To signage team member (installer) in UI, if different from creator
                if (selectedInstaller?.value && token) {
                  try {
                    if (!currentUserId || selectedInstaller.value !== currentUserId) {
                      const installerContent = getInstallationOrderCreatedContent({
                        orderId: orderId,
                        createdBy: customerName,
                        recipientType: "team_member",
                      });
                      await createNotification(selectedInstaller.value, installerContent, token, "team_member");
                    }
                  } catch (err) {
                    safeConsole.warn("Failed to send notification to assigned installer:", err);
                  }
                }

                // Send notification to Brokerage Team (only if no agent or team member end user)
                if (!hasAgentRecipient && !hasTeamMemberRecipient && selectedBrokerageFirm?.value && token) {
                  const brokerageContent = getInstallationOrderCreatedContent({
                    orderId: orderId,
                    signageCompanyName: signageCompanyName,
                    recipientType: "brokerage_team",
                  });
                  await createNotification(selectedBrokerageFirm.value, brokerageContent, token, "brokerage_team");
                }

                // Send notification to Team Member (if different from brokerage, and no agent end user)
                if (!hasAgentRecipient && values.teamMemberId && selectedBrokerageFirm && values.teamMemberId !== selectedBrokerageFirm.value && token) {
                  try {
                    const teamMemberData = await fetchUserData(values.teamMemberId, token);
                    if (teamMemberData?.id) {
                      const teamMemberContent = getInstallationOrderCreatedContent({
                        orderId: orderId,
                        signageCompanyName: signageCompanyName,
                        recipientType: "brokerage_team",
                      });
                      await createNotification(teamMemberData.id, teamMemberContent, token, "brokerage_team");
                    }
                  } catch (err) {
                    safeConsole.warn("Failed to send notification to team member:", err);
                  }
                }

                // Send notification to Agent (preferred end user)
                if (selectedAgentForEmail?.value && token) {
                  const agentContent = getInstallationOrderCreatedContent({
                    orderId: orderId,
                    signageCompanyName: signageCompanyName,
                    recipientType: "agent",
                  });
                  await createNotification(selectedAgentForEmail.value, agentContent, token, "agent");
                }
              } catch (notifError) {
                safeConsole.warn("Failed to send installation order created notifications:", notifError);
              }
            }
          } catch (emailError) {
            safeConsole.warn("Failed to send installation order creation emails:", emailError);
            // Don't fail the order creation if email fails
          }
          
          safeNavigate(`${baseRoute}/jobs/installationWorkOrder/${data.order_id}?type=View`);
        }
        clearInstallationDraftId();
      } catch (error: any) {
        showError(`Error: ${error.message}`);
        // Send error email notification
        await handleApiError(error, {
          module: "installation",
          action: "Signage_Add_Installation_create_installation_order",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/installation_orders`,
          method: "POST",
          requestBody: installationOrderPayload,
          additionalInfo: {
            hasProducts: selectedProducts.length > 0,
            hasAddress: !!installationAddress,
            buyerType: formik.values.brokerageFirm ? "brokerageFirm" : "agent"
          }
        }, token);
      } finally {
        setFormSubmitting(false);
      }
    }
  });

  applyProjectBillingAddressRef.current = () => {
    const billingAddr = projectBillingAddressRef.current;
    const billingId = resolveSalesOrderAddressId(billingAddr);
    if (!billingId) return;
    setAddressObjects((prev) => mergeProjectBillingAddress(prev));
    formik.setFieldValue("billingAddressId", billingId, false);
    setSelectedBillingAddress(billingAddr as any);
    setSelectedOfficeBillingAddress(billingAddr as any);
  };

  const handleInstallationFormSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
    if (e) {
      e.preventDefault();
    }

    if (!validateAllPurchasedProductFieldsRef.current()) {
      showError("Quantity and unit cost must be at least 1 for purchased products.");
      return;
    }

    if (!String(projectIdFromQuery || "").trim()) {
      const searchValidation = searchBatRestoreRef.current?.validate?.({
        officeId: String(formik.values.officeSelection || selectedOffice?.value || "").trim(),
        teamMemberId: String(formik.values.teamMemberId || "").trim(),
      });
      if (searchValidation && !searchValidation.valid) {
        showError(
          searchValidation.errors.teamMember ||
            searchValidation.errors.office ||
            searchValidation.errors.search ||
            "Complete office and team member selection."
        );
      }
    }

    void formik.setTouched(
      {
        ...formik.touched,
        teamMemberId: true,
        officeSelection: true,
      },
      false
    );
    formik.handleSubmit(e);
  };

  const { values, setFieldValue } = formik;

  React.useEffect(() => {
    const pid = String(linkedProjectId || "").trim();
    if (!token) return;
    if (!pid || String(projectIdFromQuery || "").trim()) {
      if (!pid && !String(projectIdFromQuery || "").trim()) {
        setFieldValue("po_number", "", false);
      }
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const projectData = await fetchProjectById(token, pid);
        if (cancelled || !projectData) return;
        setFieldValue("po_number", String(projectData.po_number || "").trim(), false);
      } catch {
        /* project PO is optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, linkedProjectId, projectIdFromQuery, setFieldValue]);

  // Studio return (?batch_id=&context_id=): merge design onto line items + restore full installation_order snapshot (batch root or context), mirroring SignageCreateOrder batch restore.
  React.useEffect(() => {
    const key = `${studioReturnBatchId}|${studioReturnContextId}`;
    if (!studioReturnBatchId || !studioReturnContextId || !token) return;
    if (restoredStudioReturnKeyRef.current === key) return;

    let cancelled = false;

    const isBlankId = (id: unknown) =>
      !id || String(id).trim() === "" || String(id).trim() === "000000000000000000000000";

    (async () => {
      try {
        const batch = await getCustomizationBatch(studioReturnBatchId);
        if (cancelled) return;
        {
          const rawItems = (batch as { items?: unknown[] }).items;
          if (Array.isArray(rawItems)) {
            setStudioBatchLineItems(
              rawItems
                .map((it: unknown) => {
                  const row = it as Record<string, unknown>;
                  const cust = (row.customization ?? {}) as Record<string, unknown>;
                  const pid = String(row.product_id ?? row.line_item_key ?? "").trim();
                  const vid = String(row.variant_id ?? "").trim();
                  return {
                    product_id: pid,
                    variant_id: vid,
                    design_id: String(cust.design_id ?? "").trim() || undefined,
                    preview_id: String(cust.preview_id ?? "").trim() || undefined,
                    design_name: String(cust.design_name ?? "").trim() || undefined,
                    context_id: String(row.active_context_id ?? "").trim() || undefined,
                  } satisfies StudioBatchLineItem;
                })
                .filter((x) => x.product_id && x.variant_id)
            );
          } else {
            setStudioBatchLineItems([]);
          }
        }
        const contexts = normalizeCustomizationBatchContexts(batch);
        const io = getInstallationOrderFromBatch(batch);
        if (contexts.length === 0 && !io) return;

        const findContextForRow = (row: { product_id?: string; variant_id?: string }) => {
          const pid = String(row.product_id ?? "").trim();
          const vid = String(row.variant_id ?? "").trim();
          const samePv = (c: (typeof contexts)[number]) =>
            String(c.product_id ?? "").trim() === pid && String(c.variant_id ?? "").trim() === vid;
          return (
            contexts.find((c) => c.context_id === studioReturnContextId && samePv(c)) ??
            contexts.find(samePv)
          );
        };

        const mergeContextsIntoRows = () => {
          if (contexts.length === 0) return;
          setSelectedProducts((prev) => {
            if (!prev.length) return prev;
            let changed = false;
            const updated = prev.map((row) => {
              const ctx = findContextForRow(row);
              if (!ctx || !ctx.design_id) return row;
              changed = true;
              const pv = String(ctx.preview_id ?? "").trim();
              const usePreviewForThumb = pv && isMongoObjectId(pv);
              return {
                ...row,
                studio_design_id: ctx.design_id,
                studio_preview_id: ctx.preview_id,
                studio_design_name: ctx.design_name,
                studio_context_id: ctx.context_id,
                design_id: ctx.design_id,
                design_name: String(ctx.design_name ?? ""),
                preview_url: pv,
                ...(usePreviewForThumb ? { image_url: pv } : {}),
              };
            });
            return changed ? updated : prev;
          });
        };

        if (io && !customerGroupId) {
          mergeContextsIntoRows();
          return;
        }

        studioRestoreLockRef.current = !!io;

        mergeContextsIntoRows();

        const apiBase = process.env.REACT_APP_API_URL || "";

        let buyerRestoredViaBat = false;
        let addressesFromBat = false;

        if (io && typeof io === "object") {
          const snapAny = io as Record<string, unknown>;
          const ut = String(snapAny.user_type ?? "").toLowerCase();

          if (customerGroupId) {
            const match = await findBuyerSearchMatchForStudioRestore(
              apiBase,
              token,
              customerGroupId,
              io as Record<string, any>,
              () => cancelled
            );
            if (cancelled) return;

            let bat = searchBatRestoreRef.current;
            if (match && !bat) {
              await new Promise((r) => window.setTimeout(r, 50));
              bat = searchBatRestoreRef.current;
            }

            if (match && bat) {
              bat.restoreApplyPick(match);
              await new Promise((r) => window.setTimeout(r, 0));

              const lt = effectiveLicenseTypeForItem(match);
              const officeIdRaw = (io as Record<string, any>).office?._id ?? (io as Record<string, any>).office?.id;
              const teamIdRaw = (io as Record<string, any>).team_member_id;

              if (lt === "BROKERAGE_FIRM" || lt === "BROKERAGE_TEAM_MEMBER") {
                if (isMongoObjectId(officeIdRaw)) {
                  await bat.restoreSelectOfficeById(String(officeIdRaw));
                }
                if (isMongoObjectId(teamIdRaw)) {
                  await bat.restoreSelectTeamMemberById(String(teamIdRaw));
                }
              } else if (lt === "OFFICE" && isMongoObjectId(teamIdRaw)) {
                await bat.restoreSelectTeamMemberById(String(teamIdRaw));
              }

              // Re-apply snapshot contact after office/team: restoreSelectTeamMemberById overwrites phone from API.
              bat.restoreSyncContact(contactFromInstallationOrderSnapshot(io as Record<string, unknown>));

              buyerRestoredViaBat = true;

              const waitForAddressList = async () => {
                for (let i = 0; i < 100; i++) {
                  if (cancelled) return [];
                  const a = addressObjectsMirrorRef.current;
                  if (Array.isArray(a) && a.length) return a;
                  await new Promise((r) => window.setTimeout(r, 100));
                }
                return Array.isArray(addressObjectsMirrorRef.current) ? addressObjectsMirrorRef.current : [];
              };

              const addrList = await waitForAddressList();
              if (cancelled) return;

              const addrBind = studioRestoreInstallationAddressBindings(io as Record<string, any>, addrList);
              setFieldValue("isSameAsBilling", addrBind.formik.isSameAsBilling, false);
              setFieldValue("billingAddressId", addrBind.formik.billingAddressId, false);
              setFieldValue("installationAddressId", addrBind.formik.installationAddressId, false);

              if (addrBind.useOfficeBilling) {
                setSelectedOfficeBillingAddress(addrBind.billingAddressObj);
              } else {
                setSelectedBillingAddress(addrBind.billingAddressObj);
              }
              if (addrBind.installationAddressObj) {
                setInstallationAddress(addrBind.installationAddressObj as Addresses);
              }
              addressesFromBat = true;
            }
          }

          if (!buyerRestoredViaBat) {
            if (ut === "agent" && snapAny.agent_id) {
              const agentId = String(snapAny.agent_id).trim();
              const agentName = String(snapAny.agent_name ?? "").trim();
              const { first, last } = splitFullName(
                typeof snapAny.user_name === "string" ? snapAny.user_name : agentName
              );
              const phone = withDefaultSignageContactPhone(
                String(
                  snapAny.user_phone ??
                    (snapAny as { phone1?: string; user_phone1?: string }).user_phone1 ??
                    (snapAny as { phone1?: string }).phone1 ??
                    ""
                ).trim()
              );
              const email = String(snapAny.user_email ?? "").trim();
              setSelectedAgent({
                value: agentId,
                label: agentName || agentId,
                firstName: first,
                lastName: last,
                email,
                phone,
              });
              setFieldValue("userType", "agent", false);
              setFieldValue("agentSelection", agentId, false);
              setFieldValue("userFirstName", first, false);
              setFieldValue("userLastName", last, false);
              setFieldValue("userEmail", email, false);
              setFieldValue("userPhone", phone, false);

              const addrList = await fetchAddresses({ value: agentId, label: agentName || agentId });
              if (cancelled) return;

              const bil = snapAny.billing_address as Record<string, unknown> | undefined;
              if (bil && typeof bil === "object") {
                const bilId = String(bil._id ?? "").trim();
                if (!isBlankId(bilId) && Array.isArray(addrList)) {
                  const found = addrList.find((a: { _id?: string }) => String(a._id) === bilId);
                  if (found) setSelectedBillingAddress(found);
                  else setSelectedBillingAddress(bil as Addresses);
                } else {
                  setSelectedBillingAddress(bil as Addresses);
                }
                if (!isBlankId(bilId)) setFieldValue("billingAddressId", bilId, false);
              }
            } else if (ut === "brokeragefirm" || ut === "brokerage_firm") {
              const bf = String(snapAny.brokerage_firm_id ?? "").trim();
              if (bf) {
                setFieldValue("userType", "brokerageFirm", false);
                setFieldValue("brokerageFirm", bf, false);
              }
              const phoneSnap = withDefaultSignageContactPhone(
                String(
                  snapAny.user_phone ??
                    (snapAny as { phone1?: string; user_phone1?: string }).user_phone1 ??
                    (snapAny as { phone1?: string }).phone1 ??
                    ""
                ).trim() || primaryPhoneFromUserLike(snapAny as Record<string, unknown>)
              );
              setFieldValue("userPhone", phoneSnap, false);
            }
          }

          const instDet = snapAny.installation_details as Record<string, unknown> | undefined;
          if (instDet && typeof instDet === "object") {
            const { date, time } = parseIsoToDateAndTime(String(instDet.scheduled_date ?? ""));
            if (date) setFieldValue("scheduled_date", date, false);
            if (time) setFieldValue("scheduled_time", time, false);

            const instId = String(instDet.installer_id ?? "").trim();
            const instName = String(instDet.installer_name ?? "").trim();
            const contact = withDefaultSignageContactPhone(
              String(
                instDet.contact_number ??
                  (instDet as { phone1?: string }).phone1 ??
                  ""
              ).trim()
            );
            if (instId && !isBlankId(instId)) {
              setSelectedInstaller({
                value: instId,
                label: instName || instId,
                firstName: "",
                lastName: "",
                email: "",
                phone: contact,
              });
              setFieldValue("installeId", instId, false);
            }

            if (!addressesFromBat) {
              const instAddr = instDet.installation_address as Record<string, unknown> | undefined;
              if (instAddr && typeof instAddr === "object" && (instAddr.address_details || instAddr.label)) {
                const normalized = buildAddressesFromInstallationSnapshot(
                  instAddr as Record<string, unknown>
                );
                setInstallationAddress((normalized ?? instAddr) as Addresses);
                if (!snapAny.both_address_same) {
                  setFieldValue(
                    "installationAddressId",
                    installationAddressDisplayFromSnapshot(instAddr as Record<string, unknown>),
                    false
                  );
                }
              }
            }
          }

          if (!addressesFromBat) {
            const same = !!snapAny.both_address_same;
            setFieldValue("isSameAsBilling", same, false);
            if (same && snapAny.billing_address) {
              const bil = snapAny.billing_address as Record<string, unknown>;
              const bid = String(bil._id ?? "").trim();
              if (!isBlankId(bid)) setFieldValue("installationAddressId", bid, false);
            }
          }

          const ai0 = (instDet?.additional_instructions as Array<{ value?: string }> | undefined)?.[0]?.value;
          const addl = snapAny.additional_instructions as Array<{ value?: string }> | undefined;
          const instr =
            typeof ai0 === "string" ? ai0 : typeof addl?.[0]?.value === "string" ? addl[0].value : "";
          setFieldValue("instructions", instr, false);
          setInstructions(instr);

          setFieldValue("localLawsChecked", !!snapAny.local_laws_compliance, false);
          const prNorm = normalizeInstallationPriority(snapAny.priority);
          if (prNorm != null) {
            setFieldValue("priority", prNorm, false);
          }
          setFieldValue("gatecode", String(snapAny.gate_access_code ?? ""), false);

          const feeRows = Array.isArray(snapAny.specialfee) ? (snapAny.specialfee as Record<string, unknown>[]) : [];
          const feeUi = feeRows
            .map((f) => mapSavedInstallationSpecialFeeToUi(f))
            .filter((f) => f.id !== "");
          if (feeUi.length) {
            setSelectedSpecialFees(feeUi);
            setPendingStudioSnapSpecialFees(feeUi);
          }

          const sub = Number(snapAny.subtotal_amount ?? 0);
          const grand = Number(snapAny.total_amount ?? 0);
          const installCost = Number(snapAny.installation_cost ?? 0);
          const feeSum = feeUi.reduce((s, f) => s + Number(f.amount ?? 0), 0);
          setInstallationCost(Number.isFinite(installCost) ? installCost : null);
          setFieldValue("totals.installation_subtotal", sub, false);
          setFieldValue("totals.specialfee_subtotal", feeSum, false);
          setFieldValue("totals.grand_total", grand, false);
          const cid = String(snapAny.cart_id ?? "").trim();
          if (cid) setFieldValue("totals.cartId", cid, false);

          const ipRaw = snapAny.installation_post as Record<string, unknown> | undefined;
          if (ipRaw && typeof ipRaw === "object" && typeof ipRaw.post_type_id === "string") {
            const slots = Array.isArray(ipRaw.slots) ? ipRaw.slots : [];
            const ipPayload = {
              post_type_id: String(ipRaw.post_type_id),
              illustration: String(ipRaw.illustration ?? ""),
              slots: slots as import("pages/signage/PostTypeSignConfiguration").InstallationPostPayload["slots"],
            };
            setInstallationPost(ipPayload);
            setSelectedPostTypeId(ipPayload.post_type_id);
            if (slots.length > 0) {
              requestAnimationFrame(() => {
                postTypeConfigRef.current?.applyStudioSlotImagesFromPayload(ipPayload);
              });
            }
          }
        }

        mergeContextsIntoRows();

        if (!cancelled) restoredStudioReturnKeyRef.current = key;
      } catch (err) {
        safeConsole.warn("Failed to restore customization batch:", err);
        if (!cancelled) setStudioBatchLineItems([]);
      } finally {
        window.setTimeout(() => {
          studioRestoreLockRef.current = false;
        }, 900);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- restore when URL + auth + customer group are ready; avoid re-running on unrelated form churn
  }, [studioReturnBatchId, studioReturnContextId, token, customerGroupId, setFieldValue]);

  const formikValuesForStudioSnapshotRef = React.useRef(formik.values);
  formikValuesForStudioSnapshotRef.current = formik.values;

  const installationStudioCustomization = useMemo(
    (): StudioCustomizationFlowProps => ({
      source: "signage_add_installation",
      existingBatchId: studioReturnBatchId.trim() || undefined,
      batchLineItems: studioBatchLineItems.length ? studioBatchLineItems : undefined,
      getInstallationOrderSnapshot: async () => {
        const built = await buildInstallationOrderPayloadRef.current?.(formikValuesForStudioSnapshotRef.current);
        return (built ?? {}) as Record<string, unknown>;
      },
    }),
    [studioReturnBatchId, studioBatchLineItems]
  );

  const uploadFile = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("uploadDir", "installation_orders");

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${process.env.REACT_APP_API_URL}/images/upload`, true);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          setUploadProgress((event.loaded / event.total) * 100);
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200 || xhr.status === 201) {
          const response = JSON.parse(xhr.responseText);
          resolve(response.path);
        } else {
          reject(new Error("Failed to upload file"));
        }
      };

      xhr.onerror = () => reject(new Error("Network error during file upload"));
      xhr.send(formData);
    });
  };

  buildInstallationOrderPayloadRef.current = async (values: any) => {
        let brokerageFirmId = selectedBrokerageFirm?.value ?? null;
        let brokerageFirmName = selectedBrokerageFirm?.label ?? null;
        let officeInfo = null;
        let agentId = null;
        let sourceId = null;
        let agentName = null
        let customerId = null;
        let inventorySource = "agent"; // default fallback
        let finalBillingAddress = null;
        let finalInstallationAddress = null;
        if (selectedOfficeAgent) {
          agentId = selectedOfficeAgent?.value;
          sourceId = selectedOfficeAgent?.value
          agentName = selectedOfficeAgent?.label
          customerId = selectedOfficeAgent?.value;
          inventorySource = "agent";
          const selectedOfficeData = offices?.find(
            (office) => office._id === selectedOffice?.value
          );
          const primaryAddress = selectedOfficeData?.officeAddress?.[0];
          officeInfo = {
            _id: selectedOffice?.value,
            officeName: selectedOffice?.label,
            officeType: selectedOffice?.label,
            officeAddress: primaryAddress,
            status: true,
          };
          // officeInfo = null;
        } else if (selectedAgent) {
          agentId = selectedAgent?.value;
          sourceId = selectedAgent?.value;
          agentName = selectedAgent?.label
          customerId = selectedAgent?.value;
          inventorySource = "agent";
          officeInfo = null;
          if (selectedAgent.brokerageFirmId) {
            brokerageFirmId = selectedAgent.brokerageFirmId;
            brokerageFirmName = selectedAgent.brokerageFirmName || brokerageFirmName;
          }
        } else if (selectedOffice) {
          const selectedOfficeData = offices?.find(
            (office) => office._id === selectedOffice?.value
          );
          const primaryAddress = selectedOfficeData?.officeAddress?.[0];
          officeInfo = {
            _id: selectedOffice?.value,
            officeName: selectedOffice?.label,
            officeType: selectedOffice?.label,
            officeAddress: primaryAddress,
            status: true,
          };
          sourceId = selectedOffice?.value;
          inventorySource = "office";
          agentId = null;
          agentName = null;
          customerId = selectedBrokerageFirm?.value ?? null;
        } else if (selectedBrokerageFirm) {
          // Brokerage-only flow (no office/agent selected)
          sourceId = selectedBrokerageFirm?.value || null;
          inventorySource = "office";
        }

        const effectiveTeamMemberId =
          values.teamMemberId ||
          selectedTeamMemberOption?.value ||
          selectedTeamMember?.value ||
          teamMemberFromContinueRef.current?.value;
        const effectiveTeamMemberName =
          values.teamMemberName ||
          selectedTeamMemberOption?.label ||
          selectedTeamMember?.label ||
          teamMemberFromContinueRef.current?.label;

        if (selectedOfficeAgent?.value) {
          customerId = selectedOfficeAgent.value;
        } else if (selectedAgent?.value) {
          customerId = selectedAgent.value;
        } else if (effectiveTeamMemberId) {
          customerId = effectiveTeamMemberId;
        } else if (!customerId && selectedBrokerageFirm?.value) {
          customerId = selectedBrokerageFirm.value;
        }

        const billingFromList = findSalesOrderAddressById(
          addressObjects,
          values.billingAddressId
        );
        const billingRaw =
          selectedBillingAddress ||
          selectedOfficeBillingAddress ||
          billingFromList ||
          projectBillingAddressRef.current ||
          null;
        finalBillingAddress = sanitizeSalesOrderAddressForPayload(billingRaw);
        const installationRaw = values.isSameAsBilling
          ? billingRaw || installationAddress
          : installationAddress;
        finalInstallationAddress =
          installationRaw && typeof installationRaw === "object" && (installationRaw as any).address_details
            ? sanitizeSalesOrderAddressForPayload(installationRaw) || installationRaw
            : installationRaw;

        // console.log("Final Billing Address:", finalBillingAddress);
        // console.log("Final Installation Address:", finalInstallationAddress);

        let lineItems: any[] = [];

        // 📦 1. Regular products (agent or office)
        safeConsole.log("Selected Products for line items:", selectedProducts);

        // Fetch individual products only for linked (agent/office) products; skip new/catalog
        const fetchedProductObjs: ProductObj[] = [];
        for (const product of selectedProducts) {
          if (product.is_new_catalog) continue;
          if (product.inventory_type && product.user_id && product.product_id) {
            const productData = await fetchProductById(
              product.inventory_type as "agent" | "office",
              product.user_id,
              product.product_id
            );
            if (productData) {
              fetchedProductObjs.push(productData);
            }
          }
        }

        safeConsole.log("Fetched Product Objects:", fetchedProductObjs);

        const keyForProduct = (p: { product_id: string; variant_id?: string }) => `${p.product_id}:${p.variant_id ?? ""}`;
        const round2 = (n: number) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
        const resolvedLineItemsSource =
          lastResolvedInstallationRef.current?.line_items?.length
            ? lastResolvedInstallationRef.current.line_items
            : ((values.resolved_line_items || []) as ResolvedInstallationLineItem[]);
        const resolvedLineMap = new Map(
          resolvedLineItemsSource.map((li) => [orderLineKey(li.product_id, li.variant_id), li])
        );
        const resolvedOrderForScope = lastResolvedInstallationRef.current;
        const submitDiscountScope = resolvedOrderForScope
          ? inferInstallationDiscountScopeFromResolved(resolvedOrderForScope)
          : inferInstallationDiscountScope({
              discountAppliesTo: values.discount_applies_to,
              discountCode: values.discount_code,
              discountAmount: values.discount_amount,
              productDiscountAmount: values.product_discount_amount,
              lineItems: values.resolved_line_items,
            });
        const submitDiscountCode = String(
          values.discount_code || resolvedOrderForScope?.discount_code || ""
        ).trim();
        const { rootDiscountCode, lineDiscountCode } = splitInstallationDiscountForPayload(
          submitDiscountCode,
          submitDiscountScope
        );

        lineItems = selectedProducts?.map((product) => {
          const quantity = Math.max(0, Number(product.quantity) || 0);
          safeConsole.log("Processing product for line item, quantity:", quantity);
          if (product.is_new_catalog) {
            const key = keyForProduct(product);
            const resolved = resolvedLineMap.get(key);
            const prices = getPayloadUnitPrices(product);
            const payloadUnitRetail = prices.unit_retail_price;
            const changedUnitPrice = prices.changed_unit_price;
            const isManualLine = manualUnitPriceMap[key] !== undefined;
            const purchase_price = 0;
            const rowDiscTotal = isManualLine
              ? 0
              : round2(Number(resolved?.discount_on_total_quantity ?? 0));
            const perUnitDiscount = quantity > 0 ? round2(rowDiscTotal / quantity) : 0;
            const lineTax = round2(Number(resolved?.item_tax_info?.total_product_tax_amount ?? 0));
            const taxInfo = resolved?.item_tax_info ?? {
              tax_percentage: 0,
              unit_product_tax_amount: 0,
              total_product_tax_amount: 0,
              tax_details: null,
              taxable: !!product.is_taxable,
            };
            // inventory_source by end user: agent or office (set when product was added)
            const newProductInventorySource = product.inventory_type || "";
            const newProductLocation = (product.location || []).map((loc: any) => ({
              quantity: Number(loc.quantity) || 0,
              warehouse_id: String(loc.warehouse_id ?? ""),
              warehouse_name: loc.warehouse_name,
              zone_id: loc.zone_id ?? "",
              zone_name: loc.zone_name,
              rack_id: loc.rack_id ?? "",
              rack_name: loc.rack_name,
              bin_id: loc.bin_id ?? "",
              bin_name: loc.bin_name,
            }));
            return {
              product_id: product.product_id,
              variant_id: product.variant_id ?? null,
              slot_id: product.is_accessory ? "" : (product.slot_id ?? ""),
              is_new_purchase: true,
              manual_unit_price_override: isManualLine,
              is_price_overridden: isManualLine,
              source_product_id: product.product_id,
              inventory_source: newProductInventorySource,
              warehouse_id: null,
              serial_ids: [],
              product_name: product.name || "",
              sku: product.sku || "",
              quantity,
              unit_price: round2(prices.unit_price),
              total_price: round2(
                isManualLine
                  ? changedUnitPrice * quantity + lineTax
                  : Number(resolved?.total_price ?? changedUnitPrice * quantity + lineTax)
              ),
              discount: round2(rowDiscTotal),
              tax_amount: round2(lineTax),
              production_status: product.productionStatus || "not started",
              is_production: false,
              is_rotational: false,
              current_status: "with_supplier",
              product_location_status: product.product_location_status || "",
              notes: product.notes || "",
              rotational_allocated: 0,
              is_taxable: !!product.is_taxable,
              is_accessories: !!product.is_accessory,
              images:
                String(product.design_id ?? "").trim() && String(product.preview_url ?? "").trim()
                  ? [{ url: String(product.preview_url).trim() }]
                  : product.image_url
                    ? [{ url: product.image_url }]
                    : [],
              storage_locations: [{
                quantity,
                current_status: "with_supplier",
                location: newProductLocation,
              }],
              unit_purchase_price: purchase_price,
              unit_retail_price: round2(payloadUnitRetail),
              changed_unit_price: round2(changedUnitPrice),
              discount_on_unit_quantity: perUnitDiscount,
              discount_on_total_quantity: round2(rowDiscTotal),
              discount_code:
                submitDiscountScope === "product"
                  ? String(resolved?.discount_code ?? lineDiscountCode ?? "").trim()
                  : "",
              item_sub_total: round2(changedUnitPrice * quantity),
              item_tax_info: taxInfo,
              ...(String(product.design_id ?? "").trim()
                ? {
                    design_id: String(product.design_id).trim(),
                    design_name: String(product.design_name ?? "").trim(),
                    preview_url: String(product.preview_url ?? "").trim(),
                  }
                : {}),
              ...(product.customizations ? { customizations: product.customizations } : {}),
            };
          }

          const matched = fetchedProductObjs.find((p) => p.product_id === product.product_id);
          const variant: any = matched?.product?.variants?.[0] || {};
          const actualUnitPrice = Number(matched?.product?.retail_price || 0);
          const changedUnitPrice = actualUnitPrice;
          const is_taxable = matched?.product?.is_taxable ?? (product as any).is_taxable ?? false;
          const purchase_price = Number(matched?.product?.purchase_price || 0);
          const rowDiscTotal = 0;
          const perUnitDiscount = 0;
          const lineTax = 0;
          const taxInfo = {
            tax_percentage: 0,
            unit_product_tax_amount: 0,
            total_product_tax_amount: 0,
            tax_details: null,
            taxable: !!is_taxable,
          };

          const variantImages =
            String(product.design_id ?? "").trim() && String(product.preview_url ?? "").trim()
              ? [{ url: String(product.preview_url).trim(), default: true }]
              : (variant?.images && Array.isArray(variant.images))
                ? variant.images.map((img: any) => ({ url: img.url, alt_text: img.alt_text, default: img.default }))
                : (product.image_url ? [{ url: product.image_url, default: true }] : []);
          return {
            product_id: product.product_id,
            variant_id: variant?.variant_id,
            slot_id: product.is_accessory ? "" : (product.slot_id ?? ""),
            inventory_source: matched?.inventory_type || "",
            warehouse_id: null,
            serial_ids: [],
            product_name: variant?.variant_name || "",
            sku: variant?.sku || "",
            quantity,
            unit_price: round2(actualUnitPrice),
            total_price: round2(changedUnitPrice * quantity),
            discount: round2(rowDiscTotal),
            tax_amount: round2(lineTax),
            production_status: product.productionStatus || "not started",
            is_production: matched?.product?.is_production || false,
            is_rotational: matched?.product?.is_rotational || false,
            current_status: matched?.product?.current_status || "",
            product_location_status: matched?.product?.product_location_status || "",
            notes: product.notes || "",
            rotational_allocated: 0,
            is_taxable: !!is_taxable,
            is_accessories: !!product.is_accessory,
            images: variantImages,
            storage_locations: [{
              quantity,
              current_status: matched?.linked_installer_details?.storage_locations?.[0]?.current_status || "",
              location: [],
            }],
            unit_purchase_price: purchase_price,
            unit_retail_price: round2(actualUnitPrice),
            changed_unit_price: round2(changedUnitPrice),
            discount_on_unit_quantity: perUnitDiscount,
            discount_on_total_quantity: round2(rowDiscTotal),
            item_sub_total: round2(changedUnitPrice * quantity + lineTax),
            item_tax_info: taxInfo,
            ...(String(product.design_id ?? "").trim()
              ? {
                  design_id: String(product.design_id).trim(),
                  design_name: String(product.design_name ?? "").trim(),
                  preview_url: String(product.preview_url ?? "").trim(),
                }
              : {}),
          };
        });

        // 📦 1b. Custom products (no discount; tax only when is_taxable)
        (customproducts || []).forEach((cp) => {
          const qty = Math.max(1, Number(cp.quantity) || 1);
          const unitPrice = round2(Number(cp.Price) || 0);
          const customKey = orderLineKey(String(cp.product_id), cp.variant_id);
          const resolved = resolvedLineMap.get(customKey);
          const lineTax = round2(Number(resolved?.item_tax_info?.total_product_tax_amount ?? 0));
          const taxInfo = resolved?.item_tax_info ?? {
            tax_percentage: 0,
            unit_product_tax_amount: 0,
            total_product_tax_amount: 0,
            tax_details: null,
            taxable: !!cp.is_taxable,
          };
          lineItems.push({
            product_id: cp.product_id,
            variant_id: cp.variant_id,
            inventory_source: "custom",
            source_product_id: cp.product_id,
            is_new_purchase:true,
            warehouse_id: null,
            serial_ids: [],
            product_name: cp.item_name || "Custom Item",
            sku: `custom_sku_${cp.product_id}`,
            quantity: qty,
            unit_price: unitPrice,
            total_price: round2(Number(resolved?.total_price ?? unitPrice * qty)),
            discount: 0,
            tax_amount: round2(lineTax),
            production_status: "not started",
            is_production: false,
            is_rotational: false,
            current_status: "with_supplier",
            product_location_status: "",
            notes: "",
            rotational_allocated: 0,
            is_taxable: !!cp.is_taxable,
            is_accessories: false,
            images: [],
            storage_locations: [{ quantity: qty, current_status: "with_supplier", location: [] }],
            unit_purchase_price: unitPrice,
            unit_retail_price: unitPrice,
            changed_unit_price: unitPrice,
            is_price_overridden: true,
            discount_on_unit_quantity: 0,
            discount_on_total_quantity: 0,
            item_sub_total: round2(unitPrice * qty + lineTax),
            item_tax_info: taxInfo,
            custom_information: cp.custom_information || "",
          } as any);
        });

        // 🔄 2. Rotational allocations
        Object.entries(allocatedUnits)?.forEach(([linkedProductId, units]) => {
          if (units.length === 0) return;
          lineItems.push({
            product_id: units[0].rotationalProductId,
            inventory_source: "rotational",
            warehouse_id: units[0].warehouseId || "",
            serial_ids: units?.map((u) => u.serial_id),
            product_name: "",
            sku: "",
            quantity: 1,
            unit_price: 0,
            total_price: 0,
            discount: 0,
            tax_amount: 0,
            production_status: null,
            is_rotational: true,
            current_status: "",
            notes: "",
            is_taxable: false,
            is_accessories: false,
            images: [],
          });
        });

        const lineItemsForSubmit =
          resolvedLineItemsSource.length > 0
            ? (mergeResolvedTaxIntoLineItems(lineItems, resolvedLineItemsSource) as typeof lineItems)
            : lineItems;
        const lineItemsMerged =
          submitDiscountScope === "installation"
            ? lineItemsForSubmit.map((li: any) =>
                li?.is_new_purchase === true ? { ...li, discount_code: "" } : li
              )
            : lineItemsForSubmit;

        const feeSub = Number(values.totals.specialfee_subtotal || 0);
        const installTax = Number(values.totals.installation_tax_amount || 0);
        const feeTax = Number(values.totals.specialfee_tax_amount || 0);
        const baseInstallationCost = Math.max(0, parseFloat(String(installationCost)) || 0);
        const resolvedOrder = lastResolvedInstallationRef.current;
        const hasManualUnitOverridesSubmit = Object.keys(manualUnitPriceMap).length > 0;
        const submitUnitsCost = hasManualUnitOverridesSubmit
          ? computeInstallationUnitsCost({
              catalogProducts: selectedProducts || [],
              customProducts: customproducts || [],
              manualUnitPriceMap,
              getResolvedLineItem: (p) => resolvedLineMap.get(orderLineKey(p.product_id, p.variant_id)),
            })
          : Number(values.totals.units_cost || 0);
        const submitAdditionalCost = Number(values.additional_cost ?? resolvedOrder?.additional_cost ?? 0);
        const subtotalAmount = round2(
          hasManualUnitOverridesSubmit
            ? baseInstallationCost + submitAdditionalCost + submitUnitsCost
            : Number(
                resolvedOrder?.subtotal_amount ??
                  baseInstallationCost + Number(values.totals.units_cost || 0)
              )
        );
        const overAllTaxAmount = round2(
          Number(resolvedOrder?.over_all_tax_amount ?? Number(values.tax || installTax + feeTax))
        );
        const submitProductTax = Number(
          (values.totals as { product_tax_amount?: number })?.product_tax_amount || 0
        );
        const totalAmount = round2(
          hasManualUnitOverridesSubmit
            ? computeInstallationGrandTotal({
                subtotalAmount,
                specialFeeSubtotal: feeSub,
                tax: Number(values.tax || installTax + feeTax + submitProductTax),
                discountAmount: Number(values.discount_amount || 0),
              })
            : Number(resolvedOrder?.total_amount ?? values.totals.grand_total ?? 0)
        );
        // ❌ was:
        // const specialfeeid = Number(values.totals.specialfee_ids || 0);
        // const specialfeename = values.totals.specialfee_names || "";

        // ✅ use arrays (or join if your API wants a string)

        const uiFees = (values?.specialfee || []) as Array<any>;
        const feeRows = uiFees.map((f) =>
          mapInstallationSpecialFeeForSubmit(f, { catalog: allAvailableSpecialFees })
        );



        const installationTaxPctStr = values.calc_install_rate_str || "";   // string
        const overallTaxPctStr = values.calc_overall_rate_str || "";       // string
        const specialFeeTaxPct = (() => {
          const s = values.calc_fee_rate_str;
          return s ? Number(s) : undefined;
        })();

        const installationOrderPayload = {
            user_type: values.userType,
            user_email: values.userEmail,
            user_name: values.userFirstName + " " + values.userLastName,
            user_phone: withDefaultSignageContactPhone(values.userPhone),
            order_number: null, // or generate dynamically
            status: 100,
            inventory_source: inventorySource,
            source_id: sourceId,
            brokerage_firm_id: brokerageFirmId,
            brokerage_firm_name: brokerageFirmName,
            brokerage_firm_admin: selectedBrokerageFirm ? (selectedBrokerageFirm?.firstName || "") + " " + (selectedBrokerageFirm?.lastName || "") : "",
            team_member_id: effectiveTeamMemberId || null,
            team_member_name: effectiveTeamMemberName || "",
            customer_id: customerId,
            office: officeInfo,
            agent_id: agentId,
            agent_name: agentName,
            customer_first_name: values.userFirstName,
            customer_last_name: values.userLastName,
            customer_email: values.userEmail,
            customer_phone: withDefaultSignageContactPhone(values.userPhone),
            signage_id: (isSignageTeammember && parentSignageId ? parentSignageId : userId) || "",
            signage_company_name: isSignageTeammember && parentSignageName ? parentSignageName : userName || "",
            line_items: lineItemsMerged,
            installation_post: installationPost ?? undefined,
            customer_group_id: null,
            payment_method: values.payment_method,
            payment_status: "Pending",
            order_type: "installation",
            po_number: String(values.po_number || "").trim(),
            project_id:
              userClearedProjectRef.current
                ? undefined
                : linkedProjectId || projectIdFromQuery || undefined,
            project_number:
              userClearedProjectRef.current
                ? undefined
                : linkedProjectNumber || undefined,
            payment_info: [], // or provide details
            installation_details: {
              ...(values.scheduled_date
                ? { scheduled_date: new Date(values.scheduled_date).toISOString() }
                : {}),
              installation_address: finalInstallationAddress,
              installer_id: selectedInstaller?.value,
              installer_name: selectedInstaller?.label,
              contact_number: selectedInstaller?.phone,
              estimated_duration: null,
              actual_installation_date: null,
              additional_instructions: [
                {
                  value: values.instructions,
                  added_by: {
                    id: userId,
                    name: userName,
                  },
                  added_at: new Date().toISOString(),
                },
              ],
              assignee_id: selectedInstaller?.value,
              tax_details: null,
            },
            billing_address: finalBillingAddress,
            both_address_same: values.isSameAsBilling,
            additional_instructions: [
              {
                value: values.instructions,
                added_by: {
                  id: userId,
                  name: userName,
                },
                added_at: new Date().toISOString(),
              },
            ],
            local_laws_compliance: values.localLawsChecked,
            priority: values.priority,
            uploaded_files: uploadedFileMeta || [],
            additional_cost: Number(resolvedOrder?.additional_cost ?? values.additional_cost ?? 0),
            tax_amount: totalAmount,
            discount_amount:
              submitDiscountScope === "installation"
                ? Number(values.discount_amount ?? resolvedOrderForScope?.discount_amount ?? 0)
                : 0,
            product_discount_amount:
              submitDiscountScope === "product"
                ? Number(values.product_discount_amount ?? values.discount_amount ?? 0)
                : 0,
            discount_code: submitDiscountScope === "installation" ? rootDiscountCode : "",
            discount_applies_to: submitDiscountScope,
            overpaid_amount: 0,
            version: 1,
            tax_details: null,
            gate_access_code: values.gatecode,
            subtotal_amount: subtotalAmount,
            tax_inclusive:dynamicTaxable,
            installation_cost: Number(resolvedOrder?.installation_cost ?? baseInstallationCost),
            specialfee: feeRows,
            installation_tax_amount: installTax,
            installation_tax_percentage: installationTaxPctStr || "",
            over_all_tax_amount: overAllTaxAmount,
            over_all_tax_percentage: overallTaxPctStr || "",
            total_amount: totalAmount,
            tax_status: values.tax_status || resolvedOrder?.tax_status || "",
            cart_id: String(
              resolvedOrder?.cart_id ||
                formik.values.draft_unique_id ||
                getInstallationDraftId() ||
                ""
            ),
        };

    return overlayInstallationSubmitFromResolved(
      installationOrderPayload,
      lastResolvedInstallationRef.current
    );
  };



  const keyForRow = (r: { productId: string; variantId: string }) => `${r.productId}:${r.variantId}`;
  const keyForInstallationProduct = (p: { product_id: string; variant_id?: string }) => `${p.product_id}:${p.variant_id ?? ""}`;
  const normalizeId1 = (row: any) => String(row?.id ?? row?._id ?? "");

  const getResolvedLineItem = useCallback(
    (p: { product_id: string; variant_id?: string }) => {
      const key = keyForInstallationProduct(p);
      const rows = (formik.values.resolved_line_items || []) as ResolvedInstallationLineItem[];
      return rows.find((li) => orderLineKey(li.product_id, li.variant_id) === key);
    },
    [formik.values.resolved_line_items]
  );

  const getPayloadUnitPrices = useCallback(
    (p: { product_id: string; variant_id?: string; unit_price?: number }) => {
      const key = keyForInstallationProduct(p);
      const original = getOriginalCatalogUnitPrice(p);
      const resolved = getResolvedLineItem(p);
      const manual = manualUnitPriceMap[key];
      if (manual !== undefined) {
        const v = getResolveRequestUnitPrice(key, original, manualUnitPriceMap);
        return { unit_price: v, unit_retail_price: v, changed_unit_price: v };
      }
      const unitRetail = getResolveRequestUnitPrice(key, original, manualUnitPriceMap);
      const changed = Number(resolved?.changed_unit_price ?? unitRetail);
      return { unit_price: unitRetail, unit_retail_price: unitRetail, changed_unit_price: changed };
    },
    [manualUnitPriceMap, getResolvedLineItem]
  );
  const PAGE_SIZE = 10;

  useEffect(() => {
    if (formik.submitCount === 0 || formik.isSubmitting || Object.keys(formik.errors).length === 0) {
      return;
    }

    const rafId = window.requestAnimationFrame(() => {
      const form = formRef.current;
      if (!form) return;

      const firstErrorElement = form.querySelector(
        ".is-invalid, .invalid-feedback.d-block, .text-danger"
      ) as HTMLElement | null;

      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [formik.submitCount, formik.isSubmitting, formik.errors]);

  const loadBrokerageOptions: LoadOptions<BrokerageOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;
      const q = (inputValue || "").trim();

      try {
        const url =
          `${process.env.REACT_APP_API_URL}/users-search` +
          `?licenseType=BROKERAGE_FIRM` +
          `&signageFirmID=${encodeURIComponent((isSignageTeammember && parentSignageId ? parentSignageId : userId) || "")}` +
          `&page=${page}&limit=${PAGE_SIZE}` +
          (q ? `&search=${encodeURIComponent(q)}` : "");


        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          safeConsole.error("Failed to fetch brokerage firms:", response.statusText);
          return { options: [], hasMore: false, additional: { page } };
        }

        const json = await response.json();

        const list = Array.isArray(json) ? json : json.data ?? json.results ?? [];

        const options: BrokerageOption[] = list.map((u: any) => ({
          value: normalizeId(u),
          label: u?.firmDetails?.companyName || u?.text || "(no name)",
          firstName: u?.firstName || "",
          lastName: u?.lastName || "",
          email: u?.email || "",
          phone: withDefaultSignageContactPhone(primaryPhoneFromUserLike(u)),
        }));

        const hasMore = Array.isArray(json)
          ? options.length === PAGE_SIZE
          : json?.pagination?.more ??
          (json?.total ? page * (json?.limit ?? PAGE_SIZE) < json.total : options.length === PAGE_SIZE);

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (err) {
        safeConsole.error("Failed to load brokerage options:", err);
        // Send error email notification
        await handleApiError(err, {
          module: "installation",
          action: "Signage_Add_Installation_load_brokerage_options",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/users-search`,
          method: "GET",
          additionalInfo: {
            page,
            search: q || "",
            isSignageTeammember,
            parentSignageId: parentSignageId || null
          }
        }, token);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [userId, isSignageTeammember, parentSignageId, token, licenseType, userName]
  );


  // ===== Team Member loader using Select2 endpoint (based on office) =====
  const loadTeamMemberOptions: LoadOptions<TeamMemberOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;

      // HARD GUARD: without office, do nothing
      const officeId = selectedOffice?.value;
      if (!officeId) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      const q = (inputValue || "").trim();
      const teamMemberPayload = {
        page,
        limit: PAGE_SIZE,
        search: q,
      };
      try {
        const url = `${process.env.REACT_APP_API_URL}/office/${officeId}/team-members/select`;

        const response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(teamMemberPayload),
        });

        if (!response.ok) {
          safeConsole.error("Failed to fetch team members:", response.statusText);
          return { options: [], hasMore: false, additional: { page } };
        }

        const json = await response.json();

        const list = json?.results || [];

        const options: TeamMemberOption[] = list.map((u: any) => ({
          value: String(u?.id ?? u?._id ?? ""),
          label: `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "---",
          firstName: u?.firstName || "",
          lastName: u?.lastName || "",
          email: u?.email || "",
          phone: withDefaultSignageContactPhone(primaryPhoneFromUserLike(u)),
          offices: [], // Not provided in new endpoint
          // Office team-members API returns camelCase licenseType per row (e.g. BROKERAGE_TEAM_MEMBER, BROKERAGE_FIRM)
          license_type: String(
            u?.licenseType ?? u?.license_type ?? "BROKERAGE_TEAM_MEMBER"
          ).trim(),
        }));

        const hasMore = json?.pagination?.more ?? false;

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (err) {
        safeConsole.error("Failed to load team member options:", err);
        // Send error email notification
        await handleApiError(err, {
          module: "installation",
          action: "Signage_Add_Installation_load_team_member_options",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/users/team-member/select2`,
          method: "POST",
          requestBody: teamMemberPayload,
          additionalInfo: {
            page,
            search: q || "",
            officeId: selectedOffice?.value || null
          }
        }, token);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [selectedOffice?.value, token, userId, licenseType, userName]
  );

  // ===== Office Agent loader using Select2 endpoint (for brokerage path - based on office) =====
  const loadOfficeAgentOptions: LoadOptions<OfficeAgentOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;

      // HARD GUARD: without office, do nothing
      const officeId = selectedOffice?.value;
      if (!officeId) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      const q = (inputValue || "").trim();
      const officeAgentPayload = {
        page,
        limit: PAGE_SIZE,
        search: q,
      };
      try {
        const url = `${process.env.REACT_APP_API_URL}/agent/select/${officeId}`;

        const response = await fetch(url, {
          method: "POST",
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(officeAgentPayload),
        });

        if (!response.ok) {
          safeConsole.error("Failed to fetch office agents:", response.statusText);
          return { options: [], hasMore: false, additional: { page } };
        }

        const json = await response.json();

        const list = json?.results || [];

        const options: OfficeAgentOption[] = list.map((u: any) => ({
          value: String(u?.id ?? u?._id ?? ""),
          label: `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "---",
          firstName: u?.firstName || "",
          lastName: u?.lastName || "",
          email: u?.email || "",
          phone: withDefaultSignageContactPhone(primaryPhoneFromUserLike(u)),
          license_type: "BROKERAGE_AGENT",
        }));

        const hasMore = json?.pagination?.more ?? false;

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (err) {
        safeConsole.error("Failed to load office agent options:", err);
        // Send error email notification
        await handleApiError(err, {
          module: "installation",
          action: "Signage_Add_Installation_load_office_agent_options",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/agent/select/${selectedOffice?.value}`,
          method: "POST",
          requestBody: officeAgentPayload,
          additionalInfo: {
            page,
            search: q || "",
            officeId: selectedOffice?.value || null
          }
        }, token);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [selectedOffice?.value, token, userId, licenseType, userName]
  );

  // ===== Signage Team Member Loader (for Assigned To dropdown) =====
  const loadSignageTeamMemberOptions: LoadOptions<SignageTeamMemberOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;
      const q = (inputValue || "").trim();
      const signageId = (isSignageTeammember && parentSignageId) ? parentSignageId : userId;

      try {
        const url =
          `${process.env.REACT_APP_API_URL}/users-search` +
          `?licenseType=SIGNAGE_TEAM_MEMBER` +
          `&signageFirmID=${encodeURIComponent((isSignageTeammember && parentSignageId ? parentSignageId : userId) || "")}` +
          `&page=${page}&limit=${PAGE_SIZE}` +
          (q ? `&search=${encodeURIComponent(q)}` : "");


        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          safeConsole.error("Failed to fetch signage team members:", response.statusText);
          return { options: [], hasMore: false, additional: { page } };
        }

        const json = await response.json();

        const list = Array.isArray(json) ? json : json.data ?? json.results ?? [];

        const options: SignageTeamMemberOption[] = list.map((u: any) => ({
          value: String(u?.id ?? u?._id ?? ""),
          label: `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || u?.text || "(no name)",
          firstName: u?.firstName || "",
          lastName: u?.lastName || "",
          email: u?.email || "",
          phone: withDefaultSignageContactPhone(primaryPhoneFromUserLike(u)),
        }));

        const hasMore = Array.isArray(json)
          ? options.length === PAGE_SIZE
          : json?.pagination?.more ??
          (json?.total ? page * (json?.limit ?? PAGE_SIZE) < json.total : options.length === PAGE_SIZE);

        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch (err) {
        safeConsole.error("Failed to load signage team member options:", err);
        // Send error email notification
        await handleApiError(err, {
          module: "installation",
          action: "Signage_Add_Installation_load_signage_team_member_options",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/users/signage/${signageId}/team-members/select2`,
          method: "GET",
          additionalInfo: {
            page,
            search: q || "",
            signageId: signageId || null,
            isSignageTeammember,
            parentSignageId: parentSignageId || null
          }
        }, token);
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [userId, isSignageTeammember, parentSignageId, token, licenseType, userName]
  );

  // const handleLocationChange = (
  //   selectedOption: SingleValue<{ value: string; label: string }>
  // ) => {
  //   formik.setFieldValue("location", selectedOption?.value || "");
  // };

  const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      setSelectedLocation({ lat, lng });
      formik.setFieldValue("location", { lat, lng });
    }
  }, []);

  const handleInstallationMapLoad = useCallback((map: google.maps.Map) => {
    installationMapRef.current = map;
    const sync = () => {
      google.maps.event.trigger(map, "resize");
      const loc = selectedLocationRef.current;
      map.setCenter(loc ?? center);
    };
    sync();
    requestAnimationFrame(sync);
    window.setTimeout(sync, 100);
    window.setTimeout(sync, 400);
  }, []);

  // to get the scheduled job count based on the selected date
  useEffect(() => {
    const fetchScheduledJobs = async () => {
      if (!formik.values.scheduled_date || !userId) return;

      const date = new Date(formik.values.scheduled_date).toISOString().split("T")[0]; // format as YYYY-MM-DD

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/installation_orders/${formik.values.installeId}/${date}/scheduled_jobs`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Failed to fetch job count");
        // console.log("Scheduled Jobs:", data);

        setScheduledJobsCount(data.scheduled_jobs); // assuming response shape: { count: number }
        formik.setFieldValue("scheduled_jobs", data.scheduled_jobs);
      } catch (err: any) {
        safeConsole.error("Scheduled Jobs Error:", err.message);
        setScheduledJobsCount(null);
        // Send error email notification
        await handleApiError(err, {
          module: "installation",
          action: "Signage_Add_Installation_fetch_scheduled_jobs",
          userId: userId || undefined,
          licenseType: licenseType || undefined,
          userName: userName || undefined,
          url: `${process.env.REACT_APP_API_URL}/installation_orders/${formik.values.installeId}/${date}/scheduled_jobs`,
          method: "GET",
          additionalInfo: {
            installeId: formik.values.installeId || null,
            scheduledDate: date || null
          }
        }, token);
      }
    };

    fetchScheduledJobs();
  }, [formik.values.installeId, userId, token]);
  console.log("Scheduled Jobs Count:", selectedProducts);

  // seting the total units based on the selected products + custom products
  useEffect(() => {
    const catalogTotal = selectedProducts.reduce((sum, product) => sum + (product.quantity || 0), 0);
    const customTotal = customproducts.reduce((sum, cp) => sum + (cp.quantity || 0), 0);
    setTotalUnits(catalogTotal + customTotal);
  }, [selectedProducts, customproducts]);

  useEffect(() => {
    const id = String(formik.values.billingAddressId || "").trim();
    if (!id) return;
    const prefilled = projectBillingAddressRef.current;
    const found =
      findSalesOrderAddressById(addressObjects, id) ||
      (prefilled && resolveSalesOrderAddressId(prefilled) === id ? prefilled : null);
    if (!found) return;
    setAddressObjects((prev) => upsertAddressInList(prev, found));
    setSelectedBillingAddress(found);
    setSelectedOfficeBillingAddress(found);
  }, [formik.values.billingAddressId, addressObjects]);

  // seting the installation address based on the selected billing address
  useEffect(() => {
    if (formik.values.isSameAsBilling) {
      const billing =
        selectedOfficeBillingAddress ||
        selectedBillingAddress ||
        findSalesOrderAddressById(addressObjects, formik.values.billingAddressId) ||
        projectBillingAddressRef.current;

      if (billing) {
        setInstallationAddress(billing);
        formik.setFieldValue("installationAddressId", `${billing?.label}: ${billing?.address_details?.addressLine1}, ${billing?.address_details?.city}, ${billing?.address_details?.state}, ${billing?.address_details?.zip ? billing?.address_details?.zip : ''}`);
      }
    }
  }, [formik.values.isSameAsBilling, selectedBillingAddress, selectedOfficeBillingAddress, formik.values.billingAddressId, addressObjects]);

  // calculating the subtotal and total
  useEffect(() => {
    const subtotal = Number(installationCost) + Number(formik.values.tax);
    const total = subtotal - Number(formik.values.discount || 0);

    formik.setFieldValue("subtotal", subtotal);
    formik.setFieldValue("total", total);
  }, [installationCost, formik.values.tax, formik.values.discount]);

  // remove the preview images
  useEffect(() => {
    return () => {
      imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [imagePreviews]);

  useEffect(() => {
    if (
      installationAddress?.address_details?.latitude &&
      installationAddress?.address_details?.longitude &&
      installationAddress?.address_details?.latitude !== 0 &&
      installationAddress?.address_details?.longitude !== 0
    ) {
      setSelectedLocation({
        lat: installationAddress?.address_details?.latitude,
        lng: installationAddress?.address_details?.longitude,
      });
    }
  }, [installationAddress]);

  useEffect(() => {
    const map = installationMapRef.current;
    if (!map || !selectedLocation) return;
    map.panTo(selectedLocation);
  }, [selectedLocation]);

  const handleMarkerDragEnd = async (event: google.maps.MapMouseEvent) => {
    const lat = event.latLng?.lat();
    const lng = event.latLng?.lng();

    if (!lat || !lng) return;

    setSelectedLocation({ lat, lng });

    try {
      const geocoder = new window.google.maps.Geocoder();
      const response = await geocoder.geocode({ location: { lat, lng } });

      if (response.results[0]) {
        const place = response.results[0];
        const addressComponents = place.address_components;

        const getComponent = (type: string) => {
          const component = addressComponents.find((c: any) =>
            c.types.includes(type)
          );
          return component
            ? { long_name: component.long_name, short_name: component.short_name }
            : { long_name: "", short_name: "" };
        };

        const streetNumber = getComponent("street_number").long_name;
        const route = getComponent("route").long_name;
        const cityObj =
          getComponent("locality") ||
          getComponent("administrative_area_level_2");
        const stateObj = getComponent("administrative_area_level_1");
        const postalCode = getComponent("postal_code").long_name;
        const countryObj = getComponent("country");

        const street = `${streetNumber} ${route}`.trim();

        const addressDetails: Address = {
          _id: "",
          label: place.formatted_address,
          addressLine1: street,
          addressLine2: "",
          country: countryObj.long_name,
          county: cityObj.long_name,
          city: cityObj.long_name,
          state: stateObj.long_name,
          zip: postalCode,
          cityCode: cityObj.short_name,
          stateCode: stateObj.short_name,
          countryCode: countryObj.short_name,
          latitude: lat,
          longitude: lng,
          phone: "",
        };

        // Build the full Addresses object (matches your imported type)
        const newAddresses: Addresses = {
          _id: "",
          user_id: "",
          user_type: "",
          label: place.formatted_address,
          first_name: "",
          last_name: "",
          company: "",
          default_address: false,
          address_details: addressDetails,
          created_by: {
            id: userId || "",
            name: userName || "",

          },
          updated_by: {
            id: userId || "",
            name: userName || "",
          },
        };

        setInstallationAddress(newAddresses);

        formik.setFieldValue("installationAddressId", place.formatted_address);

        // ✅ Automatically uncheck "Billing and Installation Address are Same" when user changes installation address via map
        formik.setFieldValue("isSameAsBilling", false);
        triggerRecomputeIfReadyRef.current();
      }
    } catch (error) {
      safeConsole.error("Geocoder failed", error);
      showError("Unable to update address from marker position.");
      // Send error email notification
      await handleApiError(error, {
        module: "installation",
        action: "Signage_Add_Installation_geocode_address",
        userId: userId || undefined,
        licenseType: licenseType || undefined,
        userName: userName || undefined,
        url: "Google Maps Geocoder API",
        method: "GET",
        additionalInfo: {
          latitude: lat || null,
          longitude: lng || null
        }
      }, token);
    }
  };

  const fetchRotationalPoolProducts = async () => {
    const signageId = (isSignageTeammember && parentSignageId) ? parentSignageId : userId;
    const signageName = (isSignageTeammember && parentSignageName) ? parentSignageName : userName;
    if (!signageId || !token) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/signage/${signageId}/rotational-products`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to fetch");
      // console.log("Rotational Pool Products:", data);

      setRotationalPoolProducts(data); // ✅ store in state
    } catch (err: any) {
      safeConsole.error("Error fetching rotational products:", err.message);
      showError("Failed to fetch rotational pool products");
      // Send error email notification
      await handleApiError(err, {
        module: "installation",
        action: "Signage_Add_Installation_fetch_rotational_pool_products",
        userId: signageId || undefined,
        licenseType: licenseType || undefined,
        userName: signageName || undefined,
        url: `${process.env.REACT_APP_API_URL}/signage/${signageId}/rotational-products`,
        method: "GET"
      }, token);
    }
  };

  const rotationalproductOptions = rotationalPoolProducts?.map((p) => ({
    value: p._id,
    label: p.rotational_product?.name || "Unnamed Product",
    fullProduct: p,
  }));


  const handleProductSelect = (selected: any) => {
    setSelectedProduct(selected);

    const warehouses = selected?.fullProduct?.warehouse_details || [];

    const options = warehouses?.map((w: any) => ({
      value: w.warehouse._id,
      label: `${w.warehouse.label} (${w.available} available)`,
    }));

    setWarehouseOptions(options);
  };

  const handleSaveInstructions = (newInstructions: string) => {
    setInstructions(newInstructions);
    formik.setFieldValue("instructions", newInstructions);
  };

  const fetchSerializedUnits = async (rotationalId: string, warehouseId: string) => {
    const signageId = (isSignageTeammember && parentSignageId) ? parentSignageId : userId;
    const signageName = (isSignageTeammember && parentSignageName) ? parentSignageName : userName;
    if (!signageId || !token) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/signage/${signageId}/warehouses/rotational-pool/${rotationalId}/warehouse/${warehouseId}/available-serialized-units`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to fetch serialized pool");
      // console.log("Serialized Units:", data);
      setAvailableSerializedUnits(data);
    } catch (err: any) {
      safeConsole.error("Fetch Serialized Units Error:", err.message);
      showError("Unable to fetch serialized units");
      // Send error email notification
      await handleApiError(err, {
        module: "installation",
        action: "Signage_Add_Installation_fetch_serialized_units",
        userId: signageId || undefined,
        licenseType: licenseType || undefined,
        userName: signageName || undefined,
        url: `${process.env.REACT_APP_API_URL}/signage/${signageId}/warehouses/rotational-pool/${rotationalId}/warehouse/${warehouseId}/available-serialized-units`,
        method: "GET",
        additionalInfo: {
          rotationalId: rotationalId || null,
          warehouseId: warehouseId || null
        }
      }, token);
    }
  };

  async function ensureDraftUniqueId(token?: string): Promise<string | null> {
    try {
      const existing = getInstallationDraftId();
      if (existing) return existing;

      if (!token) return null;

      const res = await fetch(`${process.env.REACT_APP_API_URL}/utils/new-unique-id`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (!res.ok) return null;

      const data = await res.json().catch(() => ({}));
      const id = data?.id || data?.data?.id || data?.data?.unique_id || data?.unique_id || null;
      if (!id) return null;
      setInstallationDraftId(String(id));
      return String(id);
    } catch {
      return null;
    }
  }

  // ✅ Call ensureDraftUniqueId when buyer type is set (manually or by default)
  React.useEffect(() => {
    const currentUserType = formik.values.userType;
    if (!currentUserType || currentUserType === "") return;

    // ✅ Call ensureDraftUniqueId when buyer type is set (either manually or by default)
    (async () => {
      const id = await ensureDraftUniqueId(token || "");
      if (id) {
        formik.setFieldValue("draft_unique_id", id, false);
      }
    })();
  }, [formik.values.userType, token]);

  // Fetch first customer group for SearchBrokerageAgentTeam API
  useEffect(() => {
    if (!token) return;
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/customer-groups?permissions=false`, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || "Failed to fetch customer groups");
        const list = Array.isArray(data) ? data : data?.data ?? [];
        const firstId = list[0]?._id || list[0]?.id || null;
        if (mounted && firstId) setCustomerGroupId(firstId);
      } catch {
        if (mounted) setCustomerGroupId(null);
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  // ✅ Use useCallback to stabilize the function reference
  const coalesceDestination = React.useCallback((): Addresstax => {
    const a: Addresstax | undefined = installationAddress?.address_details;
    // const f: Address | undefined = formik.values?.destination;

    return {
      addressLine1: a?.addressLine1 ?? "",
      city: a?.city ?? "",
      state: a?.stateCode ?? a?.state ?? "",
      zip: a?.zip ?? "",
      // country: a?.country ??  "US",
      stateCode: a?.stateCode ?? a?.state ?? "",
      // countryCode: a?.countryCode ?? "US",
    };
  }, [
    installationAddress?.address_details?.addressLine1,
    installationAddress?.address_details?.city,
    installationAddress?.address_details?.state,
    installationAddress?.address_details?.stateCode,
    installationAddress?.address_details?.zip,
  ]);


  // Ensure we always send a clean special fee array
  // ✅ Use the new Formik path; be backward-compatible to avoid crashes
  // ✅ Simple: Just use the state directly (no Formik reads!)
  // ✅ Use useCallback to stabilize the function reference
  const getSelectedFees = React.useCallback(() => {
    safeConsole.log("getSelectedFees from state:", selectedSpecialFees);
    return selectedSpecialFees
      .map((f) => normalizeInstallationSelectedFee(f, allAvailableSpecialFees))
      .filter((f) => f.id !== "");
  }, [selectedSpecialFees, allAvailableSpecialFees]);



  // subtotal before discount = installation_subtotal + specialfee_subtotal (from engine)
  const getCurrentSubTotalAll = React.useCallback(() => {
    return Number(installationCost || 0);
  }, [installationCost]);

  // Start manual installation cost editing from the currently displayed cost.
  const handleEditInstallationCost = () => {
    setManualInstallationCost(String(Number(installationCost || 0)));
    setIsEditingInstallationCost(true);
  };

  // Leave edit mode without changing the current installation cost.
  const handleCancelEditInstallationCost = () => {
    setManualInstallationCost("");
    setIsEditingInstallationCost(false);
  };

  // Save the manual cost so cost breakdown, tax recompute, and submit payload use the same value.
  const handleSaveInstallationCost = () => {
    const nextCost = Number(manualInstallationCost);
    if (!Number.isFinite(nextCost) || nextCost < 0) {
      showError("Please enter a valid installation cost");
      return;
    }

    setInstallationCost(nextCost);
    setManualInstallationCost("");
    setIsEditingInstallationCost(false);
    showSuccess("Installation cost updated.");
    triggerRecomputeIfReady();
  };

  const recompute = React.useCallback(async (options?: { force?: boolean; discountCode?: string }): Promise<boolean> => {
    if (!dynamicCostId) return false;

    if (isRecomputingRef.current) {
      safeConsole.log("⏸️ Recompute already in progress, skipping...");
      return false;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    const dest = coalesceDestination();
    const fees = getSelectedFees();
    const effectiveDiscountCode =
      options?.discountCode !== undefined
        ? String(options.discountCode || "").trim()
        : String(formik.values.discount_code || "").trim();

    const currentOwnerValue =
      selectedOfficeAgent
        ? { id: selectedOfficeAgent.value, name: selectedOfficeAgent.label, type: "agent" as const }
        : selectedAgent
          ? { id: selectedAgent.value, name: selectedAgent.label, type: "agent" as const }
          : selectedOffice
            ? { id: selectedOffice.value, name: selectedOffice.label, type: "office" as const }
            : null;

    const catalogLineSig = (selectedProducts || [])
      .filter((p) => p.is_new_catalog && p.product_id)
      .map((p) => {
        const key = keyForInstallationProduct(p);
        const original = getOriginalCatalogUnitPrice(p);
        const reqPrice = getResolveRequestUnitPrice(key, original, manualUnitPriceMapRef.current);
        return `${p.product_id}:${p.variant_id}:${p.quantity}:${reqPrice}`;
      })
      .join("|");

    const installAddrDetails = installationAddress?.address_details as Record<string, unknown> | undefined;
    const installAddrSig = installAddrDetails
      ? [
          installAddrDetails.addressLine1,
          installAddrDetails.addressLine2,
          installAddrDetails.city,
          installAddrDetails.state,
          installAddrDetails.stateCode,
          installAddrDetails.zip,
          installAddrDetails.latitude,
          installAddrDetails.longitude,
        ]
          .map((x) => String(x ?? "").trim())
          .join("|")
      : "";

    const requestSignature = JSON.stringify({
      dynamicCostId,
      installationCost: Number(installationCost) || 0,
      discountCode: effectiveDiscountCode,
      feeIds: fees.map((f) => f.id).sort().join(","),
      feeAmounts: fees.map((f) => f.amount).join(","),
      dest: installAddrSig,
      userId: userId || "",
      ownerId: currentOwnerValue?.id || "",
      catalogLineSig,
      customSig: (customproducts || [])
        .map((cp) => `${cp.product_id}:${cp.quantity}:${cp.Price}:${cp.is_taxable}`)
        .join("|"),
    });

    if (!options?.force && lastRecomputeParamsRef.current === requestSignature) {
      safeConsole.log("⏸️ Same parameters as last recompute, skipping duplicate call");
      return true;
    }

    recomputeRequestIdRef.current += 1;
    const currentRequestId = recomputeRequestIdRef.current;
    lastRecomputeParamsRef.current = requestSignature;

    isRecomputingRef.current = true;
    setIsRecomputingTax(true);
    setCouponResolveError("");

    const allFeesSubtotal = fees.reduce((sum, f) => sum + Number(f.amount || 0), 0);

    try {
      if (currentRequestId !== recomputeRequestIdRef.current) return false;

      const signageId = String((isSignageTeammember && parentSignageId ? parentSignageId : userId) || "").trim();
      let resolveSourceId = "";
      let resolveCustomerId = "";
      let resolveInventorySource = "agent";
      let resolveAgentId: string | null = null;
      let resolveAgentName: string | null = null;
      let resolveOfficeInfo: unknown = null;
      let resolveBrokerageFirmId = selectedBrokerageFirm?.value ?? null;
      let resolveBrokerageFirmName = selectedBrokerageFirm?.label ?? null;
      let resolveBillingAddress: unknown =
        selectedBillingAddress ||
        selectedOfficeBillingAddress ||
        findSalesOrderAddressById(addressObjects, formik.values.billingAddressId) ||
        projectBillingAddressRef.current ||
        null;
      const effectiveTeamMemberId =
        formik.values.teamMemberId ||
        selectedTeamMemberOption?.value ||
        selectedTeamMember?.value ||
        teamMemberFromContinueRef.current?.value;
      const effectiveTeamMemberName =
        formik.values.teamMemberName ||
        selectedTeamMemberOption?.label ||
        selectedTeamMember?.label ||
        teamMemberFromContinueRef.current?.label ||
        "";

      if (selectedOfficeAgent) {
        resolveSourceId = selectedOfficeAgent.value;
        resolveCustomerId = selectedOfficeAgent.value;
        resolveAgentId = selectedOfficeAgent.value;
        resolveAgentName = selectedOfficeAgent.label;
        resolveInventorySource = "agent";
        const selectedOfficeData = offices?.find((office) => office._id === selectedOffice?.value);
        resolveOfficeInfo = {
          _id: selectedOffice?.value,
          officeName: selectedOffice?.label,
          officeType: selectedOffice?.label,
          officeAddress: selectedOfficeData?.officeAddress?.[0],
          status: true,
        };
      } else if (selectedAgent) {
        resolveSourceId = selectedAgent.value;
        resolveCustomerId = selectedAgent.value;
        resolveAgentId = selectedAgent.value;
        resolveAgentName = selectedAgent.label;
        resolveInventorySource = "agent";
        if (selectedAgent.brokerageFirmId) {
          resolveBrokerageFirmId = selectedAgent.brokerageFirmId;
          resolveBrokerageFirmName = selectedAgent.brokerageFirmName || resolveBrokerageFirmName;
        }
      } else if (selectedOffice) {
        resolveSourceId = selectedOffice.value;
        resolveInventorySource = "office";
        resolveCustomerId = selectedBrokerageFirm?.value ?? effectiveTeamMemberId ?? "";
        const selectedOfficeData = offices?.find((office) => office._id === selectedOffice?.value);
        resolveOfficeInfo = {
          _id: selectedOffice?.value,
          officeName: selectedOffice?.label,
          officeType: selectedOffice?.label,
          officeAddress: selectedOfficeData?.officeAddress?.[0],
          status: true,
        };
      } else if (selectedBrokerageFirm?.value) {
        resolveSourceId = selectedBrokerageFirm.value;
        resolveInventorySource = "office";
        resolveCustomerId = effectiveTeamMemberId || selectedBrokerageFirm.value;
      } else if (currentOwnerValue?.id) {
        resolveSourceId = currentOwnerValue.id;
        resolveCustomerId = currentOwnerValue.id;
      }

      if (selectedOfficeAgent?.value) {
        resolveCustomerId = selectedOfficeAgent.value;
      } else if (selectedAgent?.value) {
        resolveCustomerId = selectedAgent.value;
      } else if (effectiveTeamMemberId) {
        resolveCustomerId = effectiveTeamMemberId;
      } else if (!resolveCustomerId && selectedBrokerageFirm?.value) {
        resolveCustomerId = selectedBrokerageFirm.value;
      }

      const resolveDiscountCode = getInstallationResolveRootDiscountCode({
        discountCodeOverride: options?.discountCode,
        formikDiscountCode: formik.values.discount_code,
      });

      const resolveLineItems = buildInstallationAddResolveLineItems({
        catalogProducts: selectedProducts || [],
        customProducts: customproducts || [],
        manualUnitPriceMap: manualUnitPriceMapRef.current,
        defaultInventorySource: resolveInventorySource,
      });

      const resolvePayload = buildInstallationResolvePayload({
        cartId: String(formik.values.draft_unique_id || getInstallationDraftId() || ""),
        dynamicCostId,
        signageId,
        sourceId: resolveSourceId,
        customerId: resolveCustomerId,
        inventorySource: resolveInventorySource,
        installationCost: Number(installationCost) || 0,
        installationTaxable: dynamicTaxable,
        discountCode: resolveDiscountCode,
        paymentMethod: formik.values.payment_method || "card",
        userType: formik.values.userType,
        userName: `${formik.values.userFirstName || ""} ${formik.values.userLastName || ""}`.trim(),
        userEmail: formik.values.userEmail,
        userPhone: formik.values.userPhone,
        customerFirstName: formik.values.userFirstName,
        customerLastName: formik.values.userLastName,
        customerEmail: formik.values.userEmail,
        customerPhone: formik.values.userPhone,
        brokerageFirmId: resolveBrokerageFirmId,
        brokerageFirmName: resolveBrokerageFirmName,
        teamMemberId: effectiveTeamMemberId || null,
        teamMemberName: effectiveTeamMemberName,
        office: resolveOfficeInfo,
        agentId: resolveAgentId,
        agentName: resolveAgentName,
        billingAddress: resolveBillingAddress as InstallationResolveAddress | null,
        installationAddress: installationAddress as InstallationResolveAddress | null,
        lineItems: resolveLineItems,
        specialFees: fees.map((f) => mapInstallationSpecialFeeForResolve(f, allAvailableSpecialFees)),
      });

      const resolved = await postInstallationOrderResolve(token || "", {
        order_type: "installation",
        action: "draft",
        payload: resolvePayload,
      });

      if (currentRequestId !== recomputeRequestIdRef.current) return false;

      if (!resolved || typeof resolved !== "object") {
        formik.setFieldValue("totals.installation_subtotal", Number(installationCost) || 0, false);
        formik.setFieldValue("totals.installation_tax_amount", 0, false);
        formik.setFieldValue("totals.specialfee_subtotal", allFeesSubtotal, false);
        formik.setFieldValue("totals.specialfee_tax_amount", 0, false);
        formik.setFieldValue("totals.grand_total", (Number(installationCost) || 0) + allFeesSubtotal, false);
        formik.setFieldValue("specialfee", fees, false);
        return false;
      }

      lastResolvedInstallationRef.current = resolved;
      isUpdatingFromRecomputeRef.current = true;
      const applied = applyInstallationResolveResponse(
        resolved,
        formik,
        Number(installationCost) || 0,
        {
          requestedDiscountCode: effectiveDiscountCode,
          existingFeeRows: fees,
          specialFeeCatalog: allAvailableSpecialFees,
        }
      );
      setSelectedSpecialFees(applied.mergedFees);
      const { code: resolvedCode, amount: resolvedDiscountAmount } = getResolvedDiscountDisplay(
        resolved,
        effectiveDiscountCode
      );
      if (resolvedCode) {
        setCouponInput(resolvedCode);
      } else if (!effectiveDiscountCode) {
        setCouponInput("");
      }
      if (!effectiveDiscountCode) {
        formik.setFieldValue("discount_code", "", false);
        formik.setFieldValue("discount_amount", 0, false);
        formik.setFieldValue("product_discount_amount", 0, false);
      } else if (resolvedCode && resolvedDiscountAmount > 0) {
        formik.setFieldValue("discount_code", resolvedCode, false);
        formik.setFieldValue("discount_amount", resolvedDiscountAmount, false);
      }
      return true;
    } catch (e) {
      safeConsole.error("[OrderResolve] failed:", e);
      setCouponResolveError(
        String((e as { message?: string })?.message ?? "Failed to calculate pricing")
      );
      formik.setFieldValue("totals.installation_subtotal", Number(installationCost) || 0, false);
      formik.setFieldValue("totals.installation_tax_amount", 0, false);
      formik.setFieldValue("totals.specialfee_subtotal", allFeesSubtotal, false);
      formik.setFieldValue("totals.specialfee_tax_amount", 0, false);
      formik.setFieldValue("totals.grand_total", (Number(installationCost) || 0) + allFeesSubtotal, false);
      formik.setFieldValue("specialfee", fees, false);
      return false;
    } finally {
      isRecomputingRef.current = false;
      setIsRecomputingTax(false);
      const shouldRetry = pendingRecomputeRef.current;
      pendingRecomputeRef.current = false;
      if (shouldRetry) {
        triggerRecomputeIfReadyRef.current();
      }
    }
  }, [
    token,
    userId,
    userName,
    selectedInstaller?.value,
    installationCost,
    dynamicCostId,
    dynamicTaxable,
    getSelectedFees,
    coalesceDestination,
    isSignageTeammember,
    parentSignageId,
    selectedBrokerageFirm,
    selectedTeamMemberOption,
    selectedTeamMember,
    offices,
    selectedOfficeBillingAddress,
    installationAddress,
    selectedBillingAddress,
    selectedOfficeAgent?.value,
    selectedAgent?.value,
    selectedOffice?.value,
    formik,
    formik.values.draft_unique_id,
    selectedProducts,
    customproducts,
    manualUnitPriceMap,
  ]);

  recomputeRef.current = recompute;

  const installationAddressSig = React.useMemo(() => {
    const d = installationAddress?.address_details as Record<string, unknown> | undefined;
    if (!d) return "";
    return [
      d.addressLine1,
      d.addressLine2,
      d.city,
      d.state,
      d.stateCode,
      d.zip,
      d.latitude,
      d.longitude,
    ]
      .map((x) => String(x ?? "").trim())
      .join("|");
  }, [installationAddress?.address_details]);

  const recomputeWatchSig = React.useMemo(
    () =>
      JSON.stringify({
        installer: selectedInstaller?.value || "",
        address: installationAddressSig,
        dest: [
          formik.values.destination?.addressLine1,
          formik.values.destination?.city,
          formik.values.destination?.state,
          formik.values.destination?.stateCode,
          formik.values.destination?.zip,
        ]
          .map((x) => String(x ?? "").trim())
          .join("|"),
        installationCost: Number(installationCost) || 0,
        custom: (customproducts || [])
          .map((cp) => `${cp.product_id}:${cp.quantity}:${cp.Price}:${cp.is_taxable}`)
          .join("|"),
        catalog: (selectedProducts || [])
          .filter((p) => p.is_new_catalog && p.product_id)
          .map((p) => {
            const key = keyForInstallationProduct(p);
            const original = getOriginalCatalogUnitPrice(p);
            const reqPrice = getResolveRequestUnitPrice(key, original, manualUnitPriceMap);
            return `${p.product_id}:${p.variant_id}:${p.quantity}:${reqPrice}`;
          })
          .join("|"),
        manualPrices: manualUnitPriceMap,
      }),
    [
      selectedInstaller?.value,
      installationAddressSig,
      formik.values.destination?.addressLine1,
      formik.values.destination?.city,
      formik.values.destination?.state,
      formik.values.destination?.stateCode,
      formik.values.destination?.zip,
      installationCost,
      customproducts,
      selectedProducts,
      manualUnitPriceMap,
    ]
  );

  const unitsCost = Number(formik.values.totals?.units_cost ?? 0);
  const effectiveUnitsCost = React.useMemo(
    () =>
      computeInstallationUnitsCost({
        catalogProducts: selectedProducts || [],
        customProducts: customproducts || [],
        manualUnitPriceMap,
        unitPriceDraftMap,
        getResolvedLineItem,
      }),
    [selectedProducts, customproducts, manualUnitPriceMap, unitPriceDraftMap, getResolvedLineItem]
  );
  const hasManualUnitOverrides =
    Object.keys(manualUnitPriceMap).length > 0 || Object.keys(unitPriceDraftMap).length > 0;
  const displayUnitsCost = hasManualUnitOverrides ? effectiveUnitsCost : unitsCost;
  const resolvedInstallCost = Number(
    formik.values.installation_cost ??
      formik.values.totals?.installation_subtotal ??
      installationCost ??
      0
  );
  const resolvedAdditionalCost = Number(formik.values.additional_cost ?? 0);
  const resolvedSubtotalAmount = hasManualUnitOverrides
    ? resolvedInstallCost + resolvedAdditionalCost + effectiveUnitsCost
    : Number(
        formik.values.subtotal_amount ??
          resolvedInstallCost + resolvedAdditionalCost + unitsCost
      );
  const discountDisplay = React.useMemo(
    () =>
      getOrderDiscountFieldDisplay({
        formikCode: formik.values.discount_code,
        formikAmount: formik.values.discount_amount,
        couponInput,
        resolved: lastResolvedInstallationRef.current,
      }),
    [formik.values.discount_code, formik.values.discount_amount, couponInput]
  );

  const applyDiscountCode = React.useCallback(
    async (rawCode: string) => {
      const code = String(rawCode || "").trim();
      if (!code) return;

      if (!selectedInstaller?.value || !installationAddress?.address_details) {
        showError("Complete installer and installation address before applying a discount.");
        return;
      }
      if (!dynamicCostId) {
        showError("Installation pricing is still loading. Please wait and try again.");
        return;
      }

      setCouponResolveError("");
      setCouponInput(code);
      formik.setFieldValue("discount_code", code, false);

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }

      const ok = await recompute({ force: true, discountCode: code });
      const display = getResolvedDiscountDisplay(lastResolvedInstallationRef.current, code);

      if (display.code && display.amount > 0) {
        setCouponInput(display.code);
        formik.setFieldValue("discount_code", display.code, false);
        formik.setFieldValue("discount_amount", display.amount, false);
        setCouponResolveError("");
        return;
      }

      if (!ok) {
        setCouponResolveError((prev) => prev || "Discount could not be applied. Please try again.");
      } else {
        setCouponResolveError("This discount code could not be applied.");
      }
    },
    [
      dynamicCostId,
      selectedInstaller?.value,
      installationAddress?.address_details,
      formik,
      recompute,
    ]
  );

  const clearDiscountCode = React.useCallback(async () => {
    setCouponResolveError("");
    setCouponInput("");
    formik.setFieldValue("discount_code", "", false);
    formik.setFieldValue("discount_amount", 0, false);
    formik.setFieldValue("product_discount_amount", 0, false);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    await recompute({ force: true, discountCode: "" });
  }, [formik, recompute]);
  const displayGrandTotal = computeInstallationGrandTotal({
    subtotalAmount: resolvedSubtotalAmount,
    specialFeeSubtotal: Number(formik.values.totals?.specialfee_subtotal || 0),
    tax: Number(formik.values.tax || 0),
    discountAmount: discountDisplay.amount,
  });
  const displayRemainingBalance = Math.max(
    0,
    displayGrandTotal - Number(formik.values?.already_paid ?? 0)
  );

  const effectiveCouponCreatedById = React.useMemo(() => {
    let id = "";
    const effectiveTeamMemberId =
      formik.values.teamMemberId ||
      selectedTeamMemberOption?.value ||
      selectedTeamMember?.value ||
      teamMemberFromContinueRef.current?.value;

    if (selectedOfficeAgent?.value) {
      id = selectedOfficeAgent.value;
    } else if (selectedAgent?.value) {
      id = selectedAgent.value;
    } else if (effectiveTeamMemberId) {
      id = effectiveTeamMemberId;
    } else if (selectedBrokerageFirm?.value) {
      id = selectedBrokerageFirm.value;
    } else {
      id = "";
    }

    return id;
  }, [
    formik.values.teamMemberId,
    selectedOfficeAgent?.value,
    selectedAgent?.value,
    selectedTeamMemberOption?.value,
    selectedTeamMember?.value,
    selectedBrokerageFirm?.value,
  ]);

  const ready = React.useMemo(
    () =>
      Boolean(
        selectedInstaller?.value &&
        installationAddress?.address_details &&
        !Number.isNaN(installationCost)
      ),
    [selectedInstaller?.value, installationAddress?.address_details, installationCost]
  );

  // ---- Debounced trigger (runs only when ready) ----------------------
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerRecomputeIfReady = React.useCallback(() => {
    if (!ready) return;

    if (isRecomputingRef.current) {
      pendingRecomputeRef.current = true;
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (!isRecomputingRef.current) {
        void recomputeRef.current?.();
      }
    }, 300);
  }, [ready]);

  triggerRecomputeIfReadyRef.current = triggerRecomputeIfReady;

  const commitManualUnitPrice = React.useCallback(
    (key: string, raw: string) => {
      const errorKey = purchasedProductErrorKey("catalog", key);
      const trimmed = raw.trim();
      if (trimmed === "" || trimmed === ".") {
        patchPurchasedProductFieldError(errorKey, "unitCost", purchasedUnitCostMinMsg);
        setManualUnitPriceMap((prev) => {
          if (!(key in prev)) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
        return;
      }
      const price = Number(trimmed);
      if (!Number.isFinite(price) || price < PURCHASED_FIELD_MIN) {
        patchPurchasedProductFieldError(errorKey, "unitCost", purchasedUnitCostMinMsg);
        return;
      }
      patchPurchasedProductFieldError(errorKey, "unitCost", undefined);
      setManualUnitPriceMap((prev) => ({
        ...prev,
        [key]: Math.round(price * 100) / 100,
      }));
      triggerRecomputeIfReady();
    },
    [triggerRecomputeIfReady]
  );

  const validateAllPurchasedProductFields = React.useCallback((): boolean => {
    const nextErrors: Record<string, PurchasedProductFieldErrors> = {};

    const setErr = (key: string, field: keyof PurchasedProductFieldErrors, msg: string) => {
      nextErrors[key] = { ...nextErrors[key], [field]: msg };
    };

    for (const item of selectedProducts.filter((p) => isPurchasedProductLine(p))) {
      const priceKey = keyForInstallationProduct(item);
      const errorKey = purchasedProductErrorKey("catalog", priceKey);

      if (item.is_accessory) {
        const qtyErr = validatePurchasedQuantityValue(item.quantity);
        if (qtyErr) setErr(errorKey, "quantity", qtyErr);
      }

      const draft = unitPriceDraftMap[priceKey];
      const manual = manualUnitPriceMap[priceKey];
      const resolvedLine = getResolvedLineItem(item);
      const displayUnitCost = getInstallationLineUnitCostDisplay(
        priceKey,
        item,
        manualUnitPriceMap,
        resolvedLine
      );
      const unitRaw =
        draft !== undefined
          ? draft
          : manual !== undefined
            ? String(manual)
            : displayUnitCost > 0
              ? String(displayUnitCost)
              : "";
      const unitErr = validatePurchasedUnitCostValue(unitRaw);
      if (unitErr) setErr(errorKey, "unitCost", unitErr);
    }

    (customproducts || []).forEach((cp, idx) => {
      const errorKey = purchasedProductErrorKey("custom", String(idx));
      const qtyErr = validatePurchasedQuantityValue(cp.quantity);
      if (qtyErr) setErr(errorKey, "quantity", qtyErr);
      const unitErr = validatePurchasedUnitCostValue(cp.Price);
      if (unitErr) setErr(errorKey, "unitCost", unitErr);
    });

    setPurchasedProductFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [selectedProducts, customproducts, unitPriceDraftMap, manualUnitPriceMap, getResolvedLineItem]);

  validateAllPurchasedProductFieldsRef.current = validateAllPurchasedProductFields;

  // ✅ Clear any pending timer and reset ref on unmount
  React.useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      Object.values(unitPriceDebounceRef.current).forEach((timer) => clearTimeout(timer));
      unitPriceDebounceRef.current = {};
      isRecomputingRef.current = false;
    };
  }, []);

  // ---- Watch pricing inputs (only when ready + signature actually changed) ----
  React.useEffect(() => {
    if (!ready) return;
    if (isRecomputingRef.current) return;
    if (isUpdatingFromRecomputeRef.current) {
      isUpdatingFromRecomputeRef.current = false;
      return;
    }
    if (lastRecomputeWatchSigRef.current === recomputeWatchSig) return;
    lastRecomputeWatchSigRef.current = recomputeWatchSig;
    triggerRecomputeIfReadyRef.current();
  }, [ready, recomputeWatchSig]);

  const currentOwner =
    selectedOfficeAgent
      ? { id: selectedOfficeAgent.value, name: selectedOfficeAgent.label, type: "agent" as const }
      : selectedAgent
        ? { id: selectedAgent.value, name: selectedAgent.label, type: "agent" as const }
        : selectedOffice
          ? { id: selectedOffice.value, name: selectedOffice.label, type: "office" as const }
          : null;

  React.useEffect(() => {
    const t = formik.values.totals || {};
    const subAll = Number(t.installation_subtotal || 0) + Number(t.specialfee_subtotal || 0);
    const taxAll = Number(t.installation_tax_amount || 0) + Number(t.specialfee_tax_amount || 0);
    const productTax = Number((formik.values.totals as { product_tax_amount?: number })?.product_tax_amount || 0);
    const grand = hasManualUnitOverrides
      ? displayGrandTotal
      : Number(t.grand_total || 0);
    formik.setFieldValue("subtotal", subAll, false);
    formik.setFieldValue("tax", taxAll + productTax, false);
    formik.setFieldValue("total", Math.max(0, grand), false);
    if (hasManualUnitOverrides) {
      formik.setFieldValue("totals.units_cost", effectiveUnitsCost, false);
      formik.setFieldValue("subtotal_amount", resolvedSubtotalAmount, false);
      formik.setFieldValue("totals.grand_total", displayGrandTotal, false);
    }
  }, [
    formik.values.totals.installation_subtotal,
    formik.values.totals.specialfee_subtotal,
    formik.values.totals.installation_tax_amount,
    formik.values.totals.specialfee_tax_amount,
    formik.values.totals.grand_total,
    (formik.values.totals as { product_tax_amount?: number })?.product_tax_amount,
    hasManualUnitOverrides,
    displayGrandTotal,
    effectiveUnitsCost,
    resolvedSubtotalAmount,
  ]);

  const prevFeesRef = React.useRef<string>("");

  React.useEffect(() => {
    if (isRecomputingRef.current) {
      return;
    }
    if (isUpdatingFromRecomputeRef.current) {
      isUpdatingFromRecomputeRef.current = false;
      return;
    }

    const feeSig = selectedSpecialFees
      .map((f) => `${f.id}:${Number(f.amount || 0)}`)
      .sort()
      .join(",");
    if (prevFeesRef.current === feeSig) return;
    prevFeesRef.current = feeSig;

    if (!ready) return;
    triggerRecomputeIfReadyRef.current();
  }, [selectedSpecialFees, ready]);




  if (loadError) {
    return (
      <div className="content container">
        <div className="alert alert-danger m-4" role="alert">
          Google Maps could not be loaded. Verify Google error in cons.
        </div>
      </div>
    );
  }

  if (!isLoaded) return <div>Loading...</div>;

  return (
    <div className="content container">
      <div className="">
        <h3 className="m-0 fw-bold addheaders">Add Installation Order</h3>
      </div>
      <div className="my-3">
        <div className="d-flex justify-content-between align-items-center flex-wrap col-lg-12 os-card" style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)', borderRadius: '12px', padding: '20px', backgroundColor: 'white' }}>
          {/* Left: Installation Status Tracker */}
          <div className="flex-grow-1">
            <InstallationStatusTracker
              type="installation"
              status={0}
              draftLabel="Draft"
              className="w-100"
            />
          </div>

        </div>
      </div>

      <div className="">
        <div className="">
          {/* <h2>Add Installation Order</h2> */}
          <form
            ref={formRef}
            onSubmit={handleInstallationFormSubmit}
            onKeyDown={(e) => {
              const t = e.target as HTMLElement;
              const tag = t.tagName;
              // Allow Enter in textareas (e.g. Custom Item modal) and buttons; block elsewhere to avoid accidental form submit.
              if (e.key === "Enter" && tag !== "BUTTON" && tag !== "TEXTAREA") {
                e.preventDefault();
              }
            }}
            className="me-2 me-lg-0"
          >
            <div className="row p-3 col-lg-12 card-body shadow-lg pb-4 mb-4 ms-0">
              <div className="mb-3 d-flex align-items-center gap-2">
                <SectionHeader title="Customer Information" icon="ri-user-3-line" color="#155dfc" size="md" divider />
                {orderTypeBadge && (
                  <span className={orderTypeBadge.cls} id="search-bat-type-badge">
                    {orderTypeBadge.label.toUpperCase()}
                  </span>
                )}
              </div>

              <div className="col-12 mb-4">
                <div className="signage-search-half">
                  <SearchBrokerageAgentTeam
                  ref={searchBatRestoreRef}
                  customerGroupId={customerGroupId || ""}
                  token={token || ""}
                  readOnly={Boolean(String(projectIdFromQuery || "").trim())}
                  title="Create Installation"
                  subtitle="Search for a brokerage, team member, or agent to begin"
                  initialLabel={projectContactPrefill?.label}
                  initialType={projectContactPrefill?.type}
                  initialContact={projectContactPrefill?.contact}
                  emailError={
                    formik.submitCount > 0 || formik.touched.userEmail
                      ? (formik.errors.userEmail as string) || ""
                      : ""
                  }
                  phoneError={
                    formik.submitCount > 0 || formik.touched.userPhone
                      ? (formik.errors.userPhone as string) || ""
                      : ""
                  }
                  officeError={
                    formik.submitCount > 0 || formik.touched.officeSelection
                      ? String(formik.errors.officeSelection || "")
                      : ""
                  }
                  teamMemberError={
                    formik.submitCount > 0 || formik.touched.teamMemberId
                      ? String(formik.errors.teamMemberId || "")
                      : ""
                  }
                  onContactChange={(c: { fName: string; lName: string; email: string; phone: string }) => {
                    formik.setFieldValue("userFirstName", c?.fName ?? "");
                    formik.setFieldValue("userLastName", c?.lName ?? "");
                    formik.setFieldValue("userEmail", c?.email ?? "");
                    formik.setFieldValue("userPhone", withDefaultSignageContactPhone(c?.phone));
                  }}
                  onOfficeChange={(office: { id: string; label: string; company_name?: string; office_count?: number }) => {
                    if (!office?.id) return;
                    const selected = {
                      value: office.id,
                      label: office.label,
                      firstName: office.label,
                      lastName: "",
                      email: "",
                      phone: "",
                    };
                    const hasTopLevelTeamMember =
                      !!formik.values.teamMemberId || !!teamMemberFromContinueRef.current?.value;
                    // reset dependent state
                    if (!hasTopLevelTeamMember) {
                      teamMemberFromContinueRef.current = null;
                      setSelectedTeamMember(null);
                      setSelectedTeamMemberOption(null);
                      formik.setFieldValue("teamMemberId", "");
                      formik.setFieldValue("teamMemberName", "");
                    }
                    setSelectedOfficeAgent(null);
                    setSelectedAgent(null);
                    if (!projectRestoreGuardRef.current) {
                      setAddressObjects([]);
                      setSelectedBillingAddress(null);
                      setSelectedOfficeBillingAddress(null);
                      setInstallationAddress(undefined);
                      setSelectedProducts([]);
                      setProductObjs([]);
                      formik.setFieldValue("billingAddressId", "");
                      formik.setFieldValue("installationAddressId", "");
                    }

                    setSelectedOffice(selected as any);
                    formik.setFieldValue("officeSelection", office.id);
                  }}
                  onTeamMemberChange={(member: {
                    id: string;
                    label: string;
                    firstName: string;
                    lastName: string;
                    email: string;
                    phone: string;
                  }) => {
                    if (!member?.id) return;
                    const s: any = {
                      value: member.id,
                      label: member.label,
                      firstName: member.firstName,
                      lastName: member.lastName,
                      email: member.email,
                      phone: member.phone,
                      offices: [],
                      license_type: String((member as any)?.license_type || "BROKERAGE_TEAM_MEMBER").trim(),
                    };
                    teamMemberFromContinueRef.current = { value: s.value, label: s.label };
                    setSelectedTeamMember(s);
                    setSelectedTeamMemberOption(s);
                    formik.setFieldValue("teamMemberId", s.value);
                    formik.setFieldValue("teamMemberName", s.label);
                    formik.setFieldValue("userEmail", s.email);
                    formik.setFieldValue("userPhone", withDefaultSignageContactPhone(s.phone));
                    formik.setFieldValue("userFirstName", s.firstName);
                    formik.setFieldValue("userLastName", s.lastName);

                    setSelectedOfficeAgent(null);
                    formik.setFieldValue("agentSelection", "");

                    if (!projectRestoreGuardRef.current) {
                      setAddressObjects([]);
                      setSelectedBillingAddress(null);
                      setSelectedOfficeBillingAddress(null);
                      setInstallationAddress(undefined);
                      formik.setFieldValue("billingAddressId", "");
                      formik.setFieldValue("installationAddressId", "");
                    }

                    // Addresses belong to office (or brokerage), not team member - pass office ID (same as SignageCreateOrder)
                    const addrUser: any = selectedOffice?.value
                      ? { value: selectedOffice.value, label: selectedOffice.label }
                      : selectedBrokerageFirm
                        ? { value: selectedBrokerageFirm.value, label: selectedBrokerageFirm.label }
                        : null;
                    if (addrUser) {
                      fetchAddresses(addrUser);
                    }
                  }}
                  onAgentChange={(agent: {
                    id: string;
                    label: string;
                    firstName: string;
                    lastName: string;
                    email: string;
                    phone: string;
                  } | null) => {
                    if (!agent?.id) {
                      setSelectedOfficeAgent(null);
                      formik.setFieldValue("agentSelection", "");

                      const tm = selectedTeamMemberOption as {
                        value?: string;
                        firstName?: string;
                        lastName?: string;
                        email?: string;
                        phone?: string;
                      } | null;
                      if (tm?.value) {
                        formik.setFieldValue("userFirstName", tm.firstName || "");
                        formik.setFieldValue("userLastName", tm.lastName || "");
                        formik.setFieldValue("userEmail", tm.email || "");
                        formik.setFieldValue(
                          "userPhone",
                          withDefaultSignageContactPhone(tm.phone)
                        );
                      }

                      if (!projectRestoreGuardRef.current) {
                        setAddressObjects([]);
                        setSelectedBillingAddress(null);
                        setSelectedOfficeBillingAddress(null);
                        setInstallationAddress(undefined);
                        formik.setFieldValue("billingAddressId", "");
                        formik.setFieldValue("installationAddressId", "");
                      }

                      const addrUser = selectedOffice?.value
                        ? { value: selectedOffice.value, label: selectedOffice.label }
                        : selectedBrokerageFirm
                          ? { value: selectedBrokerageFirm.value, label: selectedBrokerageFirm.label }
                          : null;
                      if (addrUser) {
                        void fetchAddresses(addrUser);
                      }
                      return;
                    }

                    const selected: Selected = {
                      value: String(agent.id),
                      label: agent.label || [agent.firstName, agent.lastName].filter(Boolean).join(" ").trim(),
                      firstName: agent.firstName || "",
                      lastName: agent.lastName || "",
                      email: agent.email || "",
                      phone: withDefaultSignageContactPhone(agent.phone),
                    };
                    setSelectedOfficeAgent(selected);
                    setSelectedAgent(null);
                    formik.setFieldValue("agentSelection", selected.value);
                    formik.setFieldValue("userFirstName", selected.firstName);
                    formik.setFieldValue("userLastName", selected.lastName);
                    formik.setFieldValue("userEmail", selected.email);
                    formik.setFieldValue("userPhone", selected.phone);

                    if (!projectRestoreGuardRef.current) {
                      setAddressObjects([]);
                      setSelectedBillingAddress(null);
                      setSelectedOfficeBillingAddress(null);
                      setInstallationAddress(undefined);
                      formik.setFieldValue("billingAddressId", "");
                      formik.setFieldValue("installationAddressId", "");
                    }
                    void fetchAddresses(selected);
                  }}
                  onContinue={(payload: any) => {
                    const { type: licenseTypePayload, item, contact: contactData } = payload || {};
                    const badge = ORDER_TYPE_BADGE_MAP[licenseTypePayload] || ORDER_TYPE_BADGE_MAP.AGENT;
                    setOrderTypeBadge(badge);
                    const c = contactData || {};
                    const resolvedUserPhone = withDefaultSignageContactPhone(
                      String(c.phone ?? "").trim() ||
                        primaryPhoneFromUserLike(item as Record<string, unknown>)
                    );
                    formik.setFieldValue("userFirstName", c.fName ?? "");
                    formik.setFieldValue("userLastName", c.lName ?? "");
                    formik.setFieldValue("userEmail", c.email ?? "");
                    formik.setFieldValue("userPhone", resolvedUserPhone);
                    const opt = {
                      value: item?.value ?? "",
                      label: item?.label ?? "",
                      firstName: c.fName ?? "",
                      lastName: c.lName ?? "",
                      email: item?.email ?? c.email ?? "",
                      phone: resolvedUserPhone,
                      license_type: String((item as any)?.license_type ?? "").trim(),
                      brokerageFirmId:
                        (item as any)?.brokerage_firm_id ??
                        (item as any)?.brokerageFirmId ??
                        (item as any)?.brokerage_id ??
                        null,
                      brokerageFirmName:
                        (item as any)?.brokerage_firm_name ??
                        (item as any)?.brokerageFirmName ??
                        (item as any)?.brokerage_name ??
                        null,
                    };
                    setSelectedOffice(null);
                    setSelectedOfficeAgent(null);
                    if (!projectRestoreGuardRef.current) {
                      setAddressObjects([]);
                      setSelectedBillingAddress(null);
                      setSelectedOfficeBillingAddress(null);
                      setInstallationAddress(undefined);
                    }
                    if (licenseTypePayload === "BROKERAGE_FIRM") {
                      teamMemberFromContinueRef.current = null;
                      formik.setFieldValue("userType", "brokerageFirm");
                      formik.setFieldValue("brokerageFirm", item?.value ?? "");
                      formik.setFieldValue("teamMemberId", "");
                      formik.setFieldValue("teamMemberName", "");
                      formik.setFieldValue("officeSelection", "");
                      formik.setFieldValue("agentSelection", "");
                      setSelectedBrokerageFirm(opt);
                      setSelectedTeamMember(null);
                      setSelectedTeamMemberOption(null);
                      setSelectedAgent(null);
                    } else if (licenseTypePayload === "BROKERAGE_TEAM_MEMBER" || licenseTypePayload === "TEAM_MEMBER") {
                      const tmId = (item?.value ?? (item as any)?.id ?? (item as any)?._id) ?? "";
                      const tmLabel = (item?.label ?? (item as any)?.name ?? `${(item as any)?.firstName ?? ""} ${(item as any)?.lastName ?? ""}`.trim()) || "";
                      teamMemberFromContinueRef.current = tmId ? { value: tmId, label: tmLabel } : null;
                      formik.setFieldValue("userType", "brokerageFirm");
                      formik.setFieldValue("teamMemberId", tmId);
                      formik.setFieldValue("teamMemberName", tmLabel);
                      formik.setFieldValue("officeSelection", "");
                      formik.setFieldValue("agentSelection", "");
                      const tmOption = { ...opt, value: tmId, label: tmLabel, offices: [] };
                      setSelectedTeamMember(tmOption);
                      setSelectedTeamMemberOption(tmOption as any);
                      if (opt.brokerageFirmId) {
                        const b: any = {
                          value: opt.brokerageFirmId,
                          label: opt.brokerageFirmName || "",
                          firstName: "",
                          lastName: "",
                          email: "",
                          phone: "",
                        };
                        setSelectedBrokerageFirm(b);
                        formik.setFieldValue("brokerageFirm", b.value);
                      } else {
                        setSelectedBrokerageFirm(null);
                        formik.setFieldValue("brokerageFirm", "");
                      }
                      setSelectedAgent(null);
                    } else if (licenseTypePayload === "OFFICE") {
                      teamMemberFromContinueRef.current = null;
                      formik.setFieldValue("userType", "brokerageFirm");
                      formik.setFieldValue("teamMemberId", "");
                      formik.setFieldValue("teamMemberName", "");
                      formik.setFieldValue("agentSelection", "");
                      formik.setFieldValue("officeSelection", item?.value ?? "");
                      if (opt.brokerageFirmId) {
                        const b: any = {
                          value: opt.brokerageFirmId,
                          label: opt.brokerageFirmName || "",
                          firstName: "",
                          lastName: "",
                          email: "",
                          phone: "",
                        };
                        setSelectedBrokerageFirm(b);
                        formik.setFieldValue("brokerageFirm", b.value);
                      } else {
                        setSelectedBrokerageFirm(null);
                        formik.setFieldValue("brokerageFirm", "");
                      }
                      setSelectedAgent(null);
                      setSelectedTeamMember(null);
                      setSelectedTeamMemberOption(null);
                      setSelectedOffice(opt);
                      fetchAddresses(opt);
                    } else {
                      teamMemberFromContinueRef.current = null;
                      formik.setFieldValue("userType", "agent");
                      formik.setFieldValue("agentSelection", item?.value ?? "");
                      if (opt.brokerageFirmId) {
                        const b: any = {
                          value: opt.brokerageFirmId,
                          label: opt.brokerageFirmName || "",
                          firstName: "",
                          lastName: "",
                          email: "",
                          phone: "",
                        };
                        setSelectedBrokerageFirm(b);
                        formik.setFieldValue("brokerageFirm", b.value);
                      } else {
                        formik.setFieldValue("brokerageFirm", "");
                        setSelectedBrokerageFirm(null);
                      }
                      formik.setFieldValue("teamMemberId", "");
                      formik.setFieldValue("teamMemberName", "");
                      formik.setFieldValue("officeSelection", "");
                      setSelectedAgent(opt);
                      setSelectedTeamMember(null);
                      setSelectedTeamMemberOption(null);
                    }
                    (async () => {
                      const id = await ensureDraftUniqueId(token || "");
                      if (id) formik.setFieldValue("draft_unique_id", id, false);
                    })();
                    if (item?.value && (licenseTypePayload === "AGENT" || licenseTypePayload === "BROKERAGE_AGENT")) {
                      fetchAddresses(opt);
                    }
                  }}
                  onReset={() => {
                    setOrderTypeBadge(null);
                    teamMemberFromContinueRef.current = null;
                    formik.setFieldValue("userType", "");
                    setSelectedBrokerageFirm(null);
                    setSelectedTeamMember(null);
                    setSelectedTeamMemberOption(null);
                    setSelectedOffice(null);
                    setSelectedOfficeAgent(null);
                    setSelectedAgent(null);
                    formik.setFieldValue("brokerageFirm", "");
                    formik.setFieldValue("teamMemberId", "");
                    formik.setFieldValue("teamMemberName", "");
                    formik.setFieldValue("officeSelection", "");
                    formik.setFieldValue("agentSelection", "");
                    formik.setFieldValue("userFirstName", "");
                    formik.setFieldValue("userLastName", "");
                    formik.setFieldValue("userEmail", "");
                    formik.setFieldValue("userPhone", "");
                    formik.setFieldValue("billingAddressId", "");
                    formik.setFieldValue("installationAddressId", "");
                    // Reset addresses
                    setAddressObjects([]);
                    setSelectedBillingAddress(null);
                    setSelectedOfficeBillingAddress(null);
                    setInstallationAddress(undefined);
                    // Reset product details (catalog + custom line items)
                    setSelectedProducts([]);
                    setCustomproducts([]);
                    setProductObjs([]);
                    // Reset discount
                    formik.setFieldValue("discount_amount", 0, false);
                    formik.setFieldValue("product_discount_amount", 0, false);
                    formik.setFieldValue("discount_code", "", false);
                    setCouponInput("");
                  }}
                  />
                </div>
              </div>
              {/* Old Buyer Type + Brokerage/Office/Team/Agent flow replaced by SearchBrokerageAgentTeam above - hidden */}
          
            </div>

            <div className="d-block d-lg-flex gap-3">
              <div className="col-lg-8">
                <div className="row col-lg-12 card-body shadow-lg mb-4 ms-0 p-3">
                  <div className="mb-3">
                    <SectionHeader title="Schedule & Assignment" icon="ri-calendar-line" color="#00a63e" size="md" divider />
                  </div>
                  {/* Scheduled Date */}
                  <div className="col-lg-4 mb-3">
                    <label className="form-label table-headers">
                      Scheduled Date
                    </label>
                    <input
                      type="date"
                      className="form-control table-cells"
                      id="scheduled_date"
                      name="scheduled_date"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.scheduled_date}
                    />
                    {formik.touched.scheduled_date && formik.errors.scheduled_date && (
                      <div className="invalid-feedback d-block">{formik.errors.scheduled_date}</div>
                    )}
                  </div>

                  {/* Assigned To - Only show when parentSignageId is loaded for team members */}
                  {(!isSignageTeammember || parentSignageId) ? (
                    <div className="col-lg-4 mb-3">
                      <label className="form-label table-headers">Assigned To <span className="text-danger">*</span></label>
                      <AsyncPaginate
                        classNamePrefix="rs"
                        placeholder="Search Assigned To..."
                        value={selectedInstaller ? {
                          value: selectedInstaller.value,
                          label: selectedInstaller.label,
                          firstName: selectedInstaller.firstName,
                          lastName: selectedInstaller.lastName,
                          email: selectedInstaller.email,
                          phone: selectedInstaller.phone
                        } : null}
                        loadOptions={loadSignageTeamMemberOptions}
                        onChange={(selected) => {
                          if (selected) {
                            setSelectedInstaller(selected);
                            formik.setFieldValue("installeId", selected.value || "");
                          } else {
                            setSelectedInstaller(null);
                            formik.setFieldValue("installeId", "");
                          }
                        }}
                        onBlur={() => formik.setFieldTouched("installeId", true)}
                        additional={{ page: 1 }}
                        defaultOptions
                        debounceTimeout={300}
                        isClearable
                        components={{ DropdownIndicator }}
                        styles={customSelectStyles}
                      />
                      {formik.touched.installeId && formik.errors.installeId && (
                        <div className="text-danger">{formik.errors.installeId}</div>
                      )}
                    </div>
                  ) : (
                    <div className="col-lg-4 mb-3">
                      <label className="form-label table-headers">Assigned To <span className="text-danger">*</span></label>
                      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "38px" }}>
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <span className="text-muted">Loading team members...</span>
                      </div>
                    </div>
                  )}

                  {/* Jobs Today */}
                  <div className="col-lg-4 mb-3">
                    <label className="form-label table-headers">Jobs Today</label>
                    <input
                      type="number"
                      className="form-control"
                      id="jobs_today"
                      name="jobs_today"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={scheduledJobsCount || formik.values.jobs_today}
                      readOnly
                    />
                    {formik.touched.jobs_today && formik.errors.jobs_today && (
                      <div className="invalid-feedback d-block">{formik.errors.jobs_today}</div>
                    )}
                  </div>

                  {/* Priority */}
                  <div className="col-lg-4 mb-3">
                    <label className="form-label table-headers">Priority <span className="text-danger">*</span></label>
                    <Select
                      id="priority"
                      className=""
                      name="priority"
                      options={INSTALLATION_PRIORITY_OPTIONS}
                      value={
                        INSTALLATION_PRIORITY_OPTIONS.find(
                          (o) => o.value === Number(formik.values.priority)
                        ) ?? null
                      }
                      onChange={(option) =>
                        formik.setFieldValue("priority", option?.value ?? null, false)
                      }
                      onBlur={formik.handleBlur}
                      components={{ DropdownIndicator }}
                      styles={customSelectStyles}
                      classNamePrefix="rs"
                    />
                    {formik.touched.priority && formik.errors.priority && (
                      <div className="text-danger">{formik.errors.priority}</div>
                    )}
                  </div>
                </div>
                {String(linkedProjectId || "").trim() && hasApprovedMessage ? (
                  <div className="row col-lg-12 mb-3 ms-0 p-0">
                    {approvedProofCards.map((card) => (
                      <CompactApprovedProofCard
                        key={`approved-proof-v-${card.version}`}
                        version={card.version}
                        files={card.files}
                        approvedBy={card.approvedByName}
                        approvedAt={card.approvedAtLabel}
                        footerBy={card.assignedToName}
                        variant="installation"
                        billingAddress={
                          card.billingAddress ??
                          selectedOfficeBillingAddress ??
                          selectedBillingAddress ??
                          null
                        }
                        installationAddress={
                          card.installationAddress ??
                          installationAddress ??
                          null
                        }
                        shippingName={card.shippingName}
                        shippingDesc={card.shippingDesc}
                        shippingAmountLabel={card.shippingAmountLabel}
                        showOrderSummary={isSignagePortalRoute(location.pathname, baseRoute)}
                        orderSummary={card.orderSummary}
                        onDownloadByFileId={(fileId, fileName) => {
                          void downloadWorkDriveFile(fileId, fileName);
                        }}
                      />
                    ))}
                  </div>
                ) : null}

                <div className="row col-lg-12 mb-4 card-body shadow-lg mb-4 ms-0 pb-3 p-3">
                  <div className="col-lg-12 d-flex mb-3">
                    {/* Address: teal */}
                    <SectionHeader title="Address Information" icon="ri-map-pin-line" color="#e7000b" size="md" divider className="col-lg-4" />
                    <div className="align-items-baseline col-lg-8 d-flex mb-3 justify-content-end">
                      {/* <div className="col-lg-4">
                        <h5>Address details</h5>
                      </div> */}
                      <div className="align-items-baseline col-lg-6 d-flex justify-content-end">
                        <label className="me-2">Gate Code</label>
                        <input type="text" id="gatecode" name="gatecode" className="form-control w-50" value={formik.values.gatecode} onChange={formik.handleChange} onBlur={formik.handleBlur} placeholder="Enter Gate Code" />
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="align-items-baseline col-lg-12 d-flex justify-content-between">
                      <div className="d-flex fw-bold gap-2 my-2">
                        <input
                          type="checkbox"
                          id="isSameAsBilling"
                          name="isSameAsBilling"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          checked={formik.values.isSameAsBilling}
                        />{" "}
                        Billing and Installation Address are Same

                      </div>
                      <button
                        type="button"
                        className="btn btn-primary table-addbutton br-8"
                        onClick={() => setShowAddressForm(true)}
                        // disabled={Boolean(formik.values.billingAddressId)}
                        title={formik.values.billingAddressId ? "Billing address selected" : "Add new address"}
                      >
                        + Address
                      </button>
                    </div>
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label
                            htmlFor="billingAddressId"
                            className="form-label table-headers"
                          >
                            Billing Address <span className="text-danger">*</span>
                          </label>
                          <Select
                            // className="form-control"
                            id="billingAddress"
                            name="billingAddress"
                            options={addressOptions}
                            isClearable
                            value={
                              addressOptions?.find(
                                (option) =>
                                  option.value === formik.values.billingAddressId
                              ) || null
                            }
                            onChange={(selected: any) => {
                              formik.setFieldValue("billingAddressId", selected?.value);
                              const selectedAddress =
                                findSalesOrderAddressById(addressObjects, selected?.value) ||
                                addressObjects?.find((addr) => addr._id === selected?.value) ||
                                null;
                              setSelectedOfficeBillingAddress(selectedAddress);
                              setSelectedBillingAddress(selectedAddress);
                            }}
                            components={{ DropdownIndicator }}
                            styles={customSelectStyles}
                            classNamePrefix="rs"
                          />
                          {formik.touched.billingAddressId && formik.errors.billingAddressId && (
                            <div className="text-danger mt-1">
                              {formik.errors.billingAddressId}
                            </div>
                          )}
                        </div>
                      </div>

                      {formik.values.isSameAsBilling === true ? null : (
                        <div className="col-lg-6 mb-3">
                          <div className="">
                            <label
                              htmlFor="installationAddressId"
                              className="form-label table-headers"
                            >
                              Installation Address <span className="text-danger">*</span>
                            </label>
                            <AddressAutocomplete
                              id="installationAddressId"
                              name="installationAddressId"
                              value={formik.values.installationAddressId}
                              onChange={(v) => formik.setFieldValue("installationAddressId", v)}
                              onSelect={(picked) => {
                                const installation_address = `${picked.addressLine1}, ${picked.city}, ${picked.state}, ${picked.zipcode}`;

                                const addressDetails: Address = {
                                  _id: "",
                                  label: installation_address,
                                  addressLine1: picked.addressLine1,
                                  addressLine2: "",
                                  country: picked.country,
                                  county: picked.city,
                                  city: picked.city,
                                  state: picked.state,
                                  zip: picked.zipcode,
                                  cityCode: picked.cityCode,
                                  stateCode: picked.stateCode,
                                  countryCode: picked.countryCode,
                                  latitude: picked.latitude,
                                  longitude: picked.longitude,
                                  phone: "",
                                };

                                const newAddresses: Addresses = {
                                  _id: "",
                                  user_id: "",
                                  user_type: "",
                                  label: installation_address,
                                  first_name: "",
                                  last_name: "",
                                  company: "",
                                  default_address: false,
                                  address_details: addressDetails,
                                  created_by: {
                                    id: userId || "",
                                    name: userName || "",
                                  },
                                  updated_by: {
                                    id: userId || "",
                                    name: userName || "",
                                  },
                                };

                                setInstallationAddress(newAddresses);
                                formik.setFieldValue('installationAddressId', installation_address);
                                formik.setFieldValue("isSameAsBilling", false);
                                triggerRecomputeIfReadyRef.current();
                              }}
                              className={`form-control table-cells ${formik.touched.installationAddressId && formik.errors.installationAddressId ? "is-invalid" : ""}`}
                              placeholder="Enter installation address"
                              error={formik.touched.installationAddressId && formik.errors.installationAddressId ? (formik.errors.installationAddressId as string) : undefined}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-12 d-flex justify-content-end pe-4 mb-3" style={{ marginTop: "0px !important" }}>
                    <button
                      type="button"
                      className={`btn btn-sm rounded-pill d-inline-flex align-items-center gap-2 shadow-sm ${isMarkerDraggable ? "btn-success" : "btn-outline-primary"}`}
                      title={`${isMarkerDraggable ? "Disable map edit" : "Enable map edit"}`}
                      onClick={() => {
                        const newValue = !isMarkerDraggable;
                        setIsMarkerDraggable(newValue);
                        showSuccess(
                          newValue
                            ? "You can now move the marker on the map."
                            : "Marker dragging disabled."
                        );
                      }}
                    >
                      <i className="ri-map-2-line fs-17"></i>
                      <i className="ri-edit-2-line fs-17"></i>
                      <span className="small fw-semibold">{isMarkerDraggable ? "Editing" : "Edit Map"}</span>
                    </button>
                  </div>
                  <div className="col-lg-12">
                    <GoogleMap
                      mapContainerStyle={mapContainerStyle}
                      zoom={10}
                      center={selectedLocation || center}
                      onLoad={handleInstallationMapLoad}
                    >
                      {selectedLocation && (
                        <Marker
                          position={selectedLocation}
                          draggable={isMarkerDraggable}
                          onDragEnd={handleMarkerDragEnd}
                        />
                      )}
                    </GoogleMap>
                    {formik.touched.location && formik.errors.location && (
                      <div className="text-danger mt-2">{formik.errors.location}</div>
                    )}
                  </div>
                </div>

                {/* <div className="row">
              <h5>Products Selection</h5>
              <div className="col-lg-4 mb-3">
                <h4 className="table-headers mb-2">Products</h4>
                <Select
                  id="products"
                  name="products"
                  options={productOptions}
                  value={selectedProductOption}
                  onChange={(selected) => {
                    setSelectedProductOption(selected)
                  }}
                  onBlur={formik.handleBlur}
                />
              </div>
              <div className="col-lg-4 ">
                <button
                  className="btn btn-info mt-4"
                  type="button"
                  onClick={() => {
                    if (!selectedProductOption) return;

                    const alreadyExists = selectedProducts.some(
                      (prod) => prod.product_id === selectedProductOption.value
                    );
                    if (alreadyExists) {
                      showError("Product already added");
                      return;
                    }

                    const fullProduct = productObjs.find(
                      (p) => p.product_id === selectedProductOption.value
                    );

                    if (fullProduct) {
                      const flattenedProduct = {
                        product_id: fullProduct.product_id,
                        name: fullProduct.product.name,
                        sku: fullProduct.product.sku || "",
                        inventory_type: fullProduct.inventory_type,
                        product_location_status: fullProduct.product.product_location_status,
                        user_id: fullProduct.agent_id || fullProduct.office_id,
                        quantity: 1,
                        updateStatus: "",
                        productionStatus: "",
                      };

                      setSelectedProducts((prev) => [...prev, flattenedProduct]);
                      setSelectedProductOption(null);
                    }
                    safeConsole.log("selectedProducts", selectedProducts);
                  }}

                >
                  Add Product
                </button>

              </div>
            </div> */}

                {/* Post Type & Sign Configuration - dynamic data from API */}
                <PostTypeSignConfiguration
                  type="add"
                  vendorId={pickerSignageCompanyId}
                  token={token || ""}
                  postTypeOrderContext={postTypeOrderContext}
                  pickerUserType={pickerUserType}
                  pickerUserId={pickerUserId}
                  pickerEndUserId={pickerEndUserId}
                  pickerSignageCompanyId={pickerSignageCompanyId}
                  studioCustomization={installationStudioCustomization}
                  selectedProductIds={selectedProducts.map((p) => String(p.product_id ?? "")).filter(Boolean)}
                  selectedStockLines={selectedProducts.map((p) => ({
                    product_id: String(p.product_id ?? ""),
                    variant_id: p.variant_id,
                    quantity: Number(p.quantity) || 1,
                  }))}
                  accessoryCount={selectedProducts.filter((p) => p.is_accessory).reduce((sum, p) => sum + (Number(p.quantity) || 0), 0)}
                  onPostTypeChange={(postType) => {
                    setSelectedPostTypeId(postType?.id ?? null);
                    setInstallationPost(null);
                    setSelectedProducts([]);
                    setCustomproducts([]);
                    setProductObjs([]);
                  }}
                  onClearSlotReady={(api) => { postTypeConfigRef.current = api; }}
                  onInstallationPostChange={setInstallationPost}
                  onSlotDuplicateProduct={() =>
                    showError("This product is already selected. You cannot assign the same product to multiple slots.")
                  }
                  onSlotRemoved={(slotKey) => {
                    setSelectedProducts((prev) => prev.filter((p) => p.slotKey !== slotKey));
                  }}
                  onSlotProductSelect={(product: LinkedProduct, slotKey: string, slotId?: string) => {
                    // Linked = direct/agent/office product (case 1); catalog = from Browse Catalog (cases 2–4, accessory).
                    const isLinked = product.linked_mode !== "catalog" && (product.inventory_source === "agent" || product.inventory_source === "office");
                    const isNewCatalog = product.linked_mode === "catalog";
                    // New product: inventory_type by end user — agent → "agent", office or teammember → "office"
                    const inventoryTypeForNew =
                      isNewCatalog && pickerUserType
                        ? pickerUserType === "agent"
                          ? "agent"
                          : "office"
                        : product.inventory_source || "";
                    const hasDesign = !!String(product.design_id ?? "").trim();
                    const previewId = String(product.preview_url ?? "").trim();
                    const imageForRow =
                      hasDesign && previewId ? previewId : product.images?.[0]?.url || "";
                    const flattened: FlattenedProduct = {
                      product_id: product.product_id,
                      name: product.name,
                      sku: product.sku,
                      inventory_type: inventoryTypeForNew,
                      current_status: isNewCatalog ? "with_supplier" : product.current_status,
                      availableqty: product.available_quantity ?? 0,
                      quantity: 1,
                      image_url: imageForRow,
                      location: product.location,
                      is_accessory: slotKey === "accessory",
                      ...(slotKey !== "accessory" ? { slotKey, slot_id: slotId ?? "" } : { slot_id: "" }),
                      ...(isLinked && pickerUserId ? { user_id: pickerUserId } : {}),
                      ...(product.variant_id ? { variant_id: String(product.variant_id).trim() } : {}),
                      ...(isNewCatalog
                        ? {
                            is_new_catalog: true,
                            variant_id: product.variant_id,
                            unit_price: product.retail_price != null ? Number(product.retail_price) : undefined,
                            is_taxable: Boolean((product as any).is_taxable ?? (product as any).taxable ?? (product as any)["product.is_taxable"]),
                            ...(product.customizations ? { customizations: product.customizations } : {}),
                          }
                        : {}),
                      ...(hasDesign
                        ? {
                            design_id: String(product.design_id ?? "").trim(),
                            design_name: String(product.design_name ?? "").trim(),
                            preview_url: previewId,
                          }
                        : {}),
                    };
                    handleAddFromPicker(flattened);
                  }}
                  onCustomItemAdd={customProductSettingEnabled ? async (data: any) => {
                    const t = token || "";
                    if (!t) {
                      showError("Session expired. Please log in again.");
                      return;
                    }
                    try {
                      const productId = await fetchNewUniqueId(t);
                      const variantId = await fetchNewUniqueId(t);
                      const qty = Math.max(1, parseInt(String(data.quantity), 10) || 1);
                      const price = parseFloat(String(data.price)) || 0;
                      setCustomproducts((prev) => {
                        const newItem = {
                          product_id: productId,
                          variant_id: variantId,
                          item_name: String(data.item_name || "").trim() || "Custom Item",
                          quantity: qty,
                          Price: price,
                          is_taxable: !!data.taxable,
                          custom_information: String(data.custom_info || "").trim(),
                          length: parseFloat(String(data.length)) || 0,
                          width: parseFloat(String(data.width)) || 0,
                          height: parseFloat(String(data.height)) || 0,
                          weight: parseFloat(String(data.weight)) || 0,
                        };
                        const next = [...prev, newItem];
                        const idx = next.length - 1;
                        const errorKey = purchasedProductErrorKey("custom", String(idx));
                        queueMicrotask(() => {
                          const qtyErr = validatePurchasedQuantityValue(newItem.quantity);
                          patchPurchasedProductFieldError(errorKey, "quantity", qtyErr);
                          const unitErr = validatePurchasedUnitCostValue(newItem.Price);
                          patchPurchasedProductFieldError(errorKey, "unitCost", unitErr);
                        });
                        return next;
                      });
                    } catch (err) {
                      showError(err instanceof Error ? err.message : "Failed to add custom item");
                      throw err;
                    }
                  } : undefined}
                />

                <div className="row col-lg-12 mb-lg-3 card-body shadow-lg mb-1 ms-0 p-3">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex">
                      <span
                        aria-hidden="true"
                        className="image-icons"
                        style={{
                          backgroundColor: "#9810fa",
                          WebkitMaskImage: `url(${ProductSelection})`,
                          maskImage: `url(${ProductSelection})`,
                        }}
                      />
                      <SectionHeader title="Product Selection" icon="" variant="success" size="md" divider />
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      {/* <button
                        type="button"
                        className="btn btn-primary table-addbutton br-8"
                        onClick={() => setPickerOpen(true)}
                        disabled={!canShowAdd}
                        title={canShowAdd ? "Add Product" : "Select agent/office first"}
                      >
                        <i className="ri-add-line me-1" /> Add Product
                      </button> */}
                    </div>
                  </div>
                  <div className="col-lg-12">
                    {filtered.length === 0 && customproducts.length === 0 ? (
                      <div className="text-center text-muted py-4 border rounded-3">
                        No products added yet.
                      </div>
                    ) : (
                      (() => {
                        const inventoryProducts = filtered.filter((p) => !isPurchasedProductLine(p));
                        const purchasedProducts = filtered.filter((p) => isPurchasedProductLine(p));
                        const renderCard = (item: typeof filtered[0], displayIndex: number, isPurchasedLine: boolean) => {
                          const qtyNum = Math.max(1, Number(item.quantity) || 0);
                          const priceKey = keyForInstallationProduct(item);
                          const resolvedLine = isPurchasedLine ? getResolvedLineItem(item) : undefined;
                          const unitPriceDraft = unitPriceDraftMap[priceKey];
                          const isManualUnitPrice =
                            isPurchasedLine &&
                            (manualUnitPriceMap[priceKey] !== undefined ||
                              unitPriceDraft !== undefined);
                          const rowDiscount = isPurchasedLine
                            ? getInstallationLineDiscountDisplay(
                                priceKey,
                                item,
                                manualUnitPriceMap,
                                resolvedLine
                              )
                            : 0;
                          const lineTaxAmount = Number(
                            resolvedLine?.item_tax_info?.total_product_tax_amount ?? 0
                          );
                          const lineTaxable =
                            resolvedLine?.item_tax_info?.taxable ?? item.is_taxable ?? false;
                          const displayUnitCost = isPurchasedLine
                            ? getInstallationLineUnitCostDisplay(
                                priceKey,
                                item,
                                manualUnitPriceMap,
                                resolvedLine
                              )
                            : 0;
                          const unitCostInputValue =
                            unitPriceDraft !== undefined
                              ? unitPriceDraft
                              : displayUnitCost === 0
                                ? ""
                                : String(displayUnitCost);
                          const rowKey = item.rowKey || item.product_id;
                          const purchasedErrorKey = isPurchasedLine
                            ? purchasedProductErrorKey("catalog", priceKey)
                            : "";
                          const purchasedRowErrors = purchasedProductFieldErrors[purchasedErrorKey] || {};
                          return (
                          <div key={rowKey} className="mb-3 p-3 rounded-3 border shadow-sm bg-gray-50">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <div className="d-flex align-items-center gap-2">
                                <h6 className="m-0">Product #{displayIndex}</h6>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                              <button
                                type="button"
                                className="btn btn-link text-danger p-0"
                                title="Remove"
                                onClick={() => removeRow(rowKey)}
                              >
                                <i className="ri-delete-bin-line fs-5"></i>
                              </button>
                              </div>
                            </div>
                          <div className="row g-3">
                            <div className="col-lg-4">
                              <label className="table-cells">Product Name</label>
                              <input
                                type="text"
                                className="form-control"
                                value={item.name || item.product_name || ""}
                                readOnly
                              />
                            </div>
                            <div className="col-lg-4">
                              <label className="table-cells">SKU/Product Code</label>
                              <input
                                type="text"
                                className="form-control"
                                value={item.sku || ""}
                                readOnly
                              />
                            </div>
                            <div className="col-lg-4">
                              <label className="table-cells">Production Status</label>
                              <input
                                type="text"
                                className="form-control"
                                value={item.productionStatus || "--"}
                                readOnly
                              />
                            </div>
                            <div className="col-lg-4">
                              <label className="table-cells">Available Quantity</label>
                              <input
                                type="number"
                                className="form-control"
                                value={Number(item.availableqty ?? 0)}
                                readOnly
                              />
                            </div>
                            <div className="col-lg-4">
                              <label className="table-cells">Quantity</label>
                              <input
                                type="number"
                                className={`form-control ${purchasedRowErrors.quantity ? "is-invalid" : ""}`}
                                min={1}
                                placeholder="1"
                                value={item.quantity || ''}
                                disabled={!item.is_accessory}
                                readOnly={!item.is_accessory}
                                onChange={(e) => {
                                  if (!item.is_accessory) return;
                                  const rawValue = e.target.value;
                                  const qty = rawValue === '' ? '' : Number(rawValue);
                                  setSelectedProducts((prev) =>
                                    prev.map((p) =>
                                      (p.rowKey || p.product_id) === rowKey ? { ...p, quantity: qty } : p
                                    )
                                  );
                                  if (isPurchasedLine) {
                                    patchPurchasedProductFieldError(
                                      purchasedErrorKey,
                                      "quantity",
                                      validatePurchasedQuantityValue(rawValue)
                                    );
                                  }
                                  if (item.is_new_catalog && item.product_id && item.variant_id) {
                                    const priceKey = keyForInstallationProduct(item);
                                    if (manualUnitPriceMap[priceKey] === undefined) {
                                      setManualUnitPriceMap((prev) => {
                                        const next = { ...prev };
                                        delete next[priceKey];
                                        return next;
                                      });
                                    }
                                    triggerRecomputeIfReady();
                                  }
                                }}
                                onBlur={(e) => {
                                  if (!item.is_accessory) return;
                                  patchPurchasedProductFieldError(
                                    purchasedErrorKey,
                                    "quantity",
                                    validatePurchasedQuantityValue(e.target.value)
                                  );
                                }}
                                onWheel={(e) => e.currentTarget.blur()}
                              />
                              {purchasedRowErrors.quantity ? (
                                <div className="text-danger small mt-1">{purchasedRowErrors.quantity}</div>
                              ) : null}
                            </div>
                            {isPurchasedLine ? (
                              <div className="col-lg-4">
                                <label className="table-cells">Unit Cost</label>
                                <input
                                  type="number"
                                  className={`form-control text-center ${purchasedRowErrors.unitCost ? "is-invalid" : ""}`}
                                  min={1}
                                  step="0.01"
                                  placeholder="1"
                                  value={unitCostInputValue}
                                  onChange={(e) => {
                                    const key = keyForInstallationProduct(item);
                                    const raw = e.target.value;
                                    setUnitPriceDraftMap((prev) => ({ ...prev, [key]: raw }));
                                    patchPurchasedProductFieldError(
                                      purchasedErrorKey,
                                      "unitCost",
                                      validatePurchasedUnitCostValue(raw)
                                    );
                                    if (unitPriceDebounceRef.current[key]) {
                                      clearTimeout(unitPriceDebounceRef.current[key]);
                                    }
                                    unitPriceDebounceRef.current[key] = setTimeout(() => {
                                      commitManualUnitPrice(key, raw);
                                    }, 400);
                                  }}
                                  onBlur={() => {
                                    const key = keyForInstallationProduct(item);
                                    const raw = unitPriceDraftMap[key];
                                    if (raw === undefined) {
                                      const unitErr = validatePurchasedUnitCostValue(unitCostInputValue);
                                      patchPurchasedProductFieldError(
                                        purchasedErrorKey,
                                        "unitCost",
                                        unitErr
                                      );
                                      return;
                                    }
                                    if (unitPriceDebounceRef.current[key]) {
                                      clearTimeout(unitPriceDebounceRef.current[key]);
                                      delete unitPriceDebounceRef.current[key];
                                    }
                                    commitManualUnitPrice(key, raw);
                                    setUnitPriceDraftMap((prev) => {
                                      const next = { ...prev };
                                      delete next[key];
                                      return next;
                                    });
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault();
                                  }}
                                />
                                {purchasedRowErrors.unitCost ? (
                                  <div className="text-danger small mt-1">{purchasedRowErrors.unitCost}</div>
                                ) : null}
                              </div>
                            ) : (
                              <div className="col-lg-4">
                                <label className="table-cells">Unit Cost</label>
                                <input
                                  type="text"
                                  className="form-control text-center"
                                  value="-"
                                  disabled
                                  readOnly
                                />
                              </div>
                            )}
                            {isPurchasedLine && (
                              <div className="col-lg-4">
                                <label className="table-cells">Discount</label>
                                <input
                                  type="text"
                                  className="form-control text-center"
                                  value={
                                    isManualUnitPrice ? "" : formatLineDiscountAmount(rowDiscount)
                                  }
                                  readOnly
                                  disabled
                                  style={
                                    !isManualUnitPrice && rowDiscount > 0
                                      ? { color: "#00a63e" }
                                      : undefined
                                  }
                                />
                              </div>
                            )}
                            {/* {isPurchasedLine && (
                              <div className="col-lg-4">
                                <label className="table-cells">Tax</label>
                                <input
                                  type="text"
                                  className="form-control text-center"
                                  value={
                                    lineTaxable
                                      ? `$${lineTaxAmount.toFixed(2)}`
                                      : "Non-taxable"
                                  }
                                  readOnly
                                />
                              </div>
                            )} */}
                            <div className="col-lg-4">
                              <label className="table-cells">Product Location</label>
                              <input
                                type="text"
                                className="form-control"
                                value={(item.product_current_status || "").replace(/_/g, " ")}
                                disabled
                                readOnly
                              />
                            </div>
                            <div className="col-lg-7">
                              <label className="table-cells">Bin Location</label>
                              <input
                                type="text"
                                className="form-control text-ellipsis"
                                value={
                                  item.location && item.location.length > 0
                                    ? `${item.location[0].warehouse_name || ""} > ${item.location[0].zone_name || ""} > ${item.location[0].rack_name || ""} > ${item.location[0].bin_name || ""}`
                                    : "No location assigned"
                                }
                                disabled
                                readOnly
                                title={
                                  item.location && item.location.length > 0
                                    ? `${item.location[0].warehouse_name || ""} > ${item.location[0].zone_name || ""} > ${item.location[0].rack_name || ""} > ${item.location[0].bin_name || ""}`
                                    : "No location details available"
                                }
                              />
                            </div>
                            {(() => {
                              const custRows = getVisibleCustomizationFieldsForDisplay(item.customizations);
                              if (custRows.length === 0) return null;
                              return (
                                <div className="col-12">
                                  <div className="fw-medium table-cells small mb-2 mt-1">
                                    customizations:
                                  </div>
                                  <div className="row g-2 g-lg-3">
                                    {custRows.map((field, idx) => (
                                      <div
                                        className="col-12 col-sm-6 col-lg-6"
                                        key={`${item.product_id}-cust-${String(field.field_name || idx)}`}
                                      >
                                        <div className="px-3 py-2 small text-break">
                                          <span className="fs-14 fw-medium">{field.field_name || "Field"}</span>
                                          <span className="text-muted"> : </span>
                                          {(() => {
                                            const v = String(field.value ?? "").trim();
                                            const ft = String(field.field_type || "").toLowerCase();
                                            const isFile = ft === "file" || ft === "image";
                                            const m = (customizationImageUrlMap as Record<string, string>) || {};
                                            const src =
                                              isFile && /^[a-f0-9]{24}$/i.test(v)
                                                ? m[v] || getImageSrc(v, m) || ""
                                                : "";
                                            if (src) {
                                              return (
                                                <img
                                                  src={src}
                                                  alt=""
                                                  style={{ maxHeight: 120, maxWidth: "100%", borderRadius: 8, border: "1px solid #dee2e6", display: "block", marginTop: 4 }}
                                                />
                                              );
                                            }
                                            return <span className="fs-14">{String(field.value ?? "")}</span>;
                                          })()}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                          );
                        };
                        return (
                          <>
                            {inventoryProducts.length > 0 && (
                              <>
                                <div className="d-flex align-items-center gap-2 mb-3">
                                  <span className="fw-semibold table-cells">Inventory Products</span>
                                </div>
                                {inventoryProducts.map((item, i) => renderCard(item, i + 1, false))}
                              </>
                            )}
                            {(purchasedProducts.length > 0 || customproducts.length > 0) && (
                              <>
                                {inventoryProducts.length > 0 && <hr className="my-4" />}
                                <div className="d-flex align-items-center gap-2 mb-3">
                                  <span className="fw-semibold table-cells">Purchased Products</span>
                                </div>
                                {purchasedProducts.map((item, i) =>
                                  renderCard(item, inventoryProducts.length + i + 1, true)
                                )}
                                {customproducts.length > 0 && (
                                  <div className={`d-flex align-items-center gap-2 mb-3 ${purchasedProducts.length > 0 ? "mt-4" : ""}`}>
                                    <span className="fw-semibold table-cells">Custom Products</span>
                                  </div>
                                )}
                                {customproducts.map((cp, idx) => {
                                  const customErrorKey = purchasedProductErrorKey("custom", String(idx));
                                  const customRowErrors = purchasedProductFieldErrors[customErrorKey] || {};
                                  return (
                                  <div key={`custom-${idx}`} className="mb-3 p-3 rounded-3 border shadow-sm bg-gray-50">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <h6 className="m-0">Product #{inventoryProducts.length + purchasedProducts.length + idx + 1}</h6>
                                      <button
                                        type="button"
                                        className="btn btn-link text-danger p-0"
                                        title="Remove"
                                        onClick={() => {
                                          setCustomproducts((prev) => prev.filter((_, i) => i !== idx));
                                          setPurchasedProductFieldErrors((prev) => {
                                            const next: Record<string, PurchasedProductFieldErrors> = {};
                                            Object.entries(prev).forEach(([key, value]) => {
                                              if (!key.startsWith("custom:")) {
                                                next[key] = value;
                                                return;
                                              }
                                              const customIdx = Number(key.slice("custom:".length));
                                              if (customIdx < idx) next[key] = value;
                                              else if (customIdx > idx) {
                                                next[purchasedProductErrorKey("custom", String(customIdx - 1))] =
                                                  value;
                                              }
                                            });
                                            return next;
                                          });
                                          triggerRecomputeIfReadyRef.current();
                                        }}
                                      >
                                        <i className="ri-delete-bin-line fs-5" />
                                      </button>
                                    </div>
                                    <div className="row g-3">
                                      <div className="col-lg-4">
                                        <label className="table-cells">Product Name</label>
                                        <input type="text" className="form-control" value={cp.item_name || "Custom Item"} readOnly />
                                      </div>
                                      <div className="col-lg-4">
                                        <label className="table-cells">Quantity</label>
                                        <input
                                          type="number"
                                          className={`form-control ${customRowErrors.quantity ? "is-invalid" : ""}`}
                                          min={1}
                                          value={cp.quantity === 0 ? "" : cp.quantity}
                                          onChange={(e) => {
                                            const raw = e.target.value;
                                            const qty = raw === "" ? 0 : Number(raw);
                                            setCustomproducts((prev) =>
                                              prev.map((p, i) =>
                                                i === idx
                                                  ? { ...p, quantity: Number.isFinite(qty) ? qty : 0 }
                                                  : p
                                              )
                                            );
                                            patchPurchasedProductFieldError(
                                              customErrorKey,
                                              "quantity",
                                              validatePurchasedQuantityValue(raw)
                                            );
                                          }}
                                          onBlur={(e) => {
                                            patchPurchasedProductFieldError(
                                              customErrorKey,
                                              "quantity",
                                              validatePurchasedQuantityValue(e.target.value)
                                            );
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault();
                                          }}
                                        />
                                        {customRowErrors.quantity ? (
                                          <div className="text-danger small mt-1">{customRowErrors.quantity}</div>
                                        ) : null}
                                      </div>
                                      <div className="col-lg-4">
                                        <label className="table-cells">Unit Cost</label>
                                        <input
                                          type="number"
                                          className={`form-control ${customRowErrors.unitCost ? "is-invalid" : ""}`}
                                          min={1}
                                          step="0.01"
                                          placeholder="1"
                                          value={cp.Price === 0 ? "" : cp.Price}
                                          onChange={(e) => {
                                            const raw = e.target.value;
                                            const price = raw === "" ? 0 : Number(raw);
                                            setCustomproducts((prev) =>
                                              prev.map((p, i) =>
                                                i === idx
                                                  ? { ...p, Price: Number.isFinite(price) ? price : 0 }
                                                  : p
                                              )
                                            );
                                            patchPurchasedProductFieldError(
                                              customErrorKey,
                                              "unitCost",
                                              validatePurchasedUnitCostValue(raw)
                                            );
                                          }}
                                          onBlur={(e) => {
                                            patchPurchasedProductFieldError(
                                              customErrorKey,
                                              "unitCost",
                                              validatePurchasedUnitCostValue(e.target.value)
                                            );
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === "-" || e.key === "e" || e.key === "E") e.preventDefault();
                                          }}
                                        />
                                        {customRowErrors.unitCost ? (
                                          <div className="text-danger small mt-1">{customRowErrors.unitCost}</div>
                                        ) : null}
                                      </div>
                                      {cp.custom_information && (
                                        <div className="col-lg-12">
                                          <label className="table-cells">Custom Information</label>
                                          <input type="text" className="form-control" value={cp.custom_information} readOnly />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  );
                                })}
                              </>
                            )}
                          </>
                        );
                      })()
                    )}
                  </div>
                </div>
                
                {pickerOpen && pickerUserType && pickerUserId && token ? (
                  <ProductSelectionModal
                    open={pickerOpen}
                    title="Select a product"
                    onClose={() => setPickerOpen(false)}
                    userType={pickerUserType}
                    userId={pickerUserId}
                    signageCompanyId={pickerSignageCompanyId}
                    token={token}
                    studioCustomization={installationStudioCustomization}
                    selectedStockLines={selectedProducts.map((p) => ({
                      product_id: String(p.product_id ?? ""),
                      variant_id: p.variant_id,
                      quantity: Number(p.quantity) || 1,
                    }))}
                    onProductSelect={(product) => {
                      const isLinked =
                        product.linked_mode !== "catalog" &&
                        (product.inventory_source === "agent" || product.inventory_source === "office");
                      const isNewCatalog = product.linked_mode === "catalog";
                      const inventoryTypeForNew =
                        isNewCatalog && pickerUserType
                          ? pickerUserType === "agent"
                            ? "agent"
                            : "office"
                          : product.inventory_source || "";
                      const flattened: FlattenedProduct = {
                        product_id: product.product_id,
                        name: product.name,
                        sku: product.sku,
                        inventory_type: inventoryTypeForNew,
                        current_status: isNewCatalog ? "with_supplier" : product.current_status,
                        availableqty: product.available_quantity ?? 0,
                        quantity: 1,
                        image_url: product.images?.[0]?.url || "",
                        location: product.location,
                        slot_id: "",
                        ...(isLinked && pickerUserId ? { user_id: pickerUserId } : {}),
                        ...(product.variant_id ? { variant_id: String(product.variant_id).trim() } : {}),
                        ...(isNewCatalog
                          ? {
                              is_new_catalog: true,
                              variant_id: product.variant_id,
                              unit_price:
                                product.retail_price != null ? Number(product.retail_price) : undefined,
                              is_taxable: Boolean((product as any).is_taxable ?? (product as any).taxable ?? (product as any)["product.is_taxable"]),
                              ...(product.customizations ? { customizations: product.customizations } : {}),
                            }
                          : {}),
                      };
                      void handleAddFromPicker(flattened);
                    }}
                  />
                ) : null}
                {showAddressForm && (

                  <AddressFormModal
                    isOpen={true}
                    onClose={() => {
                      setShowAddressForm(false);
                    }}
                    token={token || ""}
                    owner={selectedOfficeAgent || selectedAgent || selectedOffice}
                    useType="billing"

                      userType={
                      formik.values.userType === "brokerageFirm"
                        ? selectedOfficeAgent
                          ? "agent"
                          : selectedTeamMemberOption || selectedOffice
                            ? "office"
                            : undefined
                        : formik.values.userType === "agent"
                          ? "agent"
                          : selectedOfficeAgent || selectedAgent
                            ? "agent"
                            : selectedOffice
                              ? "office"
                              : undefined
                    }
                    prefill={{
                      label_of_address: formik?.values?.label_of_address || "",
                      firstName: formik?.values?.firstName || "",
                      lastName: formik?.values?.lastName || "",
                      company: formik?.values?.company || "",
                      phone: formik?.values?.phone || "",
                      addressLine1: formik?.values?.addressLine1 || "",
                      addressLine2: formik?.values?.addressLine2 || "",
                      city: formik?.values?.city || "",
                      state: formik?.values?.state || "",
                      zipcode: formik?.values?.zipcode || "",
                      country: formik?.values?.country || "",
                    }}
                    onCreated={(createdRaw, option, useType) => {
                      // setAddressObjects((prev) => [createdRaw, ...(prev || [])]);
                      setAddressObjects((prev) => [createdRaw, ...(prev || [])]);  // correct

                      formik.setFieldValue(
                        useType === "billing" ? "billingAddressId" : "shippingAddressId",
                        option.value
                      );

                      if (useType === "billing") {
                        setSelectedOfficeBillingAddress(createdRaw);
                        setSelectedBillingAddress(createdRaw);
                      } else {
                        setInstallationAddress(createdRaw);
                      }

                      // 4) Mirror to shipping if same-as-billing is checked
                      if (useType === "billing" && formik.values.isSameAsBilling) {
                        formik.setFieldValue("shippingAddressId", option.value);
                        if (isOffice) {
                          setInstallationAddress(createdRaw);
                        } else {
                          setInstallationAddress(createdRaw?.address_details || null);
                        }
                      }
                    }}
                    afterCreateRefresh={async () => {
                      if (selectedOfficeAgent) await fetchAddresses(selectedOfficeAgent);
                      else if (selectedAgent) await fetchAddresses(selectedAgent);
                      else if (selectedOffice) await fetchAddresses(selectedOffice);
                    }}
                  />
                )}
                <div className="row col-lg-12 mb-3">
                  <div className="col-lg-6 mb-3 mb-lg-0 pe-0">
                    <InstructionsCreateCard
                      mode="instructions_files"
                      title="Instructions"
                      loginType="signage"
                      value={formik.values.instructions}
                      onChange={(v) => formik.setFieldValue("instructions", v, false)}
                      onLocalInstructionFilesChange={handlePendingInstructionFilesChange}
                      placeholder="Add instructions here"
                      rows={4}
                      className="mt-3"
                      iconUrl={OrderNotes}
                      iconColor="#10B981"
                      iconBackground="#EAF7F1"
                    />
                  </div>
                  <div className="align-items-center card-body col-lg-5 justify-content-end ms-0 ms-2 ms-lg-0 ms-lg-5 offset-1 p-4 shadow-lg">
                    <div className="align-items-center d-flex mb-3">
                      <span
                        aria-hidden="true"
                        className="image-icons"
                        style={{
                          backgroundColor: "#00a63e",
                          WebkitMaskImage: `url(${Doller})`,
                          maskImage: `url(${Doller})`,
                        }}
                      />
                      <SectionHeader title="Cost Breakdown" icon="" color="" size="lg" divider />
                    </div>

                    <OrderDiscountCodeField
                      className="mb-2"
                      value={couponInput}
                      appliedCode={discountDisplay.code}
                      discountAmount={discountDisplay.amount}
                      voucherApplied={
                        discountDisplay.amount > 0 && Boolean(discountDisplay.code.trim())
                      }
                      disabled={isRecomputingTax}
                      error={couponResolveError}
                      onChange={setCouponInput}
                      onApply={(code) => {
                        void applyDiscountCode(code);
                      }}
                      onClear={() => {
                        void clearDiscountCode();
                      }}
                    />


                    <div className="d-flex justify-content-between align-items-center py-2">
                      <span className="fw-medium">Units Purchased:</span>
                      <span className="fw-semibold">{customCatalogUnitsPurchased}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-2">
                      <span className="fw-medium">Total Units:</span>
                      <span className="fw-semibold">{totalUnits}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-2">
                      <span className="fw-medium">Installation Cost:</span>
                      {isEditingInstallationCost ? (
                        <div className="d-flex align-items-center gap-2 flex-shrink-0">
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            style={{ width: "100px" }}
                            value={manualInstallationCost}
                            onChange={(e) => setManualInstallationCost(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveInstallationCost();
                              if (e.key === "Escape") handleCancelEditInstallationCost();
                            }}
                            autoFocus
                          />
                          <button
                            type="button"
                            className="btn btn-sm export-button br-8"
                            onClick={handleSaveInstallationCost}
                            title="Save installation cost"
                          >
                            <i className="ri-check-line fs-14" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm table-addbutton br-8"
                            onClick={handleCancelEditInstallationCost}
                            title="Cancel"
                          >
                            <i className="ri-close-line fs-14" />
                          </button>
                        </div>
                      ) : (
                        <div className="d-flex align-items-center gap-2 flex-shrink-0">
                          <span className="fw-semibold">${resolvedInstallCost.toFixed(2)}</span>
                          <button
                            type="button"
                            className="btn btn-sm table-addbutton br-8"
                            onClick={handleEditInstallationCost}
                            title="Edit installation cost"
                            style={{ lineHeight: 1 }}
                          >
                            <i className="ri-edit-line" />
                          </button>
                        </div>
                      )}
                    </div>
                    {resolvedAdditionalCost > 0 && (
                      <div className="d-flex justify-content-between align-items-center py-2">
                        <span className="fw-medium">Additional Cost:</span>
                        <span className="fw-semibold">${resolvedAdditionalCost.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="d-flex justify-content-between align-items-center py-2">
                      <span className="fw-medium">Units Cost:</span>
                      <span className="fw-semibold">${Number(displayUnitsCost || 0).toFixed(2)}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-2">
                      <span className="fw-medium">Sub-total:</span>
                      <span className="fw-semibold">${resolvedSubtotalAmount.toFixed(2)}</span>
                    </div>
                    {discountDisplay.code && discountDisplay.amount > 0 && (
                      <div className="d-flex justify-content-between align-items-center py-2">
                        <span className="fw-medium">
                          Discount:
                          {discountDisplay.code ? (
                            <span style={{ color: "#00a63e" }}> ({discountDisplay.code})</span>
                          ) : null}
                        </span>
                        <span className="fw-semibold" style={{ color: "#00a63e" }}>
                          -${discountDisplay.amount.toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="d-flex justify-content-between align-items-center py-2">
                      {(() => {
                        const { labelText, tooltip } = buildSpecialFeeCostBreakdownDisplay(selectedSpecialFees);
                        return (
                          <>
                            <span className="fw-medium me-2" style={{ minWidth: 0 }} title={tooltip}>
                              {labelText}:
                            </span>
                            <span className="fw-semibold flex-shrink-0">
                              ${Number(formik.values.totals.specialfee_subtotal || 0).toFixed(2)}
                            </span>
                          </>
                        );
                      })()}
                    </div>

                    <div className="py-1">
                      <div className="d-flex justify-content-between align-items-center">
                        <button
                          type="button"
                          onClick={() => setShowTaxDetails(v => !v)}
                          className="btn btn-link p-0 text-decoration-none d-inline-flex align-items-center"
                          aria-expanded={showTaxDetails}
                          aria-controls="tax-breakdown"
                        >
                          {/* <i className={`bi ${showTaxDetails ? "bi-chevron-down" : "bi-chevron-right"} me-1`} /> */}
                          <span className="order-card-title">Tax :</span>
                        </button>
                        <span className="order-card-cells">
                          <span>
                            ${Number(formik.values.tax || 0).toFixed(2)}
                          </span>
                        </span>
                      </div>

                      {showTaxDetails && (
                        <div id="tax-breakdown" className="border rounded-3 p-2 bg-light mt-1">
                          <div className="d-flex justify-content-between small mb-1">
                            <span>Installation Tax</span>
                            <span className="order-card-cells">
                              ${Number(formik.values.totals.installation_tax_amount || 0).toFixed(2)}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between small mb-1">
                            <span>Product Tax</span>
                            <span className="order-card-cells">
                              ${Number((formik.values.totals as { product_tax_amount?: number })?.product_tax_amount || 0).toFixed(2)}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between small">
                            <span>Special Fee Tax</span>
                            <span className="order-card-cells">
                              ${Number(formik.values.totals.specialfee_tax_amount || 0).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>


                    <hr className="my-3" />

                    <div className="d-flex justify-content-between align-items-center py-2">
                      <span className="fw-bold fs-5">Total Amount:</span>
                      <span className="fw-bold fs-5 text-primary">
                        ${displayGrandTotal.toFixed(2)}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-2">
                      <span className="fw-medium">Already Paid:</span>
                      <span className="fw-semibold">${Number(formik.values?.already_paid ?? 0).toFixed(2)}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center py-2">
                      <span className="fw-bold">Remaining Balance:</span>
                      <span className="fw-bold text-success">
                        ${displayRemainingBalance.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="row mb-3 col-lg-12 rcust-margin-4 mt-4">
                  <div className="col-lg-12 d-block d-lg-flex justify-content-between">
                    <div className="col-lg-2">
                      <button
                        type="button"
                        onClick={() => safeNavigate(-1)}
                        className="btn back-btn surface-btn fw-bold fs-16 w-100"
                      >
                        <i className="bi bi-chevron-left me-2" />
                        Back
                      </button>
                    </div>

                    <div className="d-block d-lg-flex align-items-baseline justify-content-end">
                      <div className="col-lg-10 mt-3">
                        <div className="d-flex d-lg-block form-check justify-content-center mb-3 mb-lg-0">
                          <input
                            type="checkbox"
                            id="localLaws"
                            name="localLawsChecked"
                            className="form-check-input"
                            checked={formik.values.localLawsChecked}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <label htmlFor="localLaws" className="form-check-label ms-2">
                            I acknowledge to local laws
                          </label>
                        </div>
                      </div>
                      <button
                        type="submit"
                        className="btn btn-primary save-btn surface-btn fw-bold fs-16 w-100"
                        disabled={formSubmitting || !formik.values.localLawsChecked}
                      >
                        Save Draft
                      </button>
                    </div>
                  </div>
                </div>

                {/* <div className="row justify-content-end">
              <div className="col-lg-3 mt-3">
                <label className="me-1 mb-1">Local Laws Compliance :</label><br />
                <input
                  type="checkbox"
                  id="localLaws"
                  name="localLawsChecked"
                  checked={formik.values.localLawsChecked}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <span>{" "}I acknowledge adherence to local laws</span>

              </div>
            </div>

            <button
              type="submit"
              className="btn btn-info mt-4 float-end"
              disabled={formSubmitting || !formik.values.localLawsChecked}
            >
              Confirm Installation Order
            </button> */}
              </div>
              {effectiveCouponCreatedById && (
                <div className="col-lg-4">
                  <PONumber
                    mode="create"
                    disabled={Boolean(String(linkedProjectId || projectIdFromQuery || "").trim())}
                    className="mb-3"
                    value={String(formik.values.po_number || "")}
                    onChange={(v) => formik.setFieldValue("po_number", v)}
                  />
                  <SpecialFeeui
                    token={token!}
                    personId={effectiveCouponCreatedById || ""}
                    supplierId={(isSignageTeammember && parentSignageId ? parentSignageId : userId) || ""}
                    formik={formik}
                    name="specialfee"
                    apiBase={process.env.REACT_APP_API_URL || ""}
                    readOnly={false}
                    title="Special Fees"
                    disabled={isRecomputingTax}
                    onOptionsLoaded={(allOptions: any) => {
                      setAllAvailableSpecialFees(allOptions);
                      // console.log("📋 All available special fees loaded:", allOptions);
                    }}
                    onUserChange={(nextFees: any) => {
                      // console.log("🔄 SpecialFeeui onUserChange fired with:", nextFees);
                      // ✅ Update state immediately for recompute to pick up
                      // ✅ This is a USER action, not from recompute, so it's safe to update
                      isUpdatingFromRecomputeRef.current = false; // Ensure flag is false for user actions
                      setSelectedSpecialFees(nextFees);
                    }}
                  />
                  <ProjectGetCard
                    token={token!}
                    signageId={(isSignageTeammember && parentSignageId ? parentSignageId : userId) || ""}
                    initialProjectId={
                      userClearedProjectRef.current
                        ? undefined
                        : linkedProjectId || projectIdFromQuery || undefined
                    }
                    onSelectionChange={(sel) => {
                      if (!sel) userClearedProjectRef.current = true;
                      else userClearedProjectRef.current = false;
                      setLinkedProjectId(sel?.projectId ?? null);
                      setLinkedProjectNumber(sel?.projectNumber ?? null);
                    }}
                  />
                </div>
              )}
            </div>
          </form>
          {/* {(showAllocateModal || showViewInventoryModal) && (
            <div className="modal-backdrop show"></div>
          )}
          <div className={`modal fade ${showAllocateModal ? "show d-block" : ""}`} tabIndex={-1} role="dialog">
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Allocate Rotational Product </h5>
                  <button type="button" className="btn-close" onClick={() => setShowAllocateModal(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-lg-4">
                      <label htmlFor="">Select Product:</label>
                    </div>
                    <div className="col-lg-8">
                      <Select
                        name="productId"
                        id="productId"
                        options={rotationalproductOptions}
                        value={selectedProduct}
                        onChange={handleProductSelect}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-4">
                      <label htmlFor="">Select Warehouse:</label>
                    </div>
                    <div className="col-lg-8">
                      <Select
                        name="warehouseId"
                        id="warehouseId"
                        options={warehouseOptions}
                        value={selectedWarehouse}
                        onChange={(selected) => {
                          setSelectedWarehouse(selected);
                          if (selectedProduct?.fullProduct && selected?.value) {
                            const rotationalId = selectedProduct.value;
                            const warehouseId = selected.value;
                            fetchSerializedUnits(rotationalId, warehouseId);
                          }
                        }}
                      />


                      <select className="form-control">
                        <option value="warehouse A">Warehouse A</option>
                        <option value="warehouse B">Warehouse B</option>
                      </select>
                    </div>
                  </div>
                  <p><strong>Quantity Required:</strong> 3</p>
                  {linkedProductIdContext && (
                    <>
                      * ✅ Check if this linked product already has one allocation
                      {(() => {
                        const linkedProduct = selectedProducts.find(p => p.product_id === linkedProductIdContext);
                        const quantityRequired = linkedProduct?.quantity || 0;
                        const allocatedForLinkedProduct = allocatedUnits[linkedProductIdContext || ""] || [];
                        const remainingQuantity = quantityRequired - allocatedForLinkedProduct.length;

                        const hasAllocationForLinkedProduct = !!(linkedProductIdContext && allocatedUnits[linkedProductIdContext]);

                        return (
                          <>
                            <p>
                              <strong>Quantity Required:</strong> {quantityRequired} <br />
                              <strong>Remaining:</strong> {remainingQuantity}
                            </p>
                            <table className="table table-bordered default-table">
                              <thead>
                                <tr>
                                  <th>Serial No.</th>
                                  <th>Warehouse</th>
                                  <th>Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {availableSerializedUnits?.length === 0 ? (
                                  <tr>
                                    <td colSpan={3} className="text-center">No units available</td>
                                  </tr>
                                ) : (
                                  availableSerializedUnits?.map((unit, idx) => (
                                    <tr key={unit.serial_id || idx}>
                                      <td>{unit.serial_number || "N/A"}</td>
                                      <td>{unit.warehouse_name}</td>
                                      <td>
                                        <button
                                          className="btn btn-sm btn-success"
                                          disabled={remainingQuantity <= 0 || allocatedForLinkedProduct.some(u => u.serial_id === unit.serial_id)}
                                          onClick={() => {
                                            if (!linkedProductIdContext || !selectedProduct || !selectedWarehouse) return;

                                            const linkedProductId = linkedProductIdContext;
                                            const rotationalProductId = selectedProduct.value;
                                            const warehouseId = selectedWarehouse.value;

                                            const newUnit = {
                                              rotationalProductId,
                                              warehouseId,
                                              serial_id: unit.serial_id,
                                              serial_number: unit.serial_number,
                                            };

                                            setAllocatedUnits((prev) => {
                                              const existing = prev[linkedProductId] || [];
                                              const alreadyExists = existing.some(u => u.serial_id === unit.serial_id);
                                              if (alreadyExists || existing.length >= quantityRequired) {
                                                toast.warning("You can't allocate more than the required quantity.");
                                                return prev;
                                              }
                                              return {
                                                ...prev,
                                                [linkedProductId]: [...existing, newUnit],
                                              };
                                            });

                                            toast.success("Unit allocated.");
                                          }}
                                        >
                                          Allocate
                                        </button>

                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </>
                        );
                      })()}
                    </>
                  )}


                </div>
              </div>
            </div>
          </div> */}

        </div>
      </div >
    </div >
  );
};

export default SignageAddInstallation;
